import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Split from 'react-split';
import './Orders.css';
import { showToast, hideToast, updateToast } from '../utils/toast';
import OrderItemsPicker from '../components/OrderItemsPicker';
import OrderEditWorkspace from './OrderEditWorkspace';
import ShippingCalculator from './ShippingCalculator.jsx';

import {
  FaPlus, FaRegSave, FaTrash, FaEdit, FaSearch, FaTimes, FaUndo,
  FaHistory, FaHandPointer, FaCheckCircle, FaBoxOpen, FaPaperclip, FaEnvelope,
  FaPrint, FaFileInvoice, FaClipboardList, FaUserPlus, FaUpload, FaSync, FaDollarSign, FaEye,
  FaCalculator, FaDownload, FaLock, FaLockOpen, FaListUl, FaFileSignature, FaHashtag,
  FaSort, FaSortUp, FaSortDown, FaPaperPlane, FaClock, FaGripVertical, FaExternalLinkAlt
} from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_LIMIT = 50;
const LS_ORD_COLWIDTHS_KEY = 'ordersColWidths.v1';
const MIN_COL_W = 40;

const SEARCH_DEBOUNCE_MS = 300; // live search delay

const ORD_IMPORT_BASE = `${API}/api/orders/`;
const LIVE_REFRESH_MIN_MS = 15_000;
const LIVE_REFRESH_MAX_MINUTES = 720;
const LIVE_VIEW_FETCH_LIMIT = 300;
const ORDER_ITEM_ACTION_SORT_FIELD = 'actionSortRank';

const getOrderItemActionSortValue = (item, orderIsShipped) => {
  if (orderIsShipped) return 3;
  const status = String(item?.statusname || '').trim().toLowerCase();
  if (status === 'requested') return 0;
  if (status === 'picked') return 1;
  if (!status) return 4;
  return 2;
};

// ---- Date preset helpers ----
const fmtYMD = (d) => {
  const tz = new Date(d.getTime()); // local time
  const y = tz.getFullYear();
  const m = String(tz.getMonth() + 1).padStart(2, '0');
  const da = String(tz.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};
const startOfWeekMon = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1 - day); // make Monday=first day
  x.setDate(x.getDate() + diff);
  return x;
};
const endOfWeekMon = (d) => {
  const s = startOfWeekMon(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  return e;
};

const fmtFtzDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const formatFtzAuditDetails = (details) => {
  if (details == null || details === '') return '—';
  if (typeof details === 'string') return details;
  try {
    return JSON.stringify(details);
  } catch {
    return String(details);
  }
};

const receiptEditorHref = (receiptId) => receiptId ? `/receipts?receiptid=${encodeURIComponent(receiptId)}&ftz=1` : '';
const orderEditorHref = (orderId) => orderId ? `/orders?orderid=${encodeURIComponent(orderId)}&ftz=1` : '';
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfYear = (y) => new Date(y, 0, 1);
const endOfYear = (y) => new Date(y, 11, 31);

// --- role helpers (client vs internal) ---
function getCurrentUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}') || {}; }
  catch { return {}; }
}

// near your other helpers
function getCurrentUserId() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}') || {};
    return u.userid ?? u.userId ?? u.id ?? null;
  } catch { return null; }
}

function isClientUser(u) {
  const v = u.isclient ?? u.isClient ?? u.role ?? u.roleid;
  if (v === true) return true;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'client';
  }
  return false;
}
function isAdminUser(u) {
  const val = u?.isadmin ?? u?.isAdmin ?? u?.admin ?? u?.is_admin;
  if (val === true) return true;
  if (typeof val === 'string') {
    const norm = val.trim().toLowerCase();
    if (norm === '1' || norm === 'true' || norm === 'admin') return true;
  }
  if (Number(val) === 1) return true;
  const role = String(u?.role ?? u?.roleid ?? '').toLowerCase();
  return role.includes('admin') || role.includes('owner') || role.includes('super');
}
function getUserClientId(u) {
  const id = u.clientid ?? u.clientId ?? u.client_id;
  return (id || id === 0) ? String(id) : '';
}
function getUserFullName() {
  try {
    const u = JSON.parse(localStorage.getItem('currentUser')) ||
      JSON.parse(localStorage.getItem('user')) || {};
    return (
      u.fullname ||
      u.userFullName ||
      u.fullName ||
      u.name ||
      u.login ||
      ''
    );
  } catch { return ''; }
}

// ===== Image helpers (URLs, data:, base64 with whitespace, Buffer/Array) =====
const isDataUrl = (s) => typeof s === 'string' && s.trim().startsWith('data:image/');
const isHttpUrl = (s) => typeof s === 'string' && (/^https?:\/\//i.test(s) || s.startsWith('/'));
const stripWs = (s) => (typeof s === 'string' ? s.replace(/\s+/g, '') : s);

// Accept base64 even if it contains internal whitespace/newlines from DB
const isB64 = (s) => {
  if (typeof s !== 'string' || s.length <= 64) return false;
  const t = stripWs(s);
  // now only base64 chars
  return /^[A-Za-z0-9+/=]+$/.test(t);
};

const makeBlobUrl = (arrLike, mime = 'image/jpeg', bag) => {
  try {
    const u8 = arrLike instanceof Uint8Array ? arrLike : new Uint8Array(Array.from(arrLike || []));
    const blob = new Blob([u8], { type: mime });
    const url = URL.createObjectURL(blob);
    bag.current.add(url);
    return url;
  } catch { return ''; }
};

const resolveItemThumb = (item, bag) => {
  // 1) URL-ish fields first
  const urlFields = ['itemimageurl', 'item_image_url', 'imageurl', 'image_url', 'imageUrl', 'thumb', 'thumbnail'];
  for (const f of urlFields) {
    const v = item?.[f];
    if (v && (isHttpUrl(v) || isDataUrl(v))) return String(v).trim();
  }

  // 2) Common base64/buffer fields (items.image1 etc)
  const baseFields = ['image1', 'itemimage1', 'image', 'img', 'image_base64', 'imageB64'];
  for (const f of baseFields) {
    const v = item?.[f];
    if (!v) continue;

    if (typeof v === 'string') {
      const raw = String(v);
      const trimmed = raw.trim();
      if (isDataUrl(trimmed)) return trimmed;
      if (isB64(raw)) return `data:image/jpeg;base64,${stripWs(raw)}`;
      if (trimmed.startsWith('data:image/')) return trimmed;
    }

    if (v && typeof v === 'object') {
      if (v.type === 'Buffer' && Array.isArray(v.data)) return makeBlobUrl(v.data, 'image/jpeg', bag);
      if (Array.isArray(v)) return makeBlobUrl(v, 'image/jpeg', bag);
      if (Array.isArray(v?.data)) return makeBlobUrl(v.data, 'image/jpeg', bag);
    }
  }

  return '';
};

// Top of file (or in a separate constants file if you prefer)
const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }
];

const emptyOrder = {
  id: null,
  deleted: 0,
  warehouseid: '',
  datecreated: '',
  orderdate: '',
  shippeddate: '',
  statusid: 1,
  userid: '',
  clientid: 0,
  po: '',
  trackingnumber: '',
  carrierid: -1,
  destinationid: 0,
  thirdpartybillingid: 0,
  notes: '',
  bol: '',
  nonbillable: 0,
  type: 0,
  locked: 0,
  assignedto: null,
  assignedpriority: null,

  // NEW — Ship To
  shiptoname: '',
  shiptoaddress: '',
  shiptocity: '',
  shiptostate: '',
  shiptozip: '',
  shiptophone: '',
  is_ftz_outbound: 0,
  ftz_removal_type: '',
  ftz_removal_status: '',
  ftz_broker_reference: '',
  ftz_ace_submission_id: '',
  ftz_ace_reference_no: '',
  ftz_export_reference: '',
  ftz_inbond_number: '',
  ftz_inbond_type: '',
  ftz_inbond_origin_port: '',
  ftz_inbond_destination_port: '',
  ftz_inbond_carrier: '',
  ftz_inbond_conveyance: '',
  ftz_status_message: '',
  ftz_filed_at: '',
  ftz_authorized_at: '',
  ftz_removed_at: '',
};

// Missing function: formatPhone
function formatPhone(value) {
  const cleaned = ('' + value).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (match) {
    return match[1] + (match[2] ? '-' + match[2] : '') + (match[3] ? '-' + match[3] : '');
  }
  return value;
}

function toDateTimeLocalInput(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

const LIVE_VIEW_REQUESTED_STATUSES = ['REQUESTED'];
const LIVE_VIEW_PROGRESS_STATUSES = ['REQUESTED', 'PICKING', 'PICKED', 'PACKING', 'READY TO SHIP'];
const LIVE_VIEW_PICKED_STATUSES = ['PICKED'];
const LIVE_VIEW_REQUESTED_SET = new Set(LIVE_VIEW_REQUESTED_STATUSES);
const LIVE_VIEW_PROGRESS_SET = new Set(LIVE_VIEW_PROGRESS_STATUSES);
const LIVE_VIEW_PICKED_SET = new Set(LIVE_VIEW_PICKED_STATUSES);
const LIVE_VIEW_STATUS_PRIORITY = {
  CREATED: -1,
  REQUESTED: 0,
  SCHEDULED: 1,
  PICKING: 2,
  PICKED: 3,
  PACKING: 4,
  'READY TO SHIP': 5,
  'READY TO PICKUP': 6,
  SHIPPED: 7,
  COMPLETED: 8,
  CLOSED: 9,
  CANCELLED: 10,
};
const LIVE_VIEW_STATUS_CLASS_MAP = {
  REQUESTED: 'requested',
  PICKING: 'picking',
  PICKED: 'picked',
  PACKING: 'packing',
  'READY TO SHIP': 'ready',
  'READY TO PICKUP': 'ready',
  SHIPPED: 'shipped',
  COMPLETED: 'shipped',
  CLOSED: 'shipped',
  CANCELLED: 'cancelled',
};

const normalizeStatusName = (value) => (typeof value === 'string' ? value.trim().toUpperCase() : '');
const getOrderTimestamp = (order) => {
  if (!order) return 0;
  const raw = order.orderdate || order.datecreated || order.datecreatedat;
  if (!raw) return 0;
  const ts = new Date(raw).getTime();
  return Number.isFinite(ts) ? ts : 0;
};
const getLivePriorityValue = (order) => {
  const value = Number(order?.assignedpriority);
  return Number.isFinite(value) ? value : null;
};
const compareLiveOrders = (a, b) => {
  const assigneeA = Number(a?.assignedto);
  const assigneeB = Number(b?.assignedto);
  const hasAssignedA = Number.isFinite(assigneeA) && assigneeA > 0;
  const hasAssignedB = Number.isFinite(assigneeB) && assigneeB > 0;
  if (hasAssignedA && hasAssignedB && assigneeA === assigneeB) {
    const priA = getLivePriorityValue(a);
    const priB = getLivePriorityValue(b);
    if (priA != null && priB != null && priA !== priB) return priA - priB;
    if (priA != null && priB == null) return -1;
    if (priA == null && priB != null) return 1;
  }
  if (!hasAssignedA && !hasAssignedB) {
    const priA = getLivePriorityValue(a);
    const priB = getLivePriorityValue(b);
    if (priA != null && priB != null && priA !== priB) return priA - priB;
    if (priA != null && priB == null) return -1;
    if (priA == null && priB != null) return 1;
  }
  const statusA = normalizeStatusName(a?.statusname);
  const statusB = normalizeStatusName(b?.statusname);
  const priStatusA = LIVE_VIEW_STATUS_PRIORITY[statusA] ?? 99;
  const priStatusB = LIVE_VIEW_STATUS_PRIORITY[statusB] ?? 99;
  if (priStatusA !== priStatusB) return priStatusA - priStatusB;
  const timeA = getOrderTimestamp(a);
  const timeB = getOrderTimestamp(b);
  if (timeA !== timeB) return timeB - timeA;
  const idA = Number(a?.id) || 0;
  const idB = Number(b?.id) || 0;
  return idB - idA;
};
const sortLiveOrderList = (list) => (Array.isArray(list) ? list.slice().sort(compareLiveOrders) : []);
const getLiveAssignedMeta = (order) => {
  const priorityNumber = Number(order?.assignedpriority);
  const priority = Number.isFinite(priorityNumber) ? priorityNumber : null;
  const decorate = (text) => (priority != null ? `${text} (P${priority})` : text);
  if (!order) {
    const base = 'Unassigned';
    return { value: 'unassigned', label: base, displayLabel: decorate(base), priority };
  }
  const rawId = order.assignedto;
  const id = rawId !== undefined && rawId !== null ? String(rawId).trim() : '';
  const name = (order.assignedto_name || '').trim();
  if (id && id !== '0') {
    const baseLabel = name || `User #${id}`;
    return { value: `id:${id}`, label: baseLabel, displayLabel: decorate(baseLabel), priority };
  }
  if (name) return { value: `name:${name}`, label: name, displayLabel: decorate(name), priority };
  const fallback = 'Unassigned';
  return { value: 'unassigned', label: fallback, displayLabel: decorate(fallback), priority };
};
const isLiveViewExcludedAssignee = (meta) => {
  if (!meta) return true;
  if (meta.value === 'unassigned') return true;
  const label = String(meta.label || '').trim().toLowerCase();
  return label === 'administrator';
};
const getLiveStatusClass = (status) => LIVE_VIEW_STATUS_CLASS_MAP[status] || 'default';
const clampLiveWidthPct = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 30;
  return Math.min(60, Math.max(20, num));
};

function fetchJSONFactory() {
  return async function fetchJSON(url, opts = {}) {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const b = await res.json();
        if (b?.error) msg = b.error;
      } catch { }
      throw new Error(msg);
    }
    return res.json();
  };
}

const fetchJSON = fetchJSONFactory();

export default function Orders() {
  // === Selection state for orders table ===
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const toggleSelectOrder = (orderId) => {
    setSelectedOrderIds(prev =>
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };
  const toggleSelectAll = () => {
    if (selectedOrderIds.length === orders.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(orders.map(o => o.id));
    }
  };
  const navigate = useNavigate();
  const location = useLocation();

  const user = getCurrentUser();
  const IS_CLIENT = isClientUser(user);
  const IS_ADMIN = isAdminUser(user);
  const LOCKED_CLIENT_ID = getUserClientId(user);
  const liveViewOnly = useMemo(() => {
    if (IS_CLIENT) return false;
    try {
      const p = new URLSearchParams(location.search || '');
      return p.get('liveview') === '1';
    } catch {
      return false;
    }
  }, [IS_CLIENT, location.search]);
  const openLiveViewPopout = useCallback(() => {
    const url = `${window.location.origin}/orders?liveview=1`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const [calcOpen, setCalcOpen] = useState(false);
  const [liveViewEnabled, setLiveViewEnabled] = useState(() => {
    try { return localStorage.getItem('ordersLiveViewEnabled') === '1'; }
    catch { return false; }
  });
  const [liveViewAssignedFilters, setLiveViewAssignedFilters] = useState(() => {
    try {
      const raw = localStorage.getItem('ordersLiveAssignedFilters');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try { localStorage.setItem('ordersLiveAssignedFilters', JSON.stringify(liveViewAssignedFilters || [])); } catch {}
  }, [liveViewAssignedFilters]);
  const [liveViewAssigneeOpen, setLiveViewAssigneeOpen] = useState(false);
  const [liveViewAssigneeQuery, setLiveViewAssigneeQuery] = useState('');
  const liveViewAssigneeRef = useRef(null);
  const [liveViewUserOptions, setLiveViewUserOptions] = useState([]);
  const [liveViewUsersLoading, setLiveViewUsersLoading] = useState(false);
  const [liveViewStatusScope, setLiveViewStatusScope] = useState(() => {
    try { return localStorage.getItem('ordersLiveViewStatus') || 'pre'; }
    catch { return 'pre'; }
  });
  const [liveViewRefreshMinutes, setLiveViewRefreshMinutes] = useState(() => {
    try {
      const raw = localStorage.getItem('ordersLiveRefreshMinutes');
      const num = Number(raw);
      return Number.isFinite(num) && num >= 0 ? num : 0;
    } catch {
      return 0;
    }
  });
  useEffect(() => {
    try { localStorage.setItem('ordersLiveViewEnabled', liveViewEnabled ? '1' : '0'); } catch {}
  }, [liveViewEnabled]);
  useEffect(() => {
    try { localStorage.setItem('ordersLiveViewStatus', liveViewStatusScope || 'pre'); } catch {}
  }, [liveViewStatusScope]);
  useEffect(() => {
    if (IS_CLIENT && liveViewEnabled) setLiveViewEnabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IS_CLIENT]);
  useEffect(() => {
    try { localStorage.setItem('ordersLiveRefreshMinutes', String(liveViewRefreshMinutes)); } catch {}
  }, [liveViewRefreshMinutes]);
  const [liveViewWidthPct, setLiveViewWidthPct] = useState(() => {
    try {
      const stored = localStorage.getItem('ordersLiveViewWidthPct');
      return clampLiveWidthPct(stored || 30);
    } catch {
      return 30;
    }
  });
  useEffect(() => {
    try { localStorage.setItem('ordersLiveViewWidthPct', String(liveViewWidthPct)); } catch {}
  }, [liveViewWidthPct]);

  const getInitialLimit = () => {
    const stored = window.localStorage.getItem('ordersLimit');
    return stored ? parseInt(stored, 10) : DEFAULT_LIMIT;
  };

  // --- Import / Preview state (Orders) ---
  // --- Orders CSV Import / Preview state ---
  const orderImportInputRef = useRef(null);
  const [ordPreviewOpen, setOrdPreviewOpen] = useState(false);
  const [ordPreviewData, setOrdPreviewData] = useState([]);     // rows from preview
  const [ordPreviewErrors, setOrdPreviewErrors] = useState([]); // global errors (headers etc.)
  const [ordSelectedFile, setOrdSelectedFile] = useState(null);

  // Reusable notification modal (success/failure)
  const [notify, setNotify] = useState({ open: false, type: 'success', title: '', message: '' });
  const openNotify = (type, title, message) => setNotify({ open: true, type, title, message });
  const closeNotify = () => setNotify(n => ({ ...n, open: false }));

  const [ordProgressOpen, setOrdProgressOpen] = useState(false);
  const [ordProgressPct, setOrdProgressPct] = useState(0);
  const [ordProgressMsg, setOrdProgressMsg] = useState('');
  const ordXhrRef = useRef(null);
  const [ordImportLoading, setOrdImportLoading] = useState(false);
  const [ordJobId, setOrdJobId] = useState(null);
  const ordPollAbortRef = useRef(false);

  const ordValidCount = useMemo(() => ordPreviewData.filter(r => r.valid).length, [ordPreviewData]);
  const ordInvalidCount = useMemo(() => Math.max(0, ordPreviewData.length - ordValidCount), [ordPreviewData, ordValidCount]);

  // Track the latest list request to prevent stale overwrites
  const ordersFetchSeq = useRef(0);
  const ordersFetchAbort = useRef(null);
  const [datePreset, setDatePreset] = useState(localStorage.getItem('ordersDatePreset') || '');
  const todayStr = () => new Date().toISOString().slice(0, 10);

	const getInitialQ = () => localStorage.getItem('ordersQ') || '';
	const getInitialFromDate = () => localStorage.getItem('ordersFromDate') || '';
	const getInitialToDate = () => localStorage.getItem('ordersToDate') || '';
	const getInitialLiveViewFromDate = () => localStorage.getItem('ordersLiveViewFromDate') || '';
	const getInitialLiveViewToDate = () => localStorage.getItem('ordersLiveViewToDate') || '';
	const getInitialLiveViewDatePreset = () => localStorage.getItem('ordersLiveViewDatePreset') || '';
	const getInitialShippedFromDate = () => localStorage.getItem('ordersShippedFromDate') || '';
	const getInitialShippedToDate = () => localStorage.getItem('ordersShippedToDate') || '';
	const getInitialShowDeleted = () => JSON.parse(localStorage.getItem('ordersShowDeleted') || 'false');
	const getInitialStatusIds = () => {
	  try {
    const raw = localStorage.getItem('ordersStatusIds');
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch { return []; }
};
  const getInitialSort = () => {
    try {
      const stored = window.localStorage.getItem('ordersSort');
      return stored ? JSON.parse(stored) : { sortField: 'datecreated', sortOrder: 'DESC' };
    } catch {
      return { sortField: 'datecreated', sortOrder: 'DESC' };
    }
  };

  // Hover preview state (like EOrderItems)
  const [preview, setPreview] = useState({ src: '', x: 0, y: 0, visible: false });
  const previewRef = useRef(null);
  const lastPosRef = useRef({ x: 0, y: 0 });

  // Keep track of blob: URLs to revoke on reload/unmount
  const blobUrlBag = useRef(new Set());
  useEffect(() => () => { for (const u of blobUrlBag.current) URL.revokeObjectURL(u); blobUrlBag.current.clear(); }, []);

  const [formErrors, setFormErrors] = useState({});

  const [isTypingLimit, setIsTypingLimit] = useState(false);
  const [limit, setLimit] = useState(getInitialLimit());
  const [limitInput, setLimitInput] = useState(String(getInitialLimit()));
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });

	  const [q, setQ] = useState(getInitialQ());
	  const [showDeleted, setShowDeleted] = useState(getInitialShowDeleted());
	  const [fromDate, setFromDate] = useState(getInitialFromDate());
	  const [toDate, setToDate] = useState(getInitialToDate());
	  // Live View Dashboard has its own independent date range (not tied to the main list filters).
	  const [liveViewFromDate, setLiveViewFromDate] = useState(getInitialLiveViewFromDate());
	  const [liveViewToDate, setLiveViewToDate] = useState(getInitialLiveViewToDate());
	  const [liveViewDatePreset, setLiveViewDatePreset] = useState(getInitialLiveViewDatePreset());
	  const [shippedFromDate, setShippedFromDate] = useState(getInitialShippedFromDate());
	  const [shippedToDate, setShippedToDate] = useState(getInitialShippedToDate());
	  const [shippedDatePreset, setShippedDatePreset] = useState(localStorage.getItem('ordersShippedDatePreset') || '');
	  const [statusIds, setStatusIds] = useState(getInitialStatusIds());
  // Billed filter: '', 'unbilled', 'billed'
  const [billedFilter, setBilledFilter] = useState(() => {
    try { return localStorage.getItem('ordersBilled') || ''; } catch { return ''; }
  });
  useEffect(() => { try { localStorage.setItem('ordersBilled', billedFilter || ''); } catch {} }, [billedFilter]);

  const [dateField, setDateField] = useState(localStorage.getItem('ordersDateField') || 'orderdate');
  useEffect(() => localStorage.setItem('ordersDateField', dateField), [dateField]);
  // Migrate any legacy persisted value to orderdate
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ordersDateField');
      if (saved === 'datecreated') {
        setDateField('orderdate');
        localStorage.setItem('ordersDateField', 'orderdate');
      }
    } catch {}
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getInitialClientName = () => localStorage.getItem('ordersClientName') || '';
  const [clientFilter, setClientFilter] = useState(getInitialClientName());
  useEffect(() => { localStorage.setItem('ordersClientName', clientFilter); }, [clientFilter]);
  const getInitialSkuFilter = () => localStorage.getItem('ordersSku') || '';
  const [skuFilter, setSkuFilter] = useState(getInitialSkuFilter());
  useEffect(() => { localStorage.setItem('ordersSku', skuFilter); }, [skuFilter]);
  const getInitialCreatorName = () => localStorage.getItem('ordersCreatorName') || '';
  const [creatorFilter, setCreatorFilter] = useState(getInitialCreatorName());
  useEffect(() => { localStorage.setItem('ordersCreatorName', creatorFilter); }, [creatorFilter]);
  const getInitialAssignedFilter = () => localStorage.getItem('ordersAssignedFilter') || '';
  const [assignedFilter, setAssignedFilter] = useState(getInitialAssignedFilter());
  useEffect(() => { localStorage.setItem('ordersAssignedFilter', assignedFilter); }, [assignedFilter]);
  // Explicit client id filter (esp. for client users); declare before any usage
  const [clientIdFilter, setClientIdFilter] = useState(() => localStorage.getItem('ordersClientId') || '');
  useEffect(() => { try { localStorage.setItem('ordersClientId', clientIdFilter || ''); } catch {} }, [clientIdFilter]);
  // Safety: internal users should not carry over a stale clientId filter
  useEffect(() => {
    if (!IS_CLIENT && clientIdFilter) setClientIdFilter('');
  }, [IS_CLIENT, clientIdFilter]);

  const initialSort = getInitialSort();
  const [sortField, setSortField] = useState(initialSort.sortField);
  const [sortOrder, setSortOrder] = useState(initialSort.sortOrder);
  const [loadingList, setLoadingList] = useState(false);
  // Centered topbar toast; on completion, report count and keep a moment
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    const id = 'loading.orders';
    if (loadingList && !prevLoadingRef.current) {
      showToast({ id, message: 'Loading orders…', duration: 0, align: 'center', slideFrom: 'top', pill: true });
    }
    if (!loadingList && prevLoadingRef.current) {
      const total = Number((meta && meta.total) || 0);
      const count = Number.isFinite(total) && total > 0 ? total : (Array.isArray(orders) ? orders.length : 0);
      const formatted = Number(count).toLocaleString('en-US');
      // Re-show with same id so ToastHost dedupes and resets timer reliably
      showToast({ id, message: `Loaded ${formatted} orders.`, duration: 2500, align: 'center', slideFrom: 'top', pill: true });
    }
    prevLoadingRef.current = loadingList;
  }, [loadingList, orders, meta?.total]);

  // All-in-one signature to refresh list on any search change
  const searchSignature = useMemo(() => JSON.stringify({
    q, clientFilter, clientIdFilter, skuFilter, fromDate, toDate, shippedFromDate, shippedToDate, showDeleted, dateField, sortField, sortOrder, limit, statusIds, datePreset, shippedDatePreset, billedFilter,
  }), [q, clientFilter, clientIdFilter, skuFilter, fromDate, toDate, shippedFromDate, shippedToDate, showDeleted, dateField, sortField, sortOrder, limit, statusIds, datePreset, shippedDatePreset, billedFilter]);

  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ftzActionRunning, setFtzActionRunning] = useState('');
  const [ftzDetail, setFtzDetail] = useState(null);
  const [ftzDetailLoading, setFtzDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false); // make this a clear boolean
  const [creating, setCreating] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const singleClientOption = useMemo(
    () => (IS_CLIENT && clientOptions.length === 1 ? clientOptions[0] : null),
    [IS_CLIENT, clientOptions]
  );
  const [warehouseOptions, setWarehouseOptions] = useState([]);
const [carrierOptions, setCarrierOptions] = useState([]);
const carrierLookup = useMemo(() => {
  const map = new Map();
  (carrierOptions || []).forEach((opt) => { map.set(String(opt.id), opt.name); });
  return map;
}, [carrierOptions]);
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [liveViewOrdersRaw, setLiveViewOrdersRaw] = useState([]);
  const [liveViewLoading, setLiveViewLoading] = useState(false);
  const [liveViewHasFetched, setLiveViewHasFetched] = useState(false);
  const [liveViewSelectedOrderId, setLiveViewSelectedOrderId] = useState(null);
const liveViewSelectedOrderIdRef = useRef(null);
const liveViewRestoreSelectionRef = useRef(null);
useEffect(() => {
  liveViewSelectedOrderIdRef.current = liveViewSelectedOrderId;
}, [liveViewSelectedOrderId]);
  const [liveViewItems, setLiveViewItems] = useState([]);
  const [liveViewItemsLoading, setLiveViewItemsLoading] = useState(false);
  const [liveViewDraggingId, setLiveViewDraggingId] = useState(null);
  const [liveViewDragOverId, setLiveViewDragOverId] = useState(null);
	  const [liveViewPrioritySaving, setLiveViewPrioritySaving] = useState(false);
    const [liveViewPriorityEditSaving, setLiveViewPriorityEditSaving] = useState(false);
    const [liveViewPriorityEditOrderId, setLiveViewPriorityEditOrderId] = useState(null);
    const [liveViewPriorityDraft, setLiveViewPriorityDraft] = useState('');
    const liveViewPriorityDraftOriginalRef = useRef(null);
    const liveViewPriorityInputRef = useRef(null);
    const liveViewPriorityIgnoreBlurRef = useRef(false);
	  const liveViewDragSourceRef = useRef(null);
	  const liveViewFilterSignature = useMemo(() => JSON.stringify({
	    liveFromDate: liveViewFromDate || '',
	    liveToDate: liveViewToDate || '',
	    isClient: !!IS_CLIENT,
	    clientIdFilter: clientIdFilter || '',
	  }), [IS_CLIENT, clientIdFilter, liveViewFromDate, liveViewToDate]);
  const liveViewUsersInitRef = useRef(false);
  const fetchLiveViewUsers = useCallback(async () => {
    setLiveViewUsersLoading(true);
    try {
      const json = await fetchJSON(`${API}/api/users/assignees?active=1&internalOnly=1`);
      const raw = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
      const next = raw
        .map((u) => {
          const userid = u?.userid ?? u?.id ?? u?.userId ?? null;
          const login = (u?.login ?? '').trim();
          const fullname = (u?.fullname ?? u?.name ?? '').trim();
          const label = fullname || login || (userid != null ? `User #${userid}` : '');
          return { userid, label };
        })
        .filter((u) => u.userid != null && u.label)
        .map((u) => ({ value: `id:${String(u.userid).trim()}`, label: u.label }))
        .filter((opt) => !isLiveViewExcludedAssignee({ value: opt.value, label: opt.label }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
      setLiveViewUserOptions(next);
    } catch (e) {
      console.error('[orders] Failed to load Live View assignees', e);
      setLiveViewUserOptions([]);
    } finally {
      setLiveViewUsersLoading(false);
    }
  }, [API, fetchJSON]);
  const fetchLiveViewOrderItems = useCallback(async (orderId) => {
    if (!orderId) {
      setLiveViewItems([]);
      return;
    }
    setLiveViewItemsLoading(true);
    try {
      const data = await fetchJSON(`${API}/api/orders/${orderId}/items`);
      setLiveViewItems(Array.isArray(data) ? data : []);
    } catch {
      setLiveViewItems([]);
    } finally {
      setLiveViewItemsLoading(false);
    }
  }, [fetchJSON]);

	  const fetchLiveViewOrders = useCallback(async () => {
	    const seq = ++liveViewFetchSeq.current;
	    if (liveViewFetchAbort.current) {
	      try { liveViewFetchAbort.current.abort(); } catch {}
	    }
    const controller = new AbortController();
    liveViewFetchAbort.current = controller;
    setLiveViewLoading(true);
    try {
      const params = new URLSearchParams({
        limit: LIVE_VIEW_FETCH_LIMIT,
        offset: 0,
        sortField: 'datecreated',
        sortOrder: 'DESC',
	      });
	      if (liveViewFromDate) params.set('from', liveViewFromDate);
	      if (liveViewToDate) params.set('to', liveViewToDate);
	      params.set('deleted', 0);
      if (IS_CLIENT && clientIdFilter) params.set('clientid', String(clientIdFilter));
      const json = await fetchJSON(`${API}/api/orders?${params.toString()}`, {
        signal: controller.signal,
      });
      if (seq !== liveViewFetchSeq.current) return;
      const prevSelectedId = liveViewSelectedOrderIdRef.current;
      if (prevSelectedId) {
        liveViewRestoreSelectionRef.current = Number(prevSelectedId);
      } else {
        liveViewRestoreSelectionRef.current = null;
      }
      const normalizedRows = sortLiveOrderList(Array.isArray(json.data) ? json.data : []);
      setLiveViewOrdersRaw(normalizedRows);
      setLiveViewHasFetched(true);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      if (seq !== liveViewFetchSeq.current) return;
      console.error('[orders] Failed to refresh Live View', e);
    } finally {
      if (seq === liveViewFetchSeq.current) setLiveViewLoading(false);
    }
  }, [
    API,
    IS_CLIENT,
	    clientIdFilter,
	    fetchJSON,
	    liveViewFromDate,
	    liveViewToDate,
	  ]);
  const fetchLiveViewOrdersRef = useRef(fetchLiveViewOrders);
  useEffect(() => {
    fetchLiveViewOrdersRef.current = fetchLiveViewOrders;
  }, [fetchLiveViewOrders]);

  useEffect(() => {
    if (!liveViewPriorityEditOrderId) return;
    const id = window.setTimeout(() => {
      liveViewPriorityInputRef.current?.focus?.();
      try { liveViewPriorityInputRef.current?.select?.(); } catch {}
    }, 0);
    return () => window.clearTimeout(id);
  }, [liveViewPriorityEditOrderId]);

  useEffect(() => {
    if (!liveViewPriorityEditOrderId) return;
    const onPointerDown = (e) => {
      const el = liveViewPriorityInputRef.current;
      if (el && (e.target === el || el.contains(e.target))) return;
      if (liveViewPrioritySaving || liveViewPriorityEditSaving) return;
      // Clicking on table rows/cells doesn't always move focus, so force a blur
      // to trigger the input's onBlur handler.
      el?.blur?.();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [
    liveViewPriorityEditOrderId,
    liveViewPriorityEditSaving,
    liveViewPrioritySaving,
  ]);

  const startLiveViewPriorityEdit = useCallback((order) => {
    if (!order?.id) return;
    const current = order?.assignedpriority != null ? Number(order.assignedpriority) : null;
    liveViewPriorityDraftOriginalRef.current = Number.isFinite(current) ? current : null;
    setLiveViewPriorityDraft(order?.assignedpriority != null ? String(order.assignedpriority) : '');
    setLiveViewPriorityEditOrderId(Number(order.id));
  }, []);

  const cancelLiveViewPriorityEdit = useCallback(() => {
    setLiveViewPriorityEditOrderId(null);
    setLiveViewPriorityDraft('');
    liveViewPriorityDraftOriginalRef.current = null;
  }, []);

  const saveLiveViewPriorityEdit = useCallback(async (orderId) => {
    const numericOrderId = Number(orderId);
    if (!Number.isFinite(numericOrderId) || numericOrderId <= 0) return;

    const raw = String(liveViewPriorityDraft ?? '').trim();
    if (raw === '') {
      cancelLiveViewPriorityEdit();
      return;
    }
    const nextPriority = Number(raw);
    if (nextPriority != null && (!Number.isFinite(nextPriority) || nextPriority < 1 || !Number.isInteger(nextPriority))) {
      showToast('error', 'Invalid priority', 'Priority must be a positive integer.');
      cancelLiveViewPriorityEdit();
      return;
    }

    const prevPriority = liveViewPriorityDraftOriginalRef.current;
    if ((prevPriority == null && nextPriority == null) || (prevPriority != null && nextPriority != null && Number(prevPriority) === Number(nextPriority))) {
      cancelLiveViewPriorityEdit();
      return;
    }

    setLiveViewPriorityEditSaving(true);
    try {
      // Close the editor immediately so clicking elsewhere restores the static badge.
      cancelLiveViewPriorityEdit();
      await fetchJSON(`${API}/api/orders/${numericOrderId}/priority`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priority: nextPriority,
          username: getUserFullName(),
        }),
      });
      showToast('success', 'Priority updated', 'Order priority saved.');
      fetchLiveViewOrdersRef.current?.();
    } catch (err) {
      console.error('[orders] Live View priority edit failed', err);
      showToast('error', 'Priority update failed', err?.message || 'Failed to save order priority.');
    } finally {
      setLiveViewPriorityEditSaving(false);
    }
  }, [API, cancelLiveViewPriorityEdit, fetchJSON, liveViewPriorityDraft]);

  const persistLiveViewPriorities = useCallback(async (assignedId, orderedIds, assignedName) => {
    if (!assignedId || !Array.isArray(orderedIds) || !orderedIds.length) return;
    liveViewPendingPriorityUpdate.current?.abort?.();
    const controller = new AbortController();
    liveViewPendingPriorityUpdate.current = controller;
    setLiveViewPrioritySaving(true);
    try {
      const username = getUserFullName();
      for (let idx = 0; idx < orderedIds.length; idx += 1) {
        const orderId = orderedIds[idx];
        await fetchJSON(`${API}/api/orders/${orderId}/assign`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            assignedto: assignedId,
            assignedto_name: assignedName || '',
            priority: idx + 1,
            username,
          }),
          signal: controller.signal,
        });
      }
      showToast('success', 'Priority updated', 'Assigned order priority updated.');
      fetchLiveViewOrdersRef.current();
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('[orders] Live View priority update failed', err);
      showToast('error', 'Priority update failed', err?.message || 'Failed to save order priorities.');
    } finally {
      setLiveViewPrioritySaving(false);
      if (controller === liveViewPendingPriorityUpdate.current) {
        liveViewPendingPriorityUpdate.current = null;
      }
    }
  }, [API, fetchJSON]);
  const liveViewPendingPriorityUpdate = useRef(null);
  useEffect(() => {
    return () => {
      liveViewPendingPriorityUpdate.current?.abort();
      liveViewPendingPriorityUpdate.current = null;
    };
  }, []);
  const [liveViewSplitSizes, setLiveViewSplitSizes] = useState(() => {
    try {
      const raw = localStorage.getItem('ordersLiveViewSplit');
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length === 2) return parsed;
    } catch {}
    return [70, 30];
  });
  const [liveViewSortField, setLiveViewSortField] = useState(() => {
    try { return localStorage.getItem('ordersLiveSortField') || null; } catch { return null; }
  });
  const [liveViewSortDir, setLiveViewSortDir] = useState(() => {
    try {
      const raw = localStorage.getItem('ordersLiveSortDir');
      return raw === 'DESC' ? 'DESC' : 'ASC';
    } catch {
      return 'ASC';
    }
  });
  const [liveViewDetailSortField, setLiveViewDetailSortField] = useState(() => {
    try { return localStorage.getItem('ordersLiveDetailSortField') || 'status'; } catch { return 'status'; }
  });
  const [liveViewDetailSortDir, setLiveViewDetailSortDir] = useState(() => {
    try {
      const raw = localStorage.getItem('ordersLiveDetailSortDir');
      return raw === 'DESC' ? 'DESC' : 'ASC';
    } catch {
      return 'ASC';
    }
  });
  const liveViewFetchSeq = useRef(0);
  const liveViewFetchAbort = useRef(null);
  const liveViewSourceOrders = liveViewOrdersRaw || [];
  const liveViewAssignedOptionsFromOrders = useMemo(() => {
    const map = new Map();
    (liveViewSourceOrders || []).forEach((order) => {
      if (!order || order.deleted) return;
      const meta = getLiveAssignedMeta(order);
      if (isLiveViewExcludedAssignee(meta)) return;
      const { value, displayLabel } = meta;
      if (!value || map.has(value)) return;
      const label = displayLabel || value;
      map.set(value, { value, label });
    });
    return Array.from(map.values())
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [liveViewSourceOrders]);
  const liveViewAssignedOptions = (liveViewUserOptions && liveViewUserOptions.length)
    ? liveViewUserOptions
    : liveViewAssignedOptionsFromOrders;
  useEffect(() => {
    if (!liveViewAssigneeOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setLiveViewAssigneeOpen(false);
      }
    };
    const onPointerDown = (e) => {
      const root = liveViewAssigneeRef.current;
      if (!root) return;
      if (e.target === root || root.contains(e.target)) return;
      setLiveViewAssigneeOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [liveViewAssigneeOpen]);
  const liveViewCanDrag = liveViewHasFetched && !liveViewSortField;
  const liveViewOrders = useMemo(() => {
    const scopeSet = liveViewStatusScope === 'requested'
      ? LIVE_VIEW_REQUESTED_SET
      : liveViewStatusScope === 'picked'
        ? LIVE_VIEW_PICKED_SET
      : liveViewStatusScope === 'pre'
        ? LIVE_VIEW_PROGRESS_SET
        : null;
    const filtered = (liveViewSourceOrders || [])
      .filter((order) => {
        if (!order || order.deleted) return false;
        const assignedMeta = getLiveAssignedMeta(order);
        if (isLiveViewExcludedAssignee(assignedMeta)) return false;
        if (Array.isArray(liveViewAssignedFilters) && liveViewAssignedFilters.length > 0) {
          if (!liveViewAssignedFilters.includes(assignedMeta.value)) return false;
        }
        if (scopeSet) {
          const statusName = normalizeStatusName(order?.statusname);
          if (!scopeSet.has(statusName)) return false;
        }
        return true;
      });
    const applyDefaultSort = (list) => list.slice().sort(compareLiveOrders);
    if (liveViewHasFetched && !liveViewSortField) return filtered;
    if (!liveViewSortField) return applyDefaultSort(filtered);
    const dir = liveViewSortDir === 'ASC' ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      const getVal = (o) => {
        switch (liveViewSortField) {
          case 'priority': {
            const assigned = (getLiveAssignedMeta(o).label || '').toLowerCase();
            const raw = Number(o?.assignedpriority);
            const pri = Number.isFinite(raw) ? raw : Number.POSITIVE_INFINITY;
            // Group by assignee, then by priority, then by order id.
            return `${assigned}|${String(pri).padStart(12, '0')}|${String(Number(o?.id) || 0).padStart(12, '0')}`;
          }
          case 'id':
            return Number(o?.id) || 0;
          case 'assigned':
            return (getLiveAssignedMeta(o).label || '').toLowerCase();
          case 'status': {
            const statusName = normalizeStatusName(o?.statusname);
            return (LIVE_VIEW_STATUS_PRIORITY[statusName] ?? 99) + statusName;
          }
          case 'client':
            return (o?.clientname || '').toLowerCase();
          case 'orderdate':
            return getOrderTimestamp(o);
          default:
            return '';
        }
      };
      const valA = getVal(a);
      const valB = getVal(b);
      if (valA > valB) return dir;
      if (valA < valB) return -dir;
      return 0;
    });
  }, [liveViewSourceOrders, liveViewAssignedFilters, liveViewStatusScope, liveViewSortField, liveViewSortDir, liveViewHasFetched]);
  const liveViewSelectedOrder = useMemo(
    () => liveViewOrders.find((order) => Number(order?.id) === Number(liveViewSelectedOrderId)) || null,
    [liveViewOrders, liveViewSelectedOrderId]
  );
  const liveViewDetailRows = useMemo(() => {
    if (!Array.isArray(liveViewItems) || !liveViewItems.length) return [];
    const dir = liveViewDetailSortDir === 'ASC' ? 1 : -1;
    const getVal = (item) => {
      switch (liveViewDetailSortField) {
        case 'status': {
          const status = normalizeStatusName(item?.statusname);
          return (LIVE_VIEW_STATUS_PRIORITY[status] ?? 99) + status;
        }
        case 'receipt':
          return Number(item?.receiptid) || 0;
        case 'pallet':
          return Number(item?.palletnumber) || 0;
        case 'area':
          return (item?.areaname || '').toLowerCase();
        case 'location':
          return (item?.location || '').toLowerCase();
        case 'qty':
          return Number(item?.boxes_units ?? item?.totalpiecesrequested ?? 0) || 0;
        case 'picked':
          return Number(item?.unitspicked ?? 0) || 0;
        default:
          return '';
      }
    };
    return liveViewItems.slice().sort((a, b) => {
      const valA = getVal(a);
      const valB = getVal(b);
      if (valA > valB) return dir;
      if (valA < valB) return -dir;
      return 0;
    });
  }, [liveViewItems, liveViewDetailSortField, liveViewDetailSortDir]);
  const liveLayoutRef = useRef(null);
  const handleLiveResizeStart = (event) => {
    if (!liveLayoutRef.current) return;
    event.preventDefault();
    const container = liveLayoutRef.current;
    const rect = container.getBoundingClientRect();
    const onMove = (e) => {
      const leftPct = ((e.clientX - rect.left) / rect.width) * 100;
      const rightPct = clampLiveWidthPct(100 - leftPct);
      setLiveViewWidthPct(rightPct);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Client name typeahead for internal users
  const [clientSuggestOpen, setClientSuggestOpen] = useState(false);
  const clientMatches = useMemo(() => {
    const q = String(clientFilter || '').trim().toLowerCase();
    if (!q) return [];
    return (clientOptions || [])
      .filter(c => String(c?.name || '').toLowerCase().includes(q))
      .slice(0, 10);
  }, [clientFilter, clientOptions]);
  const assignedFilterOptions = useMemo(() => {
    const map = new Map();
    (orders || []).forEach((order) => {
      if (!order || order.deleted) return;
      const { displayLabel } = getLiveAssignedMeta(order);
      if (!displayLabel || map.has(displayLabel)) return;
      map.set(displayLabel, displayLabel);
    });
    if (!map.has('Unassigned')) map.set('Unassigned', 'Unassigned');
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [orders]);
  useEffect(() => {
    try { localStorage.setItem('ordersLiveViewSplit', JSON.stringify(liveViewSplitSizes)); } catch {}
  }, [liveViewSplitSizes]);
  useEffect(() => {
    try { localStorage.setItem('ordersLiveSortField', liveViewSortField || ''); } catch {}
  }, [liveViewSortField]);
  useEffect(() => {
    try { localStorage.setItem('ordersLiveSortDir', liveViewSortDir || 'ASC'); } catch {}
  }, [liveViewSortDir]);
  useEffect(() => {
    try { localStorage.setItem('ordersLiveDetailSortField', liveViewDetailSortField || 'status'); } catch {}
  }, [liveViewDetailSortField]);
  useEffect(() => {
    try { localStorage.setItem('ordersLiveDetailSortDir', liveViewDetailSortDir || 'ASC'); } catch {}
  }, [liveViewDetailSortDir]);
  useEffect(() => {
    const pendingRestoreId = liveViewRestoreSelectionRef.current;
    if (pendingRestoreId != null) {
      const stillExists = liveViewOrders.some(o => Number(o?.id) === Number(pendingRestoreId));
      if (stillExists) {
        liveViewRestoreSelectionRef.current = null;
        if (Number(liveViewSelectedOrderId) !== Number(pendingRestoreId)) {
          setLiveViewSelectedOrderId(pendingRestoreId);
        }
        fetchLiveViewOrderItems(pendingRestoreId);
        return;
      }
    }
    if (!liveViewSelectedOrderId) return;
    const exists = liveViewOrders.some(o => Number(o?.id) === Number(liveViewSelectedOrderId));
    if (!exists) {
      setLiveViewSelectedOrderId(null);
      setLiveViewItems([]);
    }
  }, [liveViewOrders, liveViewSelectedOrderId, fetchLiveViewOrderItems]);

  // "Created" status id from the list (fallback 1)
  const createdStatusId = useMemo(() => {
    const s = statusOptions.find(
      (opt) => (opt.name || '').toLowerCase() === 'created'
    );
    return s ? (s.id || s.statusid) : 1;
  }, [statusOptions]);

  // unified bottom pane mode (items | history | attachments | null)
  const [bottomPaneMode, setBottomPaneMode] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const selectedOrderIsShipped = useMemo(() => {
    if (selected?.shippeddate) return true;
    const status = String(selected?.statusname || '').trim().toLowerCase();
    return status === 'shipped';
  }, [selected?.shippeddate, selected?.statusname]);
  // Bottom items sort state (persisted)
  const OI_SORT_FIELD_KEY = 'orders.items.sortField';
  const OI_SORT_ORDER_KEY = 'orders.items.sortOrder';
  const OI_SORT_STACK_KEY = 'orders.items.sortStack.v1';
const normalizeOiField = (field) => {
  if (field === 'totalunitsrequested') return 'totalpiecesrequested';
  if (field === 'action' || field === ORDER_ITEM_ACTION_SORT_FIELD) return ORDER_ITEM_ACTION_SORT_FIELD;
  return field;
};
  const getInitOiSortField = () => {
    try {
      const stored = localStorage.getItem(OI_SORT_FIELD_KEY);
      return normalizeOiField(stored || 'requestdate');
    } catch {
      return 'requestdate';
    }
  };
  const getInitOiSortOrder = () => {
    try { return localStorage.getItem(OI_SORT_ORDER_KEY) || 'DESC'; } catch { return 'DESC'; }
  };
  const getInitOiSortStack = () => {
    try {
      const raw = localStorage.getItem(OI_SORT_STACK_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((s) => (s && typeof s.field === 'string' && (s.dir === 'ASC' || s.dir === 'DESC') ? { field: normalizeOiField(s.field), dir: s.dir } : null))
        .filter(Boolean);
    } catch {
      return [];
    }
  };
  const [oiSortField, setOiSortField] = useState(getInitOiSortField());
  const [oiSortOrder, setOiSortOrder] = useState(getInitOiSortOrder());
  const [oiSortStack, setOiSortStack] = useState(getInitOiSortStack);
  useEffect(() => { try { localStorage.setItem(OI_SORT_FIELD_KEY, oiSortField); } catch {} }, [oiSortField]);
  useEffect(() => { try { localStorage.setItem(OI_SORT_ORDER_KEY, oiSortOrder); } catch {} }, [oiSortOrder]);
  useEffect(() => {
    try { localStorage.setItem(OI_SORT_STACK_KEY, JSON.stringify(oiSortStack)); } catch {}
  }, [oiSortStack]);

  const orderIsShipped = !!selected?.shippeddate;

  const sortedOrderItems = useMemo(() => {
    if (!Array.isArray(orderItems) || !orderItems.length) return [];
    const rows = [...orderItems];
    const spec = [
      { field: oiSortField, dir: oiSortOrder },
      ...oiSortStack.filter((s) => s.field !== oiSortField),
    ];
    const valueForField = (row, field) => {
      if (field === ORDER_ITEM_ACTION_SORT_FIELD) return getOrderItemActionSortValue(row, orderIsShipped);
      return row[field];
    };
    rows.sort((a, b) => {
      for (const { field, dir } of spec) {
        const mult = dir === 'ASC' ? 1 : -1;
        const A = valueForField(a, field);
        const B = valueForField(b, field);
        const aVal = typeof A === 'number' ? A : (Date.parse(A) || (A ?? ''));
        const bVal = typeof B === 'number' ? B : (Date.parse(B) || (B ?? ''));
        if (aVal > bVal) return mult;
        if (aVal < bVal) return -mult;
      }
      return 0;
    });
    return rows;
  }, [orderItems, oiSortField, oiSortOrder, oiSortStack, orderIsShipped]);

  // Totals for order line items (boxes/units, single pieces, total units requested, units picked, pieces picked)
  const orderItemsTotals = useMemo(() => {
    try {
      const list = Array.isArray(orderItems) ? orderItems : [];
      const sum = (field) => list.reduce((acc, r) => acc + (Number(r?.[field]) || 0), 0);
      return {
        boxes_units: sum('boxes_units'),
        single_pieces: sum('single_pieces'),
        totalpiecesrequested: sum('totalpiecesrequested'),
        unitspicked: sum('unitspicked'),
        piecespicked: sum('piecespicked'),
      };
    } catch {
      return { boxes_units: 0, single_pieces: 0, totalpiecesrequested: 0, unitspicked: 0, piecespicked: 0 };
    }
  }, [orderItems]);

  const handleOiSort = (field, e) => {
    if (!field) return;
    const isShift = e && e.shiftKey;

    if (!isShift) {
      if (oiSortField === field) {
        setOiSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
      } else {
        setOiSortField(field);
        setOiSortOrder('ASC');
      }
      // Remove this field from secondary stack when it becomes or stays primary
      setOiSortStack((prev) => prev.filter((s) => s.field !== field));
    } else {
      // Shift-click: add or toggle as secondary/tertiary sort
      if (field === oiSortField) {
        setOiSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
        return;
      }
      setOiSortStack((prev) => {
        const idx = prev.findIndex((s) => s.field === field);
        if (idx >= 0) {
          const next = [...prev];
          const cur = next[idx];
          next[idx] = { ...cur, dir: cur.dir === 'ASC' ? 'DESC' : 'ASC' };
          return next;
        }
        return [...prev, { field, dir: 'ASC' }];
      });
    }
  };
  const renderOiSortIcon = (field) => (
    oiSortField !== field
      ? <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />
      : (oiSortOrder === 'ASC'
        ? <FaSortUp style={{ color: 'var(--primary-color)', marginLeft: 4 }} />
        : <FaSortDown style={{ color: 'var(--primary-color)', marginLeft: 4 }} />)
  );
  const [itemsLoading, setItemsLoading] = useState(false);

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachDesc, setAttachDesc] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [itemStatuses, setItemStatuses] = useState([]);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailOrderId, setEmailOrderId] = useState(null);
  const [emailFields, setEmailFields] = useState({
    to: '', cc: '', bcc: '', subject: '', message: '',
  });
  const [emailSending, setEmailSending] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [emailAttachmentsLoading, setEmailAttachmentsLoading] = useState(false);
  const [emailAttachmentSelection, setEmailAttachmentSelection] = useState(new Set());
  const emailSingleOrderId = useMemo(() => {
    if (!showEmailModal) return null;
    if (emailOrderId) return emailOrderId;
    if (!emailOrderId && selectedOrderIds.length === 1) return selectedOrderIds[0];
    return null;
  }, [showEmailModal, emailOrderId, selectedOrderIds]);
  useEffect(() => {
    if (!showEmailModal || !emailSingleOrderId) {
      setEmailAttachments([]);
      setEmailAttachmentSelection(new Set());
      setEmailAttachmentsLoading(false);
      return;
    }
    let cancelled = false;
    setEmailAttachmentsLoading(true);
    (async () => {
      try {
        const data = await fetchJSON(`${API}/api/orders/${emailSingleOrderId}/attachments`);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setEmailAttachments(list);
        setEmailAttachmentSelection(new Set());
      } catch (err) {
        if (!cancelled) {
          setEmailAttachments([]);
          setEmailAttachmentSelection(new Set());
        }
      } finally {
        if (!cancelled) setEmailAttachmentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showEmailModal, emailSingleOrderId]);

  const [showNewCarrier, setShowNewCarrier] = useState(false);
  const [newCarrierName, setNewCarrierName] = useState('');
  const [showNewDestination, setShowNewDestination] = useState(false);
  const [newDestinationName, setNewDestinationName] = useState('');

  const [pickerOpen, setPickerOpen] = useState(false);

  // Pallet Labels modal state
  const [palletLabelsOpen, setPalletLabelsOpen] = useState(false);
  const [palletStart, setPalletStart] = useState(1);
  const [palletEnd, setPalletEnd] = useState(1);
  const openPalletLabels = () => {
    if (!selectedOrderId) return;
    setPalletLabelsOpen(true);
    // Default range: start at 1, end at total number of pallets on the order
    try {
      const uniq = new Set(
        (orderItems || [])
          .map(r => parseInt(String(r.palletnumber || '').replace(/\D+/g, ''), 10))
          .filter(n => Number.isFinite(n))
      );
      const total = uniq.size || 1;
      setPalletStart(1);
      setPalletEnd(total);
    } catch {
      setPalletStart(1);
      setPalletEnd(1);
    }
  };
  const confirmPalletLabels = () => {
    if (!selectedOrderId) return setPalletLabelsOpen(false);
    const s = parseInt(palletStart, 10);
    const e = parseInt(palletEnd, 10);
    if (!Number.isFinite(s) || !Number.isFinite(e) || s <= 0 || e < s) {
      openNotify('error', 'Invalid Range', 'Please enter a valid Start/End pallet number (End must be >= Start).');
      return;
    }
    const url = `${API}/api/orders/${selectedOrderId}/pallet-labels.pdf?start=${s}&end=${e}`;
    try { window.open(url, '_blank', 'noopener,noreferrer'); } catch {}
    setPalletLabelsOpen(false);
  };

  // ===== Nice universal confirm dialog (no window.confirm) =====
  const [confirmState, setConfirmState] = useState({ open: false, message: '', onConfirm: null });
  const openConfirm = (message, onConfirm) => {
    setConfirmState({ open: true, message, onConfirm });
  };
  const closeConfirm = () => setConfirmState({ open: false, message: '', onConfirm: null });

  // ===== Assign Order Modal state =====
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignSelected, setAssignSelected] = useState(null);
  const [assignPriority, setAssignPriority] = useState('');
  const [assignPriorityDirty, setAssignPriorityDirty] = useState(false);
  const [assignForOrder, setAssignForOrder] = useState(null);

  // Print Order sort choice modal
  const [printSortOpen, setPrintSortOpen] = useState(false);
  const [printForOrderId, setPrintForOrderId] = useState(null);
  const launchOrderPdf = (sort) => {
    if (!printForOrderId) { setPrintSortOpen(false); return; }
    try { window.open(`${API}/api/orders/${printForOrderId}/pdf?sort=${sort}`, '_blank', 'noopener,noreferrer'); } catch {}
    setPrintSortOpen(false);
    setPrintForOrderId(null);
  };


  useEffect(() => {
    const handleCreateOrder = (event) => {
      const detail = event.detail || {};
      const { userId, warehouseId, clientId, orderdate, po, notes } = detail;
      if (!warehouseId || !clientId) {
        console.log('[Orders] Invalid createOrder payload:', JSON.stringify(detail, null, 2));
        openNotify('error', 'Error', 'Cannot create order: missing warehouse or client.');
        return;
      }
      console.log('[Orders] Received createOrder:', JSON.stringify(detail, null, 2));
      const currentDate = fmtYMD(new Date()); // Use the existing fmtYMD helper
      const newOrder = {
        ...emptyOrder,
        warehouseid: warehouseId,
        datecreated: currentDate,
        orderdate: orderdate || currentDate, // Default to current date if not provided
        statusid: createdStatusId,
        userid: userId || getCurrentUserId() || 0,
        clientid: clientId,
        po: po || '',
        notes: notes || '',
      };
      setSelected(newOrder);
      setEditing(true);
      setBottomPaneMode(null);
      setSelectedOrderId(null);
      saveOrder().then(() => {
        navigate('/orders');
      }).catch((e) => {
        console.log('[Orders] Save failed:', e.message);
        openNotify('error', 'Error', `Failed to create order: ${e.message}`);
        setSelected(null);
        setEditing(false);
      });
    };
    window.addEventListener('agent:createOrder', handleCreateOrder);
    return () => window.removeEventListener('agent:createOrder', handleCreateOrder);
  }, [createdStatusId, navigate]);

  const canChangeWarehouse = useMemo(() => {
    if (!selected) return false;
    // New order: always allow changing warehouse
    if (!selected.id) return true;

    const isCreated = Number(selected.statusid) === Number(createdStatusId);
    const hasItems = (orderItems?.length || 0) > 0;

    return isCreated && !hasItems;
  }, [selected?.id, selected?.statusid, createdStatusId, orderItems]);

	  const applyDatePreset = (preset, { silent = false } = {}) => {
	    const today = new Date();
	    let from = '', to = '';

    switch (preset) {
      case 'today': {
        const t = fmtYMD(today);
        from = t; to = t;
        break;
      }
      case 'yesterday': {
        const y = new Date(today); y.setDate(y.getDate() - 1);
        const t = fmtYMD(y);
        from = t; to = t;
        break;
      }
      case 'this_week': {
        const s = startOfWeekMon(today);
        from = fmtYMD(s);
        to = fmtYMD(today);
        break;
      }
      case 'last_week': {
        const s0 = startOfWeekMon(today);
        const s = new Date(s0); s.setDate(s0.getDate() - 7);
        const e = new Date(s); e.setDate(s.getDate() + 6);
        from = fmtYMD(s);
        to = fmtYMD(e);
        break;
      }
      case 'this_month': {
        const s = startOfMonth(today);
        const e = endOfMonth(today);
        from = fmtYMD(s);
        to = fmtYMD(e);
        break;
      }
      case 'last_month': {
        const s = startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1));
        const e = endOfMonth(s);
        from = fmtYMD(s);
        to = fmtYMD(e);
        break;
      }
      case 'this_year': {
        const s = startOfYear(today.getFullYear());
        from = fmtYMD(s);
        to = fmtYMD(today);
        break;
      }
      case 'last_year': {
        const y = today.getFullYear() - 1;
        from = fmtYMD(startOfYear(y));
        to = fmtYMD(endOfYear(y));
        break;
      }
      case 'all': {
        from = '';
        to = '';
        break;
      }
      default: {
        // "custom" / cleared
        localStorage.setItem('ordersDatePreset', '');
        setDatePreset('');
        return;
      }
    }

    setFromDate(from);
    setToDate(to);
    setDatePreset(preset);
    localStorage.setItem('ordersDatePreset', preset);

    if (silent) return;

    // kick off a search with new range
    setOrders([]);
    setMeta({ total: 0, limit, offset: 0 });
    fetchOrders(0, limit);
  };

  const presetInitRef = useRef(false);

  useEffect(() => {
    if (presetInitRef.current) return;
    presetInitRef.current = true;
    const savedPreset = localStorage.getItem('ordersDatePreset') || '';
    if (savedPreset) {
      applyDatePreset(savedPreset, { silent: true });
      return;
    }
    if (!fromDate && !toDate) {
      applyDatePreset('this_week', { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
	  }, []);

  useEffect(() => {
    if (liveViewOnly) return;

    let params;
    try {
      params = new URLSearchParams(location.search || '');
    } catch {
      return;
    }

    if ((params.get('agent_view') || '').trim().toLowerCase() !== 'orders') return;

    const validYmd = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim());
    const rawDateField = String(params.get('agent_datefield') || 'orderdate').trim().toLowerCase();
    const nextDateField = rawDateField === 'datecreated' ? 'datecreated'
      : rawDateField === 'shippeddate' ? 'shippeddate'
      : 'orderdate';
    const nextFrom = validYmd(params.get('agent_from')) ? String(params.get('agent_from')).trim() : '';
    const nextTo = validYmd(params.get('agent_to')) ? String(params.get('agent_to')).trim() : '';

    setQ('');
    setFromDate(nextFrom);
    setToDate(nextTo);
    setShippedFromDate('');
    setShippedToDate('');
    setShowDeleted(false);
    setStatusIds([]);
    setDateField(nextDateField);
    setClientFilter('');
    setClientIdFilter(IS_CLIENT ? (LOCKED_CLIENT_ID ? String(LOCKED_CLIENT_ID) : '') : '');
    setSkuFilter('');
    setCreatorFilter('');
    setAssignedFilter('');
    setBilledFilter('');
    setDatePreset('');
    setSelected(null);
    setSelectedOrderId(null);
    setBottomPaneMode(null);
    setOrderItems([]);
    setHistory([]);
    setAttachments([]);
    setSelectedOrderIds([]);
    try { localStorage.setItem('ordersDatePreset', ''); } catch {}

    [
      'agent_view',
      'agent_datefield',
      'agent_from',
      'agent_to',
      'agent_q',
      'agent_clientid',
      'agent_clientname',
      'agent_statusids',
    ].forEach((key) => params.delete(key));

    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [IS_CLIENT, LOCKED_CLIENT_ID, liveViewOnly, location.pathname, location.search, navigate]);

	  const applyLiveViewDatePreset = (preset, { silent = false } = {}) => {
	    const today = new Date();
	    let from = '', to = '';
	
	    switch (preset) {
	      case 'today': {
	        const t = fmtYMD(today);
	        from = t; to = t;
	        break;
	      }
	      case 'yesterday': {
	        const y = new Date(today); y.setDate(y.getDate() - 1);
	        const t = fmtYMD(y);
	        from = t; to = t;
	        break;
	      }
	      case 'this_week': {
	        const s = startOfWeekMon(today);
	        from = fmtYMD(s);
	        to = fmtYMD(today);
	        break;
	      }
	      case 'last_week': {
	        const s0 = startOfWeekMon(today);
	        const s = new Date(s0); s.setDate(s0.getDate() - 7);
	        const e = new Date(s); e.setDate(s.getDate() + 6);
	        from = fmtYMD(s);
	        to = fmtYMD(e);
	        break;
	      }
	      case 'this_month': {
	        const s = startOfMonth(today);
	        const e = endOfMonth(today);
	        from = fmtYMD(s);
	        to = fmtYMD(e);
	        break;
	      }
	      case 'last_month': {
	        const s = startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1));
	        const e = endOfMonth(s);
	        from = fmtYMD(s);
	        to = fmtYMD(e);
	        break;
	      }
	      case 'this_year': {
	        const s = startOfYear(today.getFullYear());
	        from = fmtYMD(s);
	        to = fmtYMD(today);
	        break;
	      }
	      case 'last_year': {
	        const y = today.getFullYear() - 1;
	        from = fmtYMD(startOfYear(y));
	        to = fmtYMD(endOfYear(y));
	        break;
	      }
	      case 'all': {
	        from = '';
	        to = '';
	        break;
	      }
	      default: {
	        localStorage.setItem('ordersLiveViewDatePreset', '');
	        setLiveViewDatePreset('');
	        return;
	      }
	    }
	
	    setLiveViewFromDate(from);
	    setLiveViewToDate(to);
	    setLiveViewDatePreset(preset);
	    localStorage.setItem('ordersLiveViewDatePreset', preset);
	
	    if (silent) return;
	    // Live View will re-fetch via liveViewFilterSignature effect when open.
	  };
	
	  const livePresetInitRef = useRef(false);
	  useEffect(() => {
	    if (livePresetInitRef.current) return;
	    livePresetInitRef.current = true;
	    const savedPreset = localStorage.getItem('ordersLiveViewDatePreset') || '';
	    if (savedPreset) {
	      applyLiveViewDatePreset(savedPreset, { silent: true });
	      return;
	    }
	    if (!liveViewFromDate && !liveViewToDate) {
	      applyLiveViewDatePreset('this_week', { silent: true });
	    }
	    // eslint-disable-next-line react-hooks/exhaustive-deps
	  }, []);

	  const applyShippedDatePreset = (preset) => {
	    const today = new Date();
	    let from = '', to = '';

    switch (preset) {
      case 'today': {
        const t = fmtYMD(today);
        from = t; to = t;
        break;
      }
      case 'yesterday': {
        const y = new Date(today); y.setDate(y.getDate() - 1);
        const t = fmtYMD(y);
        from = t; to = t;
        break;
      }
      case 'this_week': {
        from = fmtYMD(startOfWeekMon(today));
        to = fmtYMD(today);
        break;
      }
      case 'last_week': {
        const s0 = startOfWeekMon(today);
        const s = new Date(s0); s.setDate(s0.getDate() - 7);
        const e = new Date(s); e.setDate(s.getDate() + 6);
        from = fmtYMD(s);
        to = fmtYMD(e);
        break;
      }
      case 'this_month': {
        from = fmtYMD(startOfMonth(today));
        to = fmtYMD(endOfMonth(today));
        break;
      }
      case 'last_month': {
        const s = startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1));
        const e = endOfMonth(s);
        from = fmtYMD(s);
        to = fmtYMD(e);
        break;
      }
      case 'this_year': {
        from = fmtYMD(startOfYear(today.getFullYear()));
        to = fmtYMD(today);
        break;
      }
      case 'last_year': {
        const y = today.getFullYear() - 1;
        from = fmtYMD(startOfYear(y));
        to = fmtYMD(endOfYear(y));
        break;
      }
      case 'all': {
        from = '';
        to = '';
        break;
      }
      default: {
        localStorage.setItem('ordersShippedDatePreset', '');
        setShippedDatePreset('');
        return;
      }
    }

    setShippedFromDate(from);
    setShippedToDate(to);
    setShippedDatePreset(preset);
    localStorage.setItem('ordersShippedDatePreset', preset);

    setOrders([]);
    setMeta({ total: 0, limit, offset: 0 });
    fetchOrders(0, limit);
  };

  const placePreview
    = (clientX, clientY) => {
      const vw = window.innerWidth, vh = window.innerHeight;
      const margin = 12, offset = 16;
      const boxW = previewRef.current?.offsetWidth || Math.min(vw * 0.4, 520);
      const boxH = previewRef.current?.offsetHeight || Math.min(vh * 0.6, 520);
      let left = clientX + offset, top = clientY + offset;
      if (left + boxW + margin > vw) left = clientX - boxW - offset;
      if (top + boxH + margin > vh) top = clientY - boxH - offset;
      if (left < margin) left = margin;
      if (top < margin) top = margin;
      setPreview(p => ({ ...p, x: left, y: top }));
    };
  const showPreview = (raw, e) => {
    const src = resolveItemThumb({ image1: raw, itemimageurl: raw }, blobUrlBag);
    if (!src) return;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setPreview({ src, x: e.clientX, y: e.clientY, visible: true });
    requestAnimationFrame(() => placePreview(e.clientX, e.clientY));
  };
  const movePreview = (e) => {
    if (!preview.visible) return;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    placePreview(e.clientX, e.clientY);
  };
  const hidePreview = () => setPreview(p => ({ ...p, visible: false }));
  const onPreviewImgLoad = () => {
    if (!preview.visible) return;
    const { x, y } = lastPosRef.current;
    placePreview(x, y);
  };

  const openAssignModal = async (order) => {
    setAssignOpen(true);
    setAssignForOrder(order);
    setAssignSelected(order?.assignedto ? String(order.assignedto) : '');
    if (order?.assignedpriority != null) {
      setAssignPriority(String(order.assignedpriority));
      setAssignPriorityDirty(true);
    } else {
      setAssignPriority('');
      setAssignPriorityDirty(false);
    }
    setAssignLoading(true);

    try {
      const adminUserId = getCurrentUserId();

      // Pass ?userid=... to satisfy ensureAdmin on the backend
      let res = await fetch(`${API}/api/users?active=1&internalOnly=1&userid=${adminUserId}`)
      if (!res.ok) {
        res = await fetch(`${API}/api/users?userid=${encodeURIComponent(adminUserId ?? '')}`);
      }
      if (!res.ok) {
        setAssignUsers([]);
        setAssignLoading(false);
        openNotify('error', 'Error', 'You must be an admin to view the user list.');
        return;
      }

      const json = await res.json();
      const raw = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);

      // ⬇️ INTERNAL-ONLY: filter out client users (isclient === 1)
      const users = raw
        .filter(u => Number(u.isclient) !== 1)              // internal only
        .map(u => ({
          userid: u.userid ?? u.id ?? u.userId ?? '',
          fullname: u.fullname ?? u.name ?? u.login ?? '',
          login: u.login ?? '',
        }))
        .filter(u => u.userid && u.fullname)
        .sort((a, b) => a.fullname.localeCompare(b.fullname, undefined, { sensitivity: 'base' }));

      setAssignUsers(users);
      // Default to first user if nothing preselected
      if (!order?.assignedto && users.length > 0) {
        setAssignSelected(String(users[0].userid));
        setAssignPriority('');
        setAssignPriorityDirty(false);
      }
    } catch {
      setAssignUsers([]);
    } finally {
      setAssignLoading(false);
    }
  };

  const confirmAssign = async () => {
    if (!assignForOrder?.id || !assignSelected) { setAssignOpen(false); return; }
    try {
      const picked = assignUsers.find(u => String(u.userid) === String(assignSelected));
      const assignedToName = picked?.fullname || String(assignSelected);
      const res = await fetch(`${API}/api/orders/${assignForOrder.id}/assign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignedto: Number(assignSelected),
          assignedto_name: assignedToName,
          priority: assignPriority !== '' ? Number(assignPriority) : null,
          username: getUserFullName()
        }),
      });
      const j = await res.json();
      if (!res.ok || !j.success) 
        return openNotify('error', 'Error', 'Assign failed.');

      setAssignOpen(false);
      setAssignSelected(null);
      setAssignPriority('');
      setAssignPriorityDirty(false);
      setAssignForOrder(null);
      fetchOrders(meta.offset, limit);
    } catch (e) {
      openNotify('error', 'Error', 'Assign failed: ' + e.message);
    }
  };

  useEffect(() => {
    if (!assignOpen) return;
    if (!assignSelected || assignPriorityDirty) return;
    const numericId = Number(assignSelected);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      setAssignPriority('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(`${API}/api/orders/assignees/${numericId}/next-priority`);
        if (cancelled) return;
        if (data && data.nextPriority != null) {
          setAssignPriority(String(data.nextPriority));
        }
      } catch (err) {
        if (!cancelled) setAssignPriority('');
      }
    })();
    return () => { cancelled = true; };
  }, [assignOpen, assignSelected, assignPriorityDirty]);

  const closeAssign = () => {
    setAssignOpen(false);
    setAssignSelected(null);
    setAssignPriority('');
    setAssignPriorityDirty(false);
    setAssignForOrder(null);
  };

  const openPicker = (e, o) => {
    e?.stopPropagation?.();
    if (!o?.id) return;
    setSelected(o);
    setSelectedOrderId(o.id);
    setBottomPaneMode('items');
    setTimeout(() => setPickerOpen(true), 0);
  };

  const getInitialSplitHeights = () => {
    try {
      const saved = localStorage.getItem('ordersItemsSplitHeights');
      if (saved) {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr) && arr.length === 2) return arr;
      }
      const pct = parseInt(localStorage.getItem('ordersItemsSplitPct') || '', 10);
      if (!Number.isNaN(pct)) return [100 - pct, pct];
    } catch { }
    return [65, 35];
  };
  const [splitHeights, setSplitHeights] = useState(getInitialSplitHeights);

  // ===== Resizable columns (match Inventory/Receipts) =====
  const ORDER_COLS = [
    'select', 'actions', 'id', 'clientname', 'statusid', 'datecreated', 'orderdate',
    'shippeddate', 'createdby', 'assignedto', 'warehouse', 'po', 'bol', 'trackingnumber', 'notes'
  ];
  const ORDER_COLS_VISIBLE = useMemo(() => (
    IS_CLIENT ? ORDER_COLS.filter(k => k !== 'select') : ORDER_COLS
  ), [IS_CLIENT]);
  const getInitialOrderColWidths = () => {
    try {
      const raw = localStorage.getItem(LS_ORD_COLWIDTHS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch { return {}; }
  };
  const [ordColWidths, setOrdColWidths] = useState(getInitialOrderColWidths());
  const ordResizeRef = useRef({ key: null, startX: 0, startW: 0 });
  const ordResizedRecently = useRef(false);
  const ordHeaderRowRef = useRef(null);
  const ordTableRef = useRef(null);
  const orderRowRefs = useRef(new Map());
  const clientColStyle = ordColWidths?.clientname
    ? { width: ordColWidths.clientname, maxWidth: ordColWidths.clientname }
    : undefined;

  useEffect(() => {
    try { localStorage.setItem(LS_ORD_COLWIDTHS_KEY, JSON.stringify(ordColWidths)); } catch {}
  }, [ordColWidths]);

  const ensureOrdWidthsLocked = () => {
    if (Object.keys(ordColWidths || {}).length === ORDER_COLS_VISIBLE.length) return;
    const row = ordHeaderRowRef.current;
    if (!row) return;
    const ths = Array.from(row.children || []);
    const next = {};
    ORDER_COLS_VISIBLE.forEach((k, idx) => { const th = ths[idx]; if (th) next[k] = th.offsetWidth; });
    setOrdColWidths(next);
  };

  const onOrdResizeMove = (e) => {
    const { key, startX, startW } = ordResizeRef.current;
    if (!key) return;
    const delta = e.clientX - startX;
    const nextW = Math.max(MIN_COL_W, startW + delta);
    setOrdColWidths(prev => ({ ...prev, [key]: nextW }));
    e.preventDefault();
  };
  const onOrdResizeUp = () => {
    ordResizeRef.current = { key: null, startX: 0, startW: 0 };
    try { document.body.classList.remove('col-resizing'); } catch {}
    window.removeEventListener('mousemove', onOrdResizeMove);
    window.removeEventListener('mouseup', onOrdResizeUp);
    ordResizedRecently.current = true; setTimeout(() => { ordResizedRecently.current = false; }, 150);
  };
  const startOrdResize = (key, e) => {
    ensureOrdWidthsLocked();
    const th = e.currentTarget.parentElement;
    ordResizeRef.current = { key, startX: e.clientX, startW: th.offsetWidth };
    try { document.body.classList.add('col-resizing'); } catch {}
    window.addEventListener('mousemove', onOrdResizeMove);
    window.addEventListener('mouseup', onOrdResizeUp);
    e.preventDefault(); e.stopPropagation();
  };

  const autoFitOrderCol = (key) => {
    try {
      const cols = ORDER_COLS_VISIBLE;
      const idx = cols.indexOf(key);
      if (idx === -1) return;
      let maxW = 0;
      const hdr = ordHeaderRowRef.current?.children?.[idx];
      if (hdr) maxW = Math.max(maxW, Math.ceil(hdr.scrollWidth));
      const tbl = ordTableRef.current;
      if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r++) {
          const cell = rows[r].children[idx];
          if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth));
        }
      }
      const target = Math.max(MIN_COL_W, Math.min(800, maxW));
      setOrdColWidths(prev => ({ ...prev, [key]: target }));
    } catch {}
  };

  // ===== Order Items table resizable columns =====
  const LS_ORD_ITEMS_COLWIDTHS_KEY = 'ordersItemsColWidths.v1';
  const ORDER_ITEM_COLS = [
    'select','action','status','requestdate','receiptid','palletnumber','palletstorageunit','area','location','image','sku','itemdescription','lotnum','partnumber','boxes_units','single_pieces','totalpiecesrequested','unitspicked','piecespicked','bol','trackingnumber','shipdate','shippingpallet','shippingpalletcarrier'
  ];
  const getInitialOrderItemsColWidths = () => {
    try {
      const raw = localStorage.getItem(LS_ORD_ITEMS_COLWIDTHS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch { return {}; }
  };
  const [ordItemsColWidths, setOrdItemsColWidths] = useState(getInitialOrderItemsColWidths());
  const ordItemsResizeRef = useRef({ key: null, startX: 0, startW: 0 });
  const ordItemsHeaderRowRef = useRef(null);
  const ordItemsTableRef = useRef(null);
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());
  // True when any selected line item already has status Shipped
  const selectionHasShipped = useMemo(() => {
    if (!selectedItemIds || selectedItemIds.size === 0) return false;
    try {
      return (orderItems || []).some(
        (it) => selectedItemIds.has(it.id) && String(it.statusname || '').trim().toLowerCase() === 'shipped'
      );
    } catch { return false; }
  }, [selectedItemIds, orderItems]);
  const toggleItemSelected = (id) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllItems = (checked) => {
    if (checked) {
      setSelectedItemIds(new Set(orderItems.map(r => r.id).filter(Boolean)));
    } else {
      setSelectedItemIds(new Set());
    }
  };
  useEffect(() => { try { localStorage.setItem(LS_ORD_ITEMS_COLWIDTHS_KEY, JSON.stringify(ordItemsColWidths)); } catch {} }, [ordItemsColWidths]);
  const ensureOrdItemsWidthsLocked = () => {
    if (Object.keys(ordItemsColWidths || {}).length === ORDER_ITEM_COLS.length) return;
    const row = ordItemsHeaderRowRef.current; if (!row) return;
    const next = {};
    ORDER_ITEM_COLS.forEach((k, idx) => { const th = row.children?.[idx]; if (th) next[k] = th.offsetWidth; });
    setOrdItemsColWidths(next);
  };
  const onOrdItemsResizeMove = (e) => {
    const { key, startX, startW } = ordItemsResizeRef.current; if (!key) return;
    const delta = e.clientX - startX; const nextW = Math.max(MIN_COL_W, startW + delta);
    setOrdItemsColWidths(prev => ({ ...prev, [key]: nextW })); e.preventDefault();
  };
  const onOrdItemsResizeUp = () => {
    ordItemsResizeRef.current = { key: null, startX: 0, startW: 0 };
    try { document.body.classList.remove('col-resizing'); } catch {}
    window.removeEventListener('mousemove', onOrdItemsResizeMove);
    window.removeEventListener('mouseup', onOrdItemsResizeUp);
  };
  const startOrdItemsResize = (key, e) => {
    ensureOrdItemsWidthsLocked();
    const th = e.currentTarget.parentElement;
    ordItemsResizeRef.current = { key, startX: e.clientX, startW: th.offsetWidth };
    try { document.body.classList.add('col-resizing'); } catch {}
    window.addEventListener('mousemove', onOrdItemsResizeMove);
    window.addEventListener('mouseup', onOrdItemsResizeUp);
    e.preventDefault(); e.stopPropagation();
  };
  const autoFitOrderItemCol = (key) => {
    try {
      const idx = ORDER_ITEM_COLS.indexOf(key); if (idx === -1) return;
      let maxW = 0;
      const hdr = ordItemsHeaderRowRef.current?.children?.[idx]; if (hdr) maxW = Math.max(maxW, Math.ceil(hdr.scrollWidth));
      const tbl = ordItemsTableRef.current; if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows; for (let r = 0; r < rows.length; r++) { const cell = rows[r].children[idx]; if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth)); }
      }
      const target = Math.max(MIN_COL_W, Math.min(800, maxW));
      setOrdItemsColWidths(prev => ({ ...prev, [key]: target }));
    } catch {}
  };

  // Change Item Status modal state
  const [changeStatusOpen, setChangeStatusOpen] = useState(false);
  const [changeStatusId, setChangeStatusId] = useState('');
  const [changeShipDate, setChangeShipDate] = useState('');
  const [lineBolOpen, setLineBolOpen] = useState(false);
  const [lineBolValue, setLineBolValue] = useState('');
  const [lineBolSaving, setLineBolSaving] = useState(false);
  const [lineTrackingOpen, setLineTrackingOpen] = useState(false);
  const [lineTrackingValue, setLineTrackingValue] = useState('');
  const [lineTrackingSaving, setLineTrackingSaving] = useState(false);
  const [shippingPalletOpen, setShippingPalletOpen] = useState(false);
  const [shippingPalletValue, setShippingPalletValue] = useState('');
  const [shippingPalletSequential, setShippingPalletSequential] = useState(false);
  const [shippingPalletStartValue, setShippingPalletStartValue] = useState('');
  const [shippingPalletStepValue, setShippingPalletStepValue] = useState('1');
  const [shippingPalletCarrierId, setShippingPalletCarrierId] = useState('');
  const [shippingPalletSaving, setShippingPalletSaving] = useState(false);
  const handleGenerateLineItemBol = () => {
    if (!selectedOrderId) return;
    const selectedLines = resolveSelectedLineItems();
    if (!selectedLines.length) {
      openNotify('error', 'Select Line Items', 'Select one or more line items to include on the Bill of Lading.');
      return;
    }
    const missingPallet = selectedLines.filter(
      (it) => !String(it.shippingpalletnumber || '').trim()
    );
    if (missingPallet.length) {
      openNotify(
        'error',
        'Shipping Pallet Required',
        'Every selected line item must have a Shipping Pallet # before generating the BOL.'
      );
      return;
    }
    const uniqueCarrierIds = Array.from(
      new Set(
        selectedLines
          .map((it) => String(it?.shippingpalletcarrierid ?? '').trim())
          .filter(Boolean)
      )
    );
    if (uniqueCarrierIds.length > 1) {
      openNotify(
        'error',
        'Multiple Carriers Selected',
        'Selected line items must use the same carrier to generate a Bill of Lading.'
      );
      return;
    }
    const params = new URLSearchParams();
    params.set('itemIds', selectedLines.map((it) => it.id).join(','));
    const url = `${API}/api/orders/${selectedOrderId}/bol.pdf?${params.toString()}`;
    try { window.open(url, '_blank', 'noopener,noreferrer'); } catch {}
  };
  const openChangeStatus = () => {
    setChangeStatusOpen(true);
    // default shipped date to today when opening (user can change)
    try { setChangeShipDate(fmtYMD(new Date())); } catch { setChangeShipDate(''); }
  };
  const closeChangeStatus = () => { setChangeStatusOpen(false); setChangeStatusId(''); setChangeShipDate(''); };
  const confirmChangeStatus = async () => {
    const ids = Array.from(selectedItemIds || []);
    const sid = Number(changeStatusId);
    if (!ids.length || !sid) { closeChangeStatus(); return; }
    const selectedStatus = itemStatuses.find(st => Number(st.id || st.statusid) === sid);
    const isShipped = selectedStatus && String(selectedStatus.name || '').trim().toLowerCase() === 'shipped';
    const payload = { statusid: sid, username: getUserFullName() };
    if (isShipped && changeShipDate) payload.shipdate = changeShipDate;
    try {
      await Promise.all(ids.map(id => fetch(`${API}/api/orders/orderitems/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })));
      await fetchOrderItems(selectedOrderId);
      setSelectedItemIds(new Set());
       // Reload orders list so any auto-updated order header status is reflected
      await fetchOrders(meta.offset, limit);
      openNotify('success', 'Updated', `Changed status on ${ids.length} item(s).`);
    } catch (e) {
      openNotify('error', 'Error', `Failed to update item status: ${e.message || 'Unknown error'}`);
    } finally {
      closeChangeStatus();
    }
  };

  const resolveSelectedLineItems = () => {
    const ids = Array.from(selectedItemIds || []);
    if (!ids.length) return [];
    const idSet = new Set(ids);
    return (orderItems || []).filter(it => idSet.has(it.id));
  };

  const openLineItemBolModal = () => {
    if (!selectedItemIds?.size) return;
    const selectedLines = resolveSelectedLineItems();
    if (!selectedLines.length) return;
    const unique = Array.from(new Set(selectedLines.map(it => it?.bol ?? '')));
    const initial = unique.length === 1 ? (unique[0] || '') : '';
    setLineBolValue(initial);
    setLineBolOpen(true);
  };

  const closeLineItemBolModal = () => {
    setLineBolOpen(false);
    setLineBolValue('');
  };

  const confirmApplyLineItemBol = async () => {
    const ids = Array.from(selectedItemIds || []);
    if (!ids.length) {
      closeLineItemBolModal();
      return;
    }
    setLineBolSaving(true);
    try {
      const normalizedBolValue = typeof lineBolValue === 'string' ? lineBolValue.trim() : lineBolValue;
      await Promise.all(ids.map((id) =>
        fetchJSON(`${API}/api/orders/orderitems/${id}/bol`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bol: normalizedBolValue, username: getUserFullName() }),
        })
      ));
      if (selectedOrderId) await fetchOrderItems(selectedOrderId);
      openNotify('success', 'Updated', `Applied BOL to ${ids.length} line item(s).`);
      closeLineItemBolModal();
    } catch (err) {
      openNotify('error', 'Error', err?.message || 'Failed to update BOL.');
    } finally {
      setLineBolSaving(false);
    }
  };

  const openLineItemTrackingModal = () => {
    if (!selectedItemIds?.size) return;
    const selectedLines = resolveSelectedLineItems();
    if (!selectedLines.length) return;
    const unique = Array.from(new Set(selectedLines.map(it => it?.trackingnumber ?? '')));
    const initial = unique.length === 1 ? (unique[0] || '') : '';
    setLineTrackingValue(initial);
    setLineTrackingOpen(true);
  };

  const closeLineItemTrackingModal = () => {
    setLineTrackingOpen(false);
    setLineTrackingValue('');
    setLineTrackingSaving(false);
  };

  const confirmApplyLineItemTracking = async () => {
    const ids = Array.from(selectedItemIds || []);
    if (!ids.length) {
      closeLineItemTrackingModal();
      return;
    }
    setLineTrackingSaving(true);
    try {
      const normalizedTracking = typeof lineTrackingValue === 'string' ? lineTrackingValue.trim() : lineTrackingValue;
      await Promise.all(ids.map((id) =>
        fetchJSON(`${API}/api/orders/orderitems/${id}/tracking`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ trackingnumber: normalizedTracking, username: getUserFullName() }),
        })
      ));
      if (selectedOrderId) await fetchOrderItems(selectedOrderId);
      openNotify('success', 'Updated', `Applied Tracking # to ${ids.length} line item(s).`);
      closeLineItemTrackingModal();
    } catch (err) {
      openNotify('error', 'Error', err?.message || 'Failed to update Tracking #.');
    } finally {
      setLineTrackingSaving(false);
    }
  };

  const openShippingPalletModal = () => {
    if (!selectedItemIds?.size) return;
    const selectedLines = resolveSelectedLineItems();
    if (!selectedLines.length) return;
    const unique = Array.from(new Set(selectedLines.map(it => String(it?.shippingpalletnumber || '').trim())));
    setShippingPalletValue(unique.length === 1 ? unique[0] : '');
    setShippingPalletSequential(false);
    setShippingPalletStartValue('');
    setShippingPalletStepValue('1');
    const carrierUnique = Array.from(new Set(selectedLines.map(it => String(it?.shippingpalletcarrierid ?? '').trim())));
    setShippingPalletCarrierId(carrierUnique.length === 1 ? (carrierUnique[0] || '') : '');
    setShippingPalletOpen(true);
  };
  const closeShippingPalletModal = () => {
    setShippingPalletOpen(false);
    setShippingPalletValue('');
    setShippingPalletSequential(false);
    setShippingPalletStartValue('');
    setShippingPalletStepValue('1');
    setShippingPalletCarrierId('');
  };
  const confirmApplyShippingPallet = async () => {
    const selectedLines = resolveSelectedLineItems();
    if (!selectedLines.length) {
      closeShippingPalletModal();
      return;
    }
    setShippingPalletSaving(true);
    try {
      const carrierIdNum = shippingPalletCarrierId ? Number(shippingPalletCarrierId) : null;
      let assignments = [];
      if (shippingPalletSequential) {
        const start = Number(shippingPalletStartValue);
        const step = Number(shippingPalletStepValue || 1);
        if (!Number.isFinite(start) || start <= 0) {
          openNotify('error', 'Invalid Number', 'Enter a positive starting pallet number.');
          setShippingPalletSaving(false);
          return;
        }
        if (!Number.isFinite(step) || step <= 0) {
          openNotify('error', 'Invalid Step', 'Enter a positive increment for the pallet numbers.');
          setShippingPalletSaving(false);
          return;
        }
        assignments = selectedLines.map((it, idx) => ({
          orderinventoryid: it.id,
          palletnumber: start + idx * step,
          carrierid: carrierIdNum ?? null,
        }));
      } else {
        const num = Number(shippingPalletValue);
        if (!Number.isFinite(num) || num <= 0) {
          openNotify('error', 'Invalid Number', 'Enter a positive Shipping Pallet number.');
          setShippingPalletSaving(false);
          return;
        }
        assignments = selectedLines.map((it) => ({
          orderinventoryid: it.id,
          palletnumber: num,
          carrierid: carrierIdNum ?? null,
        }));
      }
      await fetchJSON(`${API}/api/orders/${selectedOrderId}/shipping-pallets`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignments, username: getUserFullName() }),
      });
      await fetchOrderItems(selectedOrderId);
      if (shippingPalletSequential) {
        const start = Number(shippingPalletStartValue);
        const step = Number(shippingPalletStepValue || 1);
        openNotify('success', 'Saved', `Assigned ${assignments.length} sequential pallets starting at #${start} (+${step}).`);
      } else {
        openNotify('success', 'Saved', `Assigned pallet #${assignments[0]?.palletnumber} to ${assignments.length} item(s).`);
      }
      closeShippingPalletModal();
    } catch (err) {
      openNotify('error', 'Error', err?.message || 'Failed to assign pallet number.');
    } finally {
      setShippingPalletSaving(false);
    }
  };

  const page = useMemo(
    () => Math.floor((meta.offset || 0) / (limit || DEFAULT_LIMIT)) + 1,
    [meta.offset, limit]
  );
  const lastPage = useMemo(
    () => Math.max(1, Math.ceil((meta.total || 0) / (limit || DEFAULT_LIMIT))),
    [meta.total, limit]
  );

  // Assign order (internal only) — replaced by modal picker
  async function assignOrder(o) {
    if (!o?.id) return;
    openAssignModal(o);
  }

  const fetchOrders = async (offset = 0, customLimit = limit, opts = {}) => {
    const { recenter = true } = opts || {};
    // increment sequence; capture this call's id
    const thisSeq = ++ordersFetchSeq.current;

    // cancel any in-flight request
    if (ordersFetchAbort.current) {
      try { ordersFetchAbort.current.abort(); } catch { }
    }
    const controller = new AbortController();
    ordersFetchAbort.current = controller;

    setLoadingList(true);

    try {
      const params = new URLSearchParams({
        limit: customLimit, offset, sortField, sortOrder,
      });
      if (q) params.set('q', q);
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      if (shippedFromDate) params.set('shipped_from', shippedFromDate);
      if (shippedToDate) params.set('shipped_to', shippedToDate);
      if (dateField) params.set('datefield', String(dateField));
      if (!showDeleted) params.set('deleted', 0);

      if (!IS_CLIENT && clientFilter) params.set('clientname', clientFilter);
      if (assignedFilter) params.set('assigned', assignedFilter);
      if (skuFilter) params.set('sku', skuFilter);
      if (creatorFilter) params.set('creator', creatorFilter);
      // Only enforce clientid when in client portal context
      if (IS_CLIENT && clientIdFilter) params.set('clientid', String(clientIdFilter));
      if (Array.isArray(statusIds) && statusIds.length > 0) {
        params.set('statusIds', statusIds.join(','));
        if (statusIds.length === 1) params.set('statusId', statusIds[0]);
      }

      if (billedFilter === 'unbilled') params.set('hasinvoice','unbilled');
      else if (billedFilter === 'billed') params.set('hasinvoice','billed');

      const json = await fetchJSON(`${API}/api/orders?${params.toString()}`, {
        signal: controller.signal,
      });

      // ignore if a newer request has started
      if (thisSeq !== ordersFetchSeq.current) return;

      const nextOrders = json.data || [];
      setOrders(nextOrders);
      // Keep selected header in sync (non-editing only)
      if (!editing && selectedOrderId) {
        const found = nextOrders.find(r => Number(r.id) === Number(selectedOrderId));
        if (found) setSelected(found);
      }
      const total = Number(json?.meta?.total || 0);
      const safeLimit = Number.isFinite(+customLimit) && +customLimit > 0 ? +customLimit : DEFAULT_LIMIT;
      const requestedPage = Math.floor((offset || 0) / safeLimit) + 1;
      const computedLastPage = Math.max(1, Math.ceil(total / safeLimit));

      if (recenter && requestedPage > computedLastPage) {
        const newOffset = Math.max(0, (computedLastPage - 1) * safeLimit);
        setMeta({ total, limit: safeLimit, offset: newOffset });
        await fetchOrders(newOffset, safeLimit, { recenter: false });
        return;
      }

      setMeta(json.meta || { total, limit: safeLimit, offset });
    } catch (e) {
      // ignore abort errors
      if (e?.name === 'AbortError') return;
      // only apply error result if still the latest
      if (thisSeq !== ordersFetchSeq.current) return;
      setOrders([]);
      setMeta({ total: 0, limit: Number.isFinite(+customLimit)&&+customLimit>0?+customLimit:DEFAULT_LIMIT, offset });
    } finally {
      // only clear loading if still the latest
      if (thisSeq === ordersFetchSeq.current) setLoadingList(false);
    }
  };
  const handleLiveViewRowClick = (order) => {
    if (!order || !order.id) return;
    const id = Number(order.id);
    if (!id) return;
    liveViewRestoreSelectionRef.current = null;
    setLiveViewSelectedOrderId(id);
    setLiveViewItems([]);
    fetchLiveViewOrderItems(id);
  };
  const handleCloseLiveViewDetails = useCallback(() => {
    liveViewRestoreSelectionRef.current = null;
    setLiveViewSelectedOrderId(null);
    setLiveViewItems([]);
  }, []);
  const handleLiveViewSort = (field) => {
    if (!field) return;
    if (liveViewSortField !== field) {
      setLiveViewSortField(field);
      setLiveViewSortDir('ASC');
      return;
    }
    if (liveViewSortDir === 'ASC') {
      setLiveViewSortDir('DESC');
    } else {
      setLiveViewSortField(null);
      setLiveViewSortDir('ASC');
    }
  };
  const handleLiveViewDetailSort = (field) => {
    if (!field) return;
    if (liveViewDetailSortField !== field) {
      setLiveViewDetailSortField(field);
      setLiveViewDetailSortDir('ASC');
      return;
    }
    if (liveViewDetailSortDir === 'ASC') {
      setLiveViewDetailSortDir('DESC');
    } else {
      setLiveViewDetailSortDir('ASC');
    }
  };
  const renderLiveViewSortIndicator = (field, activeField, dir) => {
    if (activeField !== field) return '';
    return dir === 'ASC' ? ' ▲' : ' ▼';
  };

  const handleLiveViewDragStart = useCallback((order, event) => {
    if (!liveViewCanDrag || !order?.assignedto) {
      event.preventDefault();
      return;
    }
    liveViewDragSourceRef.current = order;
    setLiveViewDraggingId(order.id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(order.id));
  }, [liveViewCanDrag]);

  const handleLiveViewDragOver = useCallback((order, event) => {
    const src = liveViewDragSourceRef.current;
    if (!src || !liveViewCanDrag) return;
    if (Number(order?.assignedto) !== Number(src.assignedto)) return;
    event.preventDefault();
    if (liveViewDragOverId !== order.id) setLiveViewDragOverId(order.id);
  }, [liveViewCanDrag, liveViewDragOverId]);

  const handleLiveViewDragEnd = useCallback(() => {
    setLiveViewDraggingId(null);
    setLiveViewDragOverId(null);
    liveViewDragSourceRef.current = null;
  }, []);

  const handleLiveViewDrop = useCallback((order, event) => {
    event.preventDefault();
    const src = liveViewDragSourceRef.current;
    if (!src || !order) {
      handleLiveViewDragEnd();
      return;
    }
    setLiveViewDragOverId(null);
    setLiveViewDraggingId(null);
    liveViewDragSourceRef.current = null;
    if (!liveViewHasFetched) return;
    if (Number(order.id) === Number(src.id)) return;
    if (!order.assignedto || Number(order.assignedto) !== Number(src.assignedto)) return;
    if (!Array.isArray(liveViewOrdersRaw) || !liveViewOrdersRaw.length) return;

    const next = [...liveViewOrdersRaw];
    const fromIdx = next.findIndex((o) => Number(o.id) === Number(src.id));
    const toIdx = next.findIndex((o) => Number(o.id) === Number(order.id));
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setLiveViewOrdersRaw(next);

    const assignedId = Number(moved.assignedto);
    if (assignedId) {
      const orderedIds = next
        .filter((o) => Number(o.assignedto) === assignedId)
        .map((o) => o.id);
      persistLiveViewPriorities(assignedId, orderedIds, moved.assignedto_name || '');
    }
  }, [handleLiveViewDragEnd, liveViewHasFetched, liveViewOrdersRaw, persistLiveViewPriorities]);

  const fetchStatusOptions = async () => {
    try {
      const json = await fetchJSON(`${API}/api/orders/status/all`);
      setStatusOptions(Array.isArray(json) ? json : []);
    } catch { setStatusOptions([]); }
  };
  const fetchClientOptions = async () => {
    try {
      let opts = [];
      if (IS_CLIENT) {
        const res = await fetch(`${API}/api/reports/filters/clients`);
        const data = res.ok ? await res.json() : [];
        opts = (Array.isArray(data) ? data : []).map(c => ({ id: String(c.id), name: c.name }));
      } else {
        const json = await fetchJSON(`${API}/api/clients?limit=1000`);
        opts = Array.isArray(json.data)
          ? json.data.map(c => ({ id: String(c.id), name: c.name }))
          : [];
      }
      setClientOptions(opts);
      if (IS_CLIENT && opts.length === 1) {
        const onlyClientId = String(opts[0]?.id || '');
        setClientIdFilter(onlyClientId);
      }
    } catch {
      setClientOptions([]);
    }
  };
  const fetchWarehouseOptions = async () => {
    try {
      // For both internal and client users, provide a warehouse list so
      // the New Order form has options. Use the lightweight shared filters
      // endpoint which returns [{ id, name }]. Fallback to /api/warehouses
      // if needed.
      let rows = await fetchJSON(`${API}/api/reports/filters/warehouses`);
      if (!Array.isArray(rows) || rows.length === 0) {
        rows = await fetchJSON(`${API}/api/warehouses?limit=1000`);
      }
      const opts = (Array.isArray(rows) ? rows : []).map(w => ({ id: String(w.id), name: w.name }));
      setWarehouseOptions(opts);
    } catch {
      setWarehouseOptions([]);
    }
  };
  const fetchCarrierOptions = async () => {
    try {
      const json = await fetchJSON(`${API}/api/orders/carriers`);
      setCarrierOptions(Array.isArray(json) ? json.filter(c => !c.deleted).map(c => ({ id: String(c.id), name: c.name })) : []);
    } catch { setCarrierOptions([]); }
  };
  const fetchDestinationOptions = async () => {
    try {
      const json = await fetchJSON(`${API}/api/destinations?limit=1000`);
      setDestinationOptions(Array.isArray(json) ? json.filter(d => !d.deleted).map(d => ({ id: String(d.id), name: d.name })) : []);
    } catch { setDestinationOptions([]); }
  };
  useEffect(() => () => {
    if (liveViewFetchAbort.current) {
      try { liveViewFetchAbort.current.abort(); } catch {}
    }
  }, []);

  const fetchItemStatuses = async () => {
    try {
      const json = await fetchJSON(`${API}/api/orders/orderitems/orderitemstatus`);
      setItemStatuses(Array.isArray(json) ? json : []);
    } catch { setItemStatuses([]); }
  };

  // ====== REQUIRED FIELD VALIDATION ======
  const [fieldErrors, setFieldErrors] = useState({
    clientid: false,
    warehouseid: false,
    carrierid: false,
    orderdate: false,
  });
  const markError = (patch) => setFieldErrors(prev => ({ ...prev, ...patch }));

  const showFieldError = (name) => !!(formErrors?.[name] || fieldErrors?.[name]);

  // Compute the display name for the selected client
  const clientNameForSelected = useMemo(() => {
    if (!selected?.clientid) return '';
    const hit = clientOptions.find(c => String(c.id) === String(selected.clientid));
    return hit?.name || selected?.clientname || String(selected.clientid);
  }, [selected?.clientid, selected?.clientname, clientOptions]);

  function validateOrder(o, { isClientUser }) {
    const errs = {};

    // (Removed) Carrier requirement
    // (Removed) Ship-To requirements: name, address, city, state, zip

    // Still required: client for internal users
    if (!isClientUser && !o.clientid) errs.clientid = 'Client is required';

    // Still required: warehouse for brand-new orders
    if (!o.id && !o.warehouseid) errs.warehouseid = 'Warehouse is required';

    return errs;
  }

  const uploadFiles = async (files) => {
    if (!selectedOrderId || !files?.length) return;

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      if (attachDesc) {
        formData.append('description', attachDesc);
      }
      formData.append('username', getUserFullName());

      const res = await fetch(`${API}/api/orders/${selectedOrderId}/attachments`, {
        method: 'POST',
        body: formData,
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        openNotify('error', 'Error', json.error || 'Upload failed');
        return;
      }

      // refresh list and reset state
      await fetchOrderAttachments(selectedOrderId);
      setPendingFiles([]);
      setAttachDesc('');
      await fetchOrders(meta.offset, limit);
    } catch (err) {
      openNotify('error', 'Error', 'Upload failed: ' + err.message);
    }
  };

  const loadOrderById = async (id) => {
    try {
      const data = await fetchJSON(`${API}/api/orders/${id}`);
      return { ...emptyOrder, ...data };
    } catch (e) {
      console.error('loadOrderById failed', e);
      return null;
    }
  };

  const ftzOrderQueryRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(location.search || '');
    const requestedOrderId = Number(params.get('orderid') || 0) || 0;
    if (!requestedOrderId) {
      ftzOrderQueryRef.current = '';
      return () => { cancelled = true; };
    }
    if (ftzOrderQueryRef.current === String(requestedOrderId)) {
      return () => { cancelled = true; };
    }
    (async () => {
      const hydrated = await loadOrderById(requestedOrderId);
      if (cancelled || !hydrated) return;
      ftzOrderQueryRef.current = String(requestedOrderId);
      setSelected(hydrated);
      setSelectedOrderId(requestedOrderId);
      setEditing(true);
      setCreating(false);
      setBottomPaneMode('items');
    })();
    return () => { cancelled = true; };
  }, [location.search]);

  const runOrderFtzAction = async (action) => {
    const orderId = Number(selected?.id || 0);
    if (!orderId || !action) return;
    try {
      setFtzActionRunning(action);
      await fetchJSON(`${API}/api/orders/${orderId}/ftz/actions/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: getUserFullName() }),
      });
      const refreshed = await loadOrderById(orderId);
      if (refreshed) setSelected(refreshed);
      await fetchOrders(meta.offset, limit);
      openNotify('success', 'FTZ Updated', `FTZ outbound order marked ${action === 'validate' ? 'validated' : action}.`);
    } catch (e) {
      openNotify('error', 'Error', e.message || 'Failed to update FTZ outbound workflow.');
    } finally {
      setFtzActionRunning('');
    }
  };

  const exportOrderFtzPacket = async (orderId, format) => {
    const id = Number(orderId || 0);
    const suffix = String(format || '').trim().toLowerCase();
    if (!id || !suffix) return;
    try {
      const res = await fetch(`${API}/api/orders/${id}/ftz/export.${suffix}`);
      if (!res.ok) {
        let message = `Failed to export FTZ packet (${res.status})`;
        try {
          const body = await res.json();
          if (body?.error) message = body.error;
        } catch {
          try {
            const text = await res.text();
            if (text) message = text;
          } catch {}
        }
        openNotify('error', 'Export Failed', message);
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const disposition = res.headers.get('content-disposition') || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `ftz-outbound.${suffix}`;

      if (suffix === 'pdf') {
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      openNotify('error', 'Export Failed', error?.message || 'Failed to export FTZ packet.');
    }
  };

  // Whitelist order fields to avoid cyclic values and accidental extra props
  function buildOrderPayload(src = {}) {
    const pick = (k, def = '') => (src[k] !== undefined ? src[k] : def);
    const pickDateTime = (k) => {
      const value = pick(k, '');
      return typeof value === 'string' ? value.replace('T', ' ') : value;
    };
    const allowed = {
      id: pick('id', null),
      deleted: pick('deleted', 0),
      warehouseid: pick('warehouseid', ''),
      datecreated: pick('datecreated', ''),
      orderdate: pick('orderdate', ''),
      shippeddate: pick('shippeddate', ''),
      statusid: pick('statusid', 1),
      userid: pick('userid', ''),
      clientid: pick('clientid', 0),
      po: pick('po', ''),
      trackingnumber: pick('trackingnumber', ''),
      carrierid: pick('carrierid', -1),
      destinationid: pick('destinationid', 0),
      thirdpartybillingid: pick('thirdpartybillingid', 0),
      notes: pick('notes', ''),
      bol: pick('bol', ''),
      nonbillable: pick('nonbillable', 0),
      type: pick('type', 0),
      locked: pick('locked', 0),
      assignedto: pick('assignedto', null),
      // Ship-To
      shiptoname: pick('shiptoname', ''),
      shiptoaddress: pick('shiptoaddress', ''),
      shiptocity: pick('shiptocity', ''),
      shiptostate: pick('shiptostate', ''),
      shiptozip: pick('shiptozip', ''),
      shiptophone: pick('shiptophone', ''),
      is_ftz_outbound: pick('is_ftz_outbound', 0),
      ftz_removal_type: pick('ftz_removal_type', ''),
      ftz_removal_status: pick('ftz_removal_status', ''),
      ftz_broker_reference: pick('ftz_broker_reference', ''),
      ftz_ace_submission_id: pick('ftz_ace_submission_id', ''),
      ftz_ace_reference_no: pick('ftz_ace_reference_no', ''),
      ftz_export_reference: pick('ftz_export_reference', ''),
      ftz_inbond_number: pick('ftz_inbond_number', ''),
      ftz_inbond_type: pick('ftz_inbond_type', ''),
      ftz_inbond_origin_port: pick('ftz_inbond_origin_port', ''),
      ftz_inbond_destination_port: pick('ftz_inbond_destination_port', ''),
      ftz_inbond_carrier: pick('ftz_inbond_carrier', ''),
      ftz_inbond_conveyance: pick('ftz_inbond_conveyance', ''),
      ftz_status_message: pick('ftz_status_message', ''),
      ftz_filed_at: pickDateTime('ftz_filed_at'),
      ftz_authorized_at: pickDateTime('ftz_authorized_at'),
      ftz_removed_at: pickDateTime('ftz_removed_at'),
    };
    return allowed;
  }

  const saveOrder = async (order) => {
    console.log("save order");

    setSaving(true);
    const sel = order || selected || {};
    const isCreating = !sel.id;

    // Unified validation (highlights Client for internal users, Warehouse for new)
    const errs = validateOrder(sel, { isClientUser: IS_CLIENT });
    if (errs && Object.keys(errs).length) {
      console.log("2");
      setSaving(false);
      if (sel.id) {
        setFormErrors(errs); // existing order: show inline errors
      } else {
        const flags = Object.fromEntries(Object.keys(errs).map(k => [k, true]));
        setFieldErrors(prev => ({ ...prev, ...flags })); // new order: red borders
      }
      return;
    }

    if (Number(sel?.is_ftz_outbound || 0) === 1) {
      const removalType = String(sel?.ftz_removal_type || '').trim().toLowerCase();
      if (!removalType) {
        setSaving(false);
        openNotify('error', 'Error', 'FTZ outbound orders require a removal type.');
        return;
      }
      if (removalType === 'inbond' && !String(sel?.ftz_inbond_number || '').trim()) {
        setSaving(false);
        openNotify('error', 'Error', 'FTZ in-bond outbound orders require an in-bond number.');
        return;
      }
      if (removalType === 'inbond' && !String(sel?.ftz_inbond_type || '').trim()) {
        setSaving(false);
        openNotify('error', 'Error', 'FTZ in-bond outbound orders require an in-bond type.');
        return;
      }
      if (removalType === 'inbond' && !String(sel?.ftz_inbond_origin_port || '').trim()) {
        setSaving(false);
        openNotify('error', 'Error', 'FTZ in-bond outbound orders require an origin port.');
        return;
      }
      if (removalType === 'inbond' && !String(sel?.ftz_inbond_destination_port || '').trim()) {
        setSaving(false);
        openNotify('error', 'Error', 'FTZ in-bond outbound orders require a destination port.');
        return;
      }
    }

    console.log('[saveOrder] isCreating=', isCreating, 'selected keys=', Object.keys(sel || {}));

    if (isCreating) {
      console.log("2");
      const wid = Number(sel.warehouseid || 0);
      const cid = Number(sel.clientid || 0);
      const uid = Number(sel.userid || getCurrentUserId() || 0);

      console.log('[saveOrder] Saving with: userid=', uid, 'warehouseid=', wid, 'clientid=', cid);

      if (!wid) {
        setSaving(false);
        setFieldErrors(prev => ({ ...prev, warehouseid: true }));
        return;
      }
      if (!cid) {
        setSaving(false);
        setFieldErrors(prev => ({ ...prev, clientid: true }));
        return;
      }
      if (!uid) {
        setSaving(false);
        console.log('[saveOrder] Error: Invalid userid=', uid);
        openNotify('error', 'Error', 'Cannot create order: invalid user ID.');
        return;
      }
    }
    try {
      const method = isCreating ? 'POST' : 'PUT';
      const url = isCreating ? `${API}/api/orders` : `${API}/api/orders/${sel.id}`;
      const body = { ...buildOrderPayload(sel), userFullName: getUserFullName() };
      // On create, always stamp the creating user id
      if (isCreating) {
        body.userid = Number(sel.userid || getCurrentUserId() || 0);
      }
      // Enforce client lock for client users: always use their client id
      if (IS_CLIENT && LOCKED_CLIENT_ID) {
        body.clientid = Number(LOCKED_CLIENT_ID);
      }
      // Avoid stringify on potentially cyclic objects; log keys only
      try { console.log('[saveOrder] Sending keys:', Object.keys(body), 'Method:', method, 'URL:', url); } catch { }
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        let error = 'Failed to save order';
        try {
          const errorData = await res.json();
          error = errorData.error || error;
        } catch { }
        console.log('[saveOrder] Error response:', error, 'Status:', res.status);
        throw new Error(error);
      }
      const data = await res.json();
      console.log('[saveOrder] Success:', JSON.stringify(data, null, 2));
      await fetchOrders(meta.offset, limit);
      setSelected(null);
      setCreating(false);
      setEditing(false);
      navigate('/orders');
    } catch (e) {
      console.log('[saveOrder] Error:', e.message);
      openNotify('error', 'Error', `Failed to save order: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

	  useEffect(() => { localStorage.setItem('ordersQ', q); }, [q]);
	  useEffect(() => { localStorage.setItem('ordersFromDate', fromDate); }, [fromDate]);
	  useEffect(() => { localStorage.setItem('ordersToDate', toDate); }, [toDate]);
	  useEffect(() => { localStorage.setItem('ordersLiveViewFromDate', liveViewFromDate); }, [liveViewFromDate]);
	  useEffect(() => { localStorage.setItem('ordersLiveViewToDate', liveViewToDate); }, [liveViewToDate]);
	  useEffect(() => { localStorage.setItem('ordersLiveViewDatePreset', liveViewDatePreset || ''); }, [liveViewDatePreset]);
	  useEffect(() => { localStorage.setItem('ordersShippedFromDate', shippedFromDate); }, [shippedFromDate]);
	  useEffect(() => { localStorage.setItem('ordersShippedToDate', shippedToDate); }, [shippedToDate]);
	  useEffect(() => {
	    localStorage.setItem('ordersShowDeleted', JSON.stringify(showDeleted));
	  }, [showDeleted]);
  useEffect(() => {
    localStorage.setItem('ordersSort', JSON.stringify({ sortField, sortOrder }));
  }, [sortField, sortOrder]);

  // When switching client, clear selection and bottom panes to avoid stale context
  useEffect(() => {
    setSelected(null);
    setSelectedOrderId(null);
    setBottomPaneMode(null);
    setOrderItems([]);
    setHistory([]);
    setAttachments([]);
    setSelectedOrderIds([]);
  }, [clientIdFilter]);

  // Track last search signature to avoid duplicate fetches (esp. in StrictMode)
  const lastQueryKeyRef = useRef('');
  //useEffect(() => { fetchOrders(0, limit); }, [searchSignature]); // eslint-disable-line

  // Unified search/sort/filter effect (debounced + deduped)
  useEffect(() => {
    if (liveViewOnly) return;
    const signature = JSON.stringify({
      q: (q || '').trim(),
      client: (clientFilter || '').trim(),
      clientId: clientIdFilter || '',
      sku: (skuFilter || '').trim(),
      creator: (creatorFilter || '').trim(),
      from: fromDate || '',
      to: toDate || '',
      deleted: !!showDeleted,
      dateField,
      status: (statusIds || []).slice().sort(),
      billed: billedFilter || '',
      sortField,
      sortOrder,
      limit,
    });
    const t = setTimeout(() => {
      if (lastQueryKeyRef.current === signature) return;
      lastQueryKeyRef.current = signature;
      setOrders([]);
      setMeta({ total: 0, limit, offset: 0 });
      fetchOrders(0, limit);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, clientFilter, clientIdFilter, skuFilter, creatorFilter, fromDate, toDate, showDeleted, dateField, statusIds, billedFilter, sortField, sortOrder, limit, liveViewOnly]);

  useEffect(() => {
    if (liveViewOnly) return;
    fetchStatusOptions();
    fetchClientOptions();
    // Always load warehouses so clients see options in New Order
    fetchWarehouseOptions();
    fetchCarrierOptions();
    fetchDestinationOptions();
    fetchItemStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveViewOnly]);
  useEffect(() => { try { localStorage.setItem('ordersStatusIds', JSON.stringify(statusIds)); } catch {} }, [statusIds]);
  const assignedDebounceRef = useRef(true);
  useEffect(() => {
    if (liveViewOnly) return;
    if (assignedDebounceRef.current) {
      assignedDebounceRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setOrders([]);
      setMeta({ total: 0, limit, offset: 0 });
      fetchOrders(0, limit);
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedFilter, limit, liveViewOnly]);

  const fetchOrderItems = async (orderId) => {
    if (!orderId) { setOrderItems([]); return; }
    setItemsLoading(true);
    try {
      const data = await fetchJSON(`${API}/api/orders/${orderId}/items`);

      setOrderItems(Array.isArray(data) ? data : []);
      const selectedId = Number(selected?.id || selectedOrderId || 0);
      if (
        editing &&
        selectedId === Number(orderId) &&
        Number(selected?.is_ftz_outbound || 0) === 1
      ) {
        fetchOrderFtzDetail(orderId);
      }
    } catch {
      setOrderItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  const fetchOrderHistory = async (orderId) => {
    if (!orderId) { setHistory([]); return; }
    if (IS_CLIENT) return; // guard (clients should not load history)
    setHistoryLoading(true);
    try {
      const data = await fetchJSON(`${API}/api/orders/${orderId}/history`);
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchOrderAttachments = async (orderId) => {
    if (!orderId) { setAttachments([]); return; }
    setAttachmentsLoading(true);
    try {
      const data = await fetchJSON(`${API}/api/orders/${orderId}/attachments`);
      setAttachments(Array.isArray(data) ? data : []);
    } catch {
      setAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const fetchOrderFtzDetail = async (orderId) => {
    if (!orderId) {
      setFtzDetail(null);
      return;
    }
    setFtzDetailLoading(true);
    try {
      const data = await fetchJSON(`${API}/api/orders/${orderId}/ftz/detail`);
      setFtzDetail(data || null);
    } catch {
      setFtzDetail(null);
    } finally {
      setFtzDetailLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedOrderId) {
      setOrderItems([]);
      setHistory([]);
      setAttachments([]);
      return;
    }
    if (bottomPaneMode === 'items') fetchOrderItems(selectedOrderId);
    if (bottomPaneMode === 'history' && !IS_CLIENT) fetchOrderHistory(selectedOrderId);
    if (bottomPaneMode === 'attachments') fetchOrderAttachments(selectedOrderId);
  }, [selectedOrderId, bottomPaneMode]);

  useEffect(() => {
    const orderId = Number(selected?.id || 0) || Number(selectedOrderId || 0) || 0;
    if (!editing || !orderId || Number(selected?.is_ftz_outbound || 0) !== 1) {
      setFtzDetail(null);
      setFtzDetailLoading(false);
      return;
    }
    fetchOrderFtzDetail(orderId);
  }, [editing, selected?.id, selected?.is_ftz_outbound, selectedOrderId]);

  useEffect(() => {
    if (!selectedOrderId) return;
    const row = orderRowRefs.current.get(selectedOrderId);
    if (!row || typeof row.scrollIntoView !== 'function') return;
    try {
      row.scrollIntoView({ block: bottomPaneMode ? 'center' : 'nearest', behavior: 'smooth' });
    } catch {
      row.scrollIntoView();
    }
  }, [selectedOrderId, bottomPaneMode, orders.length, editing]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Orders ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [meta?.total]);









  // Agent-driven Create Order wiring (robust)
  const pendingCreateRef = useRef(null);

  // Accept payload in any of these shapes: e.detail | e.detail.payload | e.detail.action.payload
  const normalizeAgentPayload = (pAny = {}) => {
    const p1 = pAny?.payload ?? pAny;
    const p = p1?.action?.payload ?? p1;
    const first = (...vals) => vals.find(v => v !== undefined && v !== null && v !== '');
    const warehouseId = first(p?.warehouseid, p?.warehouseId, p?.initialOrder?.warehouseid, p?.initialOrder?.warehouseId);
    const clientId = first(p?.clientid, p?.clientId, p?.initialOrder?.clientid, p?.initialOrder?.clientId);
    const warehouseName = first(p?.warehousename, p?.warehouseName, p?.resolvedNames?.warehouse, p?.initialOrder?.warehouse);
    const clientName = first(p?.clientname, p?.clientName, p?.resolvedNames?.client, p?.initialOrder?.client);
    const orderdate = first(p?.orderdate, p?.initialOrder?.orderdate, fmtYMD(new Date())); // Default to 2025-08-31
    const po = first(p?.po, p?.initialOrder?.po, '');
    const notes = first(p?.notes, p?.initialOrder?.notes, '');
    return { warehouseId, clientId, warehouseName, clientName, orderdate, po, notes };
  };

  // Process the agent payload to create a new order
  const processAgentCreateOrder = async (rawDetail) => {
    if (IS_CLIENT) {
      openNotify('error', 'Error', 'Your role does not allow creating orders.');
      return;
    }
    if (!rawDetail) {
      console.log('[Agent->Orders] Error: No payload received');
      return;
    }
    console.log('[Agent->Orders] Raw payload:', JSON.stringify(rawDetail, null, 2));
    const detail = normalizeAgentPayload(rawDetail);
    const userId = getCurrentUserId() || null;
    const warehouseId = Number(detail.warehouseId) || Number(detail.warehouseid);
    const clientId = Number(detail.clientId) || Number(detail.clientid);
    const orderdate = detail.orderdate;
    const po = detail.po || '';
    const notes = detail.notes || '';
    console.log('[Agent->Orders] Extracted: userId=', userId, 'warehouseId=', warehouseId, 'clientId=', clientId, 'orderdate=', orderdate, 'po=', po, 'notes=', notes);

    if (!userId || !warehouseId || !clientId) {
      console.log('[Agent->Orders] Error: Missing valid IDs (userId=', userId, 'warehouseId=', warehouseId, 'clientId=', clientId, ')');
      openNotify('error', 'Error', 'Cannot create order: invalid user, warehouse, or client ID.');
      return;
    }

    setEditing(false);
    setBottomPaneMode(null);
    setCreating(true);
    const hydrated = {
      ...emptyOrder,
      datecreated: orderdate,
      orderdate,
      statusid: createdStatusId,
      userid: userId,
      warehouseid: warehouseId,
      clientid: clientId,
      po: po,
      notes: notes,
    };
    console.log('[Agent->Orders] Hydrated:', JSON.stringify(hydrated, null, 2));
    setSelected(hydrated);
    // Ensure state updates before saving
    await new Promise(resolve => setTimeout(resolve, 0)); // Force state update
    await saveOrder(hydrated);
  };

  // Define lookupsLoaded to ensure options are ready
  const lookupsLoaded = useMemo(
    () => (warehouseOptions?.length || 0) > 0 && (clientOptions?.length || 0) > 0 && (statusOptions?.length || 0) > 0,
    [warehouseOptions, clientOptions, statusOptions]
  );

  // Listen for agent events (unwrap shapes)
  useEffect(() => {
    const handler = (e) => {
      const d = e?.detail ?? {};
      const unwrapped = d?.payload ?? d?.action?.payload ?? d;
      console.log('[Orders] Received createOrder:', JSON.stringify(unwrapped, null, 2));
      processAgentCreateOrder(unwrapped);
    };
    window.addEventListener('agent:createOrder', handler);
    return () => window.removeEventListener('agent:createOrder', handler);
  }, [lookupsLoaded, warehouseOptions, clientOptions, IS_CLIENT, createdStatusId, setCreating, setEditing, setBottomPaneMode, setSelected]);

  // Replay queued agent request once lookups are ready
  useEffect(() => {
    if (pendingCreateRef.current && lookupsLoaded) {
      const d = pendingCreateRef.current;
      pendingCreateRef.current = null;
      processAgentCreateOrder(d);
    }
  }, [lookupsLoaded]);







  function pad4(num) {
    if (!num && num !== 0) return '';
    return String(num).padStart(4, '0');
  }
  const statusIdForName = name => {
    const status = itemStatuses.find(s => s.name.toLowerCase() === name.toLowerCase());
    return status ? status.id : null;
  };

  const pickOrderItem = async (item, pick) => {
    if (!selectedOrderId || !item?.id) return;
    try {
      const statusId = pick ? statusIdForName('Picked') : statusIdForName('Requested');
      if (!statusId) throw new Error('Invalid status');
      await fetchJSON(`${API}/api/orders/orderitems/${item.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statusid: statusId, username: getUserFullName() }),
      });
      await fetchOrderItems(selectedOrderId);
      // Refresh header/list so any auto-updated order status is visible
      await fetchOrders(meta.offset, limit);
    } catch (e) {
      openNotify('error', 'Error', `Failed to ${pick ? 'pick' : 'unpick'} item: ${e.message}`);
    }
  };

  const gotoPage = (p) => {
    const lp = Math.max(1, Math.ceil((meta.total || 0) / (limit || DEFAULT_LIMIT)));
    const clamped = Math.min(Math.max(1, p), lp);
    const nextOffset = Math.max(0, (clamped - 1) * limit);
    fetchOrders(nextOffset, limit);
    setSelectedOrderId(null);
    setBottomPaneMode(null);
  };
  const handleSort = (field, e) => {
    if (ordResizedRecently.current) return;
    if (e?.target && typeof e.target.closest === 'function' && e.target.closest('.col-resizer')) return;
    if (sortField === field) setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    else { setSortField(field); setSortOrder('ASC'); }
    setSelectedOrderId(null);
    setBottomPaneMode(null);
  };

  const onEdit = async (o) => {
    setEditing(true);
    setBottomPaneMode(null);
    setSelectedOrderId(o.id);
    setSelected({ ...emptyOrder, ...o });
    const full = await loadOrderById(o.id);
    if (full) setSelected(full);

    await fetchOrderItems(o.id);
  };

  const onRowClick = (o) => {
    if (o.deleted) return;
    if (editing && !selected?.id) return;

   /* if (selectedOrderId === o.id) {
      setSelectedOrderId(null);
      setSelected(null);
      setBottomPaneMode(null);
      return;
    }*/
    setSelected(o);
    setSelectedOrderId(o.id);
    setBottomPaneMode('items');
  };

  const onShowOrderHistory = (orderId, e) => {
    e.stopPropagation();
    if (IS_CLIENT) return; // clients cannot open history
    if (!selected || selected.id !== orderId) {
      const found = orders.find(x => x.id === orderId);
      if (found) {
        setSelected(found);
        setSelectedOrderId(orderId);
      }
    }
    setBottomPaneMode('history');
  };
  const onShowAttachments = (orderId, e) => {
    e?.stopPropagation?.();
    if (!selected || selected.id !== orderId) {
      const found = orders.find(x => x.id === orderId);
      if (found) {
        setSelected(found);
        setSelectedOrderId(orderId);
      }
    }
    setBottomPaneMode('attachments');
  };

  const onNew = () => {
    const base = {
      ...emptyOrder,
      datecreated: todayStr(),
      orderdate: todayStr(),
      statusid: createdStatusId,
      // Ensure new orders default to the current user
      userid: getCurrentUserId() || '',
    };
    // If only one warehouse is available, preselect it
    const onlyWarehouseId = (Array.isArray(warehouseOptions) && warehouseOptions.length === 1)
      ? Number(warehouseOptions[0]?.id) || ''
      : '';
    if (IS_CLIENT) {
      const cid = clientOptions?.[0]?.id ? Number(clientOptions[0].id) : (Number(LOCKED_CLIENT_ID) || 0);
      setSelected({ ...base, clientid: cid || '', warehouseid: onlyWarehouseId || '' });
    } else {
      setSelected({ ...base, warehouseid: onlyWarehouseId || '' });
    }
    setSelectedOrderId(null);
    setBottomPaneMode(null);
    setEditing(false);
    setCreating(true);
    setFieldErrors({ clientid: false, warehouseid: false, carrierid: false, orderdate: false });
  };

  // REPLACE the existing deleteOrder and restoreOrder with the versions below

  const deleteOrder = async (orderId) => {
    console.log("deleteOrder clicked - " + orderId);

    if (orderId == null) return;


    try {
      const uid = getCurrentUserId() || '';
      const uname = getUserFullName() || '';
      // Some servers ignore DELETE bodies — pass identifiers in query string too.
      const url = `${API}/api/orders/${orderId}?userid=${encodeURIComponent(uid)}&username=${encodeURIComponent(uname)}`;

      const res = await fetch(url, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userFullName: uname, userid: uid }),
      });

      // Accept 200/204 as success; try to parse json if available
      if (res.status === 204) {
        // no content – treat as success
      } else if (!res.ok) {
        let msg = 'Delete failed';
        try { const j = await res.json(); msg = j?.error || msg; } catch { }
        throw new Error(msg);
      }

      await fetchOrders(meta.offset, limit);
      if (selectedOrderId === orderId) {
        setSelected(null);
        setSelectedOrderId(null);
        setBottomPaneMode(null);
      }
    } catch (e) {
      openNotify('error', 'Error', 'Delete failed: ' + e.message);
    }
  };

  const restoreOrder = async (orderId) => {
    if (!orderId) return;
    try {
      const uid = getCurrentUserId() || '';
      const uname = getUserFullName() || '';
      const url = `${API}/api/orders/${orderId}/restore?userid=${encodeURIComponent(uid)}&username=${encodeURIComponent(uname)}`;

      const res = await fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) {
        let msg = 'Restore failed';
        try { const j = await res.json(); msg = j?.error || msg; } catch { }
        throw new Error(msg);
      }
      await fetchOrders(meta.offset, limit);
    } catch (e) {
      openNotify('error', 'Error', 'Restore failed: ' + e.message);
    }
  };

  const deleteOrderItem = async (item) => {
    //if (!window.confirm('Delete this item? Boxes and pieces will be returned to inventory.')) return;
    try {
      await fetchJSON(`${API}/api/orders/orderitems/${item.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: getUserFullName() }),
      });
      fetchOrderItems(selectedOrderId);
    } catch (e) {
      openNotify('error', 'Error', 'Failed to delete item: ' + e.message);
    }
  };

  const deleteOrderAttachment = async (attId) => {
    if (!selectedOrderId) return;
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await fetchJSON(`${API}/api/orders/${selectedOrderId}/attachments/${attId}`, { method: 'DELETE' });
      await fetchOrderAttachments(selectedOrderId);
      await fetchOrders(meta.offset, limit);
    } catch (e) { 
      openNotify('error', 'Error', 'Delete failed: ' + e.message);
    }
  };

  // Create an invoice for an order from its ordercharges lines
  const createInvoiceForOrder = async (orderId) => {
    if (IS_CLIENT) return; // guard
    if (!orderId) return;

    try {
      const res = await fetch(`${API}/api/invoices/from-order/${orderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userFullName: getUserFullName() }),
      });
      const j = await res.json();
      if (!res.ok || !j?.id) {
        openNotify('error', 'Error',j?.error || 'Failed to create invoice.');
        return;
      }
      // Navigate to the invoice editor (adjust if your route differs)
      //window.location.href = `/invoice/${j.id}/edit`;
      openNotify('success', 'Success', `Invoice ${j.id} created.`);

      // Optimistically update the row so the "Create Invoice" action disappears
      setOrders(prev => Array.isArray(prev)
        ? prev.map(o => o.id === orderId
          ? { ...o, hasInvoice: 1, invoiceId: j.id, invoiceCount: (Number(o.invoiceCount || 0) + 1) }
          : o)
        : prev
      );
    } catch (e) {
      openNotify('error', 'Error','Failed to create invoice: ' + e.message);
    }
  };

  const handleLimitUpdate = (raw) => {
    let v = parseInt(raw ?? limitInput, 10);
    if (isNaN(v)) v = limit;
    if (v < 1) v = 1;
    if (v > 10000) v = 10000;
    setLimit(v);
    setLimitInput(String(v));
    window.localStorage.setItem('ordersLimit', v);
    fetchOrders(0, v);
  };

  // --- Orders Import helpers (Preview -> Import) ---

  // Upload CSV to preview endpoint -> returns jobId
  function xhrOrdersPreviewUpload({ file }) {
    return new Promise((resolve, reject) => {
      const fd = new FormData();
      fd.append('file', file);
      const xhr = new XMLHttpRequest();
      ordXhrRef.current = xhr;
      console.log("calling preview - " + `${ORD_IMPORT_BASE}/import/preview`);

      xhr.open('POST', `${ORD_IMPORT_BASE}/import/preview`, true);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.max(1, Math.min(60, Math.round((e.loaded / e.total) * 60))); // 0..60%
          setOrdProgressPct(pct);
          setOrdProgressMsg(`Uploading… ${pct}%`);
        } else setOrdProgressMsg('Uploading…');
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.onabort = () => reject(new Error('Upload canceled'));
      xhr.onload = () => {
        try {
          const json = JSON.parse(xhr.responseText || '{}');
          if (xhr.status >= 200 && xhr.status < 300 && json.success && json.jobId) resolve(json.jobId);
          else reject(new Error(json.error || `Preview failed (${xhr.status})`));
        } catch { reject(new Error('Invalid server response')); }
      };
      xhr.send(fd);
    });
  }

  // Poll preview status until done
  async function pollOrdersPreview(jobId, { intervalMs = 300 } = {}) {
    setOrdProgressMsg('Parsing on server…');
    setOrdProgressPct((p) => Math.max(p, 62));
    for (; ;) {
      const resp = await fetch(`${ORD_IMPORT_BASE}/import/preview/status/${jobId}`);
      if (!resp.ok) throw new Error(`Status error (${resp.status})`);
      const json = await resp.json();
      if (!json.success) throw new Error(json.error || 'Status failed');
      if (!json.done) {
        const pct = 62 + Math.floor((json.progress / 100) * 36); // 62..98%
        setOrdProgressPct(Math.max(62, Math.min(pct, 98)));
        setOrdProgressMsg(`Parsing on server… ${json.progress}%`);
        await new Promise(r => setTimeout(r, intervalMs));
      } else {
        if (json.error) throw new Error(json.error);
        setOrdProgressPct(100);
        setOrdProgressMsg('Done');
        return json.result || { data: [], errors: [] };
      }
    }
  }

  // Handle file choose -> Preview
  async function handleOrdersPreviewImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      openNotify('error', 'Error', 'Please select a valid CSV file.');
      return;
    }
    try {
      setOrdSelectedFile(file);
      setOrdImportLoading(true);
      setOrdProgressOpen(true);
      setOrdProgressPct(1);
      setOrdProgressMsg('Uploading…');

      const jobId = await xhrOrdersPreviewUpload({ file });
      const result = await pollOrdersPreview(jobId);

      setOrdPreviewData(result.data || []);
      setOrdPreviewErrors(result.errors || []);
      setOrdPreviewOpen(true);
    } catch (err) {
      if (err.message !== 'Upload canceled') {
         openNotify('error', 'Error', `Preview error: ${err.message}`);
      }

      setOrdPreviewData([]); setOrdPreviewErrors([]); setOrdSelectedFile(null);
    } finally {
      setOrdImportLoading(false);
      setOrdProgressOpen(false);
      setOrdProgressMsg('');
      setOrdProgressPct(0);
      if (orderImportInputRef.current) orderImportInputRef.current.value = '';
    }
  }

  // Confirm Import (from preview) -> real import + polling
  async function handleOrdersConfirmImport() {
    if (!ordSelectedFile) {
      openNotify('error', 'Error', 'No file available for import. Please upload a CSV again.');
      setOrdPreviewOpen(false);
      return;
    }
    setOrdImportLoading(true);
    setOrdProgressOpen(true);
    setOrdProgressPct(1);
    setOrdProgressMsg('Uploading…');

    try {
      // Start import
      const jobId = await new Promise((resolve, reject) => {
        const fd = new FormData();
        fd.append('file', ordSelectedFile);
        const xhr = new XMLHttpRequest();
        ordXhrRef.current = xhr;
        xhr.open('POST', `${ORD_IMPORT_BASE}/import`, true);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.max(1, Math.min(60, Math.round((e.loaded / e.total) * 60)));
            setOrdProgressPct(pct);
            setOrdProgressMsg(`Uploading… ${pct}%`);
          } else setOrdProgressMsg('Uploading…');
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.onabort = () => reject(new Error('Upload canceled'));
        xhr.onload = () => {
          try {
            const json = JSON.parse(xhr.responseText || '{}');
            if (xhr.status >= 200 && xhr.status < 300 && json.success && json.jobId) resolve(json.jobId);
            else reject(new Error(json.error || `Import start failed (${xhr.status})`));
          } catch { reject(new Error('Invalid server response')); }
        };
        xhr.send(fd);
      });

      setOrdJobId(jobId);
      ordPollAbortRef.current = false;
      setOrdProgressMsg('Importing on server…');
      setOrdProgressPct((p) => Math.max(p, 62));

      // Poll import status
      let done = false;
      while (!done && !ordPollAbortRef.current) {
        const resp = await fetch(`${ORD_IMPORT_BASE}/import/status/${jobId}`);
        if (!resp.ok) throw new Error(`Status error (${resp.status})`);
        const json = await resp.json();
        if (!json.success) throw new Error(json.error || 'Status failed');

        if (json.canceled || json.stage === 'canceled') {
          setOrdProgressMsg('Canceled');
          break;
        }
        if (!json.done) {
          const pct = 62 + Math.floor((json.progress / 100) * 36);
          setOrdProgressPct(Math.max(62, Math.min(pct, 98)));
          setOrdProgressMsg(`Importing on server… ${json.progress}%`);
          await new Promise(r => setTimeout(r, 300));
        } else {
          done = true;
          if (json.error) throw new Error(json.error);
          setOrdProgressPct(100);
          setOrdProgressMsg(`Imported ${json.result?.imported ?? 0} row(s)`);
          // refresh Orders list
          fetchOrders(meta.offset, limit);
          setOrdPreviewOpen(false);
        }
      }
    } catch (err) {
      if (err.message !== 'Upload canceled') {
        openNotify('error', 'Error', `Import error: ${err.message}`);
      }
    } finally {
      setOrdJobId(null);
      ordPollAbortRef.current = false;
      setOrdImportLoading(false);
      setOrdProgressOpen(false);
      setOrdProgressMsg('');
      setOrdProgressPct(0);
      setOrdSelectedFile(null);
    }
  }

  // Cancel handlers
  const cancelOrdersPreviewUpload = () => {
    try { ordXhrRef.current?.abort(); } catch { }
    setOrdProgressOpen(false);
    setOrdProgressMsg('');
    setOrdProgressPct(0);
    setOrdImportLoading(false);
    if (orderImportInputRef.current) orderImportInputRef.current.value = '';
  };

  async function cancelOrdersImport() {
    if (ordXhrRef.current && ordXhrRef.current.readyState !== 4) {
      try { ordXhrRef.current.abort(); } catch { }
    }
    ordPollAbortRef.current = true;
    if (ordJobId) {
      try { await fetch(`${ORD_IMPORT_BASE}/import/cancel/${ordJobId}`, { method: 'POST' }); setOrdProgressMsg('Canceling…'); }
      catch { /* ignore */ }
    }
  }

  // --- Orders CSV Template (PO, SKU, Qty are required) ---
  const ordersSampleCsv =
    `Client ID,PO Number,SKU,Qty,Order Date,Warehouse,Notes
  101,PO-1001,ABC-001,5,2025-08-01,Main,"Rush order – ship ASAP"
  101,PO-1001,XYZ-777,2,2025-08-01,Main,""
  205,PO-2002,ABC-001,1,2025-08-02,North,"Leave at dock"
  `;

  // Reusable text download helper (same as Items)
  function downloadTextFile(filename, text) {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.style.display = 'none';
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
  }

  function handleDownloadOrdersTemplate() {
    downloadTextFile('orders-import-template.csv', ordersSampleCsv);
  }

  const lockOrder = async (orderId) => {
    if (!orderId) return;
    try {
      const uid = getCurrentUserId() || '';
      const uname = getUserFullName() || '';
      const res = await fetch(`${API}/api/orders/${orderId}/lock`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid: uid, username: uname })
      });
      const j = await res.json();
      if (!res.ok || !j.success) {
        openNotify('error', 'Error', j.error || 'Failed to lock order');
        return;
      }
      await fetchOrders(meta.offset, limit);
    } catch (e) {
      openNotify('error', 'Error', 'Lock failed: ' + e.message);
    }
  };

  const closeEmailModal = () => {
    if (emailSending) return;
    setShowEmailModal(false);
    setEmailOrderId(null);
    setEmailFields({ to: '', cc: '', bcc: '', subject: '', message: '' });
    setEmailAttachments([]);
    setEmailAttachmentSelection(new Set());
    setEmailAttachmentsLoading(false);
  };

  const sendEmailOrder = async () => {
   const ids = (selectedOrderIds.length && !emailOrderId) ? selectedOrderIds : (emailOrderId ? [emailOrderId] : []);
    if (!ids.length) return;

    const { to, cc, bcc, subject, message } = emailFields || {};
    if (!String(to || '').trim()) { 
      openNotify('error', 'Error', 'Please enter a recipient email');
      return; 
    }

    setEmailSending(true);
    try {
      const isBulk = ids.length > 1;
      const url = isBulk ? `${API}/api/orders/email-bulk` : `${API}/api/orders/${ids[0]}/email`;
    let attachmentFilenames = [];
    if (!isBulk && emailAttachmentSelection instanceof Set && emailAttachmentSelection.size > 0) {
      const selectedRecords = (emailAttachments || []).filter(
        (att) => att?.filename && emailAttachmentSelection.has(att.filename)
      );
      attachmentFilenames = selectedRecords
        .map((att) => att?.filename || '')
        .filter(Boolean);
    }
    const payload = isBulk
      ? { orderIds: ids, to, cc, bcc, subject, message, userFullName: getUserFullName() }
      : {
          to,
          cc,
          bcc,
          subject,
          message,
          userFullName: getUserFullName(),
          attachmentFilenames,
        };

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to email order(s)');

      closeEmailModal();
      openNotify('success', 'Order Sent', (ids.length > 1) ? 'Order emailed successfully.' : 'Order emailed successfully.');
    } catch (e) {
      const many = ids.length > 1;
      openNotify('error', 'Email Failed', many ? ('Failed sending emails - ' + e.message) : ('Failed sending email - ' + e.message));
    } finally {
      setEmailSending(false);
    }
  };

  function NoticeModal({ open, type = 'success', title = '', message = '', onClose }) {
  if (!open) return null;
  const color = type === 'error' ? '#ef4444' : type === 'warn' ? '#f59e0b' : '#21bb50';
  return (
    <div className="assign-modal-overlay" onClick={onClose}>
      <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 999, background: color }} />
            {title || (type === 'error' ? 'Something went wrong' : 'Success')}
          </h3>
          <button className="mini-btn" onClick={onClose} aria-label="Close" style={{ color: '#000' }}>×</button>
        </div>
        <div className="assign-search" style={{ paddingTop: 6 }}>
          <div style={{ color: 'var(--text-color,#eaeef5)' }}>{message}</div>
        </div>
        <div className="assign-actions">
          <button className="btn primary" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
}

function NoticeModal({ open, type = 'success', title = '', message = '', onClose }) {
  if (!open) return null;
  const color = type === 'error' ? '#ef4444' : type === 'warn' ? '#f59e0b' : '#21bb50';
  return (
    <div className="assign-modal-overlay" onClick={onClose}>
      <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 999, background: color }} />
            {title || (type === 'error' ? 'Something went wrong' : 'Success')}
          </h3>
          <button className="mini-btn" onClick={onClose} aria-label="Close" style={{ color: '#000' }}>×</button>
        </div>
        <div className="assign-search" style={{ paddingTop: 6 }}>
          <div style={{ color: 'var(--text-color,#eaeef5)' }}>{message}</div>
        </div>
        <div className="assign-actions">
          <button className="btn primary" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
}

/** Lightweight searchable multi-select (status filter) — same behavior as Invoices */
function MultiSelectStatuses({ options = [], value = [], onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // No search filtering; show all options

  const isAll = value.length === 0;
  const toggle = (id) => {
    const sid = String(id);
    if (value.includes(sid)) onChange(value.filter(v => v !== sid));
    else onChange([...value, sid]);
  };
  const clear = () => onChange([]);
  const selectAll = () => onChange(options.map(o => String(o.id)));

  const selectedNames = options.filter(o => value.includes(String(o.id))).map(o => o.name);
  const summary = isAll ? 'All statuses'
    : selectedNames.length <= 2 ? selectedNames.join(', ')
    : `${selectedNames.slice(0,2).join(', ')} +${selectedNames.length - 2}`;

  return (
    <div className="ms-container" ref={ref}>
      <button type="button" className={`ms-control ${open ? 'open' : ''}`} onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox" aria-expanded={open} title="Filter by status">
        <span className={`ms-summary ${isAll ? 'muted' : ''}`}>{summary}</span>
        {!isAll && (
          <span className="ms-clear" onClick={(e) => { e.stopPropagation(); clear(); }} title="Clear" aria-label="Clear">×</span>
        )}
        <span className="ms-caret">▾</span>
      </button>

      {open && (
        <div className="ms-popover">
          <div className="ms-tools">
            <div className="ms-actions">
              <button type="button" className="btn" onClick={selectAll}>Select All</button>
              <button type="button" className="btn" onClick={clear}>Clear</button>
            </div>
          </div>
          <ul className="ms-list" role="listbox" aria-multiselectable="true">
            {options.length ? options.map(o => {
              const checked = value.includes(String(o.id));
              return (
                <li key={o.id}>
                  <label className="ms-option">
                    <input type="checkbox" checked={checked} onChange={() => toggle(o.id)} />
                    <span>{o.name}</span>
                  </label>
                </li>
              );
            }) : <li className="ms-empty">No statuses</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

  const OrdersTopList = (
    <>
      <div className="orders-toolbar">
        <div className="orders-toolbar-row">
          <div className="search">
            <FaSearch />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  fetchOrders(0, limit);
                }
              }}
              placeholder="Search Order #, PO, BOL, Tracking, Notes"
              title="Tip: type 123 or #123 to jump to that Order #"
            />
            {q && (
              <button className="clear" onClick={() => { setQ(''); fetchOrders(0, limit); }}>
                <FaTimes />
              </button>
            )}

          </div>

          {/* INTERNAL ONLY: client name filter */}
          {!IS_CLIENT && (
            <div className="client-filter" style={{ position: 'relative' }}>
              <FaSearch />
              <input
                value={clientFilter}
                onChange={e => { setClientFilter(e.target.value); setClientIdFilter(''); setClientSuggestOpen(true); }}
                onFocus={() => setClientSuggestOpen(true)}
                onBlur={() => setTimeout(() => setClientSuggestOpen(false), 120)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    fetchOrders(0, limit);
                  }
                }}
                placeholder="Client name"
              />
              {clientFilter && (
                <button
                  className="clear"
                  onClick={() => { setClientFilter(''); setClientIdFilter(''); fetchOrders(0, limit); }}
                  title="Clear client filter"
                >
                  <FaTimes />
                </button>
              )}
              {clientSuggestOpen && clientMatches.length > 0 && (
                <div
                  className="client-suggest"
                  style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
                    background: '#fff', color: '#222', border: '1px solid var(--border-color, #cfd3dc)',
                    borderTop: 'none', boxShadow: '0 6px 16px rgba(0,0,0,0.25)', borderBottomLeftRadius: 8, borderBottomRightRadius: 8,
                    maxHeight: 220, overflowY: 'auto'
                  }}
                >
                  {clientMatches.map(c => (
                    <div
                      key={c.id}
                      className="client-suggest-item"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setClientFilter(c.name);
                        setClientIdFilter(String(c.id || ''));
                        setClientSuggestOpen(false);
                        setOrders([]);
                        setMeta({ total: 0, limit, offset: 0 });
                        fetchOrders(0, limit);
                      }}
                      style={{ padding: '6px 10px', cursor: 'pointer' }}
                      title={c.name}
                    >
                      {c.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="client-filter assigned-filter" style={{ position: 'relative' }}>
            <FaSearch />
            <input
              value={assignedFilter}
              onChange={e => setAssignedFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  fetchOrders(0, limit);
                }
              }}
              list="orders-assigned-options"
              placeholder="Assigned To"
              title="Filter orders by assignee name or type 'unassigned'"
            />
            {assignedFilter && (
              <button
                className="clear"
                onClick={() => { setAssignedFilter(''); fetchOrders(0, limit); }}
                title="Clear Assigned To filter"
              >
                <FaTimes />
              </button>
            )}
            <datalist id="orders-assigned-options">
              {assignedFilterOptions.map((label) => (
                <option key={label} value={label} />
              ))}
              <option value="unassigned" />
            </datalist>
          </div>

          {/* Creator filter */}
          <div className="client-filter sku-filter">
            <FaSearch />
            <input
              value={skuFilter}
              onChange={e => setSkuFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  fetchOrders(0, limit);
                }
              }}
              placeholder="SKU contains"
            />
            {skuFilter && (
              <button
                className="clear"
                onClick={() => { setSkuFilter(''); fetchOrders(0, limit); }}
                title="Clear SKU filter"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {/* Creator filter */}
          <div className="client-filter" style={{ position: 'relative' }}>
            <FaSearch />
            <input
              value={creatorFilter}
              onChange={e => setCreatorFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  fetchOrders(0, limit);
                }
              }}
              placeholder="Creator"
            />
            {creatorFilter && (
              <button
                className="clear"
                onClick={() => { setCreatorFilter(''); fetchOrders(0, limit); }}
                title="Clear creator filter"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {/* Client dropdown for client users */}
          {IS_CLIENT && !singleClientOption && (
            <div className="client-select">
              <label htmlFor="orders-client-select" style={{ marginRight: 6 }}>Client</label>
              <select
                id="orders-client-select"
                className="client-dropdown"
                value={clientIdFilter || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setClientIdFilter(val);
                  // Clear current context to avoid stale panes/selection
                  setSelected(null);
                  setSelectedOrderId(null);
                  setBottomPaneMode(null);
                  setOrders([]);
                  setMeta({ total: 0, limit, offset: 0 });
                  fetchOrders(0, limit);
                }}
              >
                <option value="">— All —</option>
                {clientOptions.map(c => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

        </div>

        {/* Second row: Date / From / To / Billed / Deleted */}
        <div className="orders-toolbar-row">

          <div className="date-filters">
            <div className="date-input preset">
              <span className="date-label">Order Date</span>
              <select
                className="rd-input rd-select"
                value={datePreset}
                onChange={(e) => applyDatePreset(e.target.value)}
                title="Quick date ranges"
              >
                <option value="all">All Time</option>
                <option value="">Custom…</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
              </select>
            </div>

            <div className="date-input from">
              <span className="date-label">From</span>
              <input
                type="date"
                className="rd-input"
                value={fromDate}
                onChange={e => {
                  setFromDate(e.target.value);
                  setDatePreset('');
                  localStorage.setItem('ordersDatePreset', '');
                  setOrders([]); setMeta({ total: 0, limit, offset: 0 });
                  fetchOrders(0, limit);
                }}
              />
            </div>

            <div className="date-input to">
              <span className="date-label">To</span>
              <input
                type="date"
                className="rd-input"
                value={toDate}
                onChange={e => {
                  setToDate(e.target.value);
                  setDatePreset('');
                  localStorage.setItem('ordersDatePreset', '');
                  setOrders([]); setMeta({ total: 0, limit, offset: 0 });
                  fetchOrders(0, limit);
                }}
              />
            </div>
          </div>

          <div className="date-filters">
            <div className="date-input preset shipped shipped-preset-control">
              <label className="date-inline-label" htmlFor="orders-shipped-date-preset">Shipped Date</label>
              <select
                id="orders-shipped-date-preset"
                className="rd-input rd-select"
                value={shippedDatePreset}
                onChange={(e) => applyShippedDatePreset(e.target.value)}
                title="Quick shipped date ranges"
              >
                <option value="all">All Time</option>
                <option value="">Custom…</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
              </select>
            </div>

            <div className="date-input from shipped-from">
              <span className="date-label">Ship From</span>
              <input
                type="date"
                className="rd-input"
                value={shippedFromDate}
                onChange={e => {
                  setShippedFromDate(e.target.value);
                  setShippedDatePreset('');
                  localStorage.setItem('ordersShippedDatePreset', '');
                  setOrders([]); setMeta({ total: 0, limit, offset: 0 });
                  fetchOrders(0, limit);
                }}
              />
            </div>

            <div className="date-input to shipped-to">
              <span className="date-label">Ship To</span>
              <input
                type="date"
                className="rd-input"
                value={shippedToDate}
                onChange={e => {
                  setShippedToDate(e.target.value);
                  setShippedDatePreset('');
                  localStorage.setItem('ordersShippedDatePreset', '');
                  setOrders([]); setMeta({ total: 0, limit, offset: 0 });
                  fetchOrders(0, limit);
                }}
              />
            </div>
          </div>

          <MultiSelectStatuses
            options={statusOptions.map(s => ({ id: Number(s.id ?? s.statusid), name: s.name }))}
            value={statusIds}
            onChange={setStatusIds}
          />

          {/* Billed filter (internal only) */}
          {!IS_CLIENT && (
            <label style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span>Billed</span>
              <select
                className="rd-input rd-select"
                value={billedFilter}
                onChange={(e) => { setBilledFilter(e.target.value); fetchOrders(0, limit); }}
              >
                <option value="">All</option>
                <option value="unbilled">Unbilled only</option>
                <option value="billed">Billed only</option>
              </select>
            </label>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={e => {
                setShowDeleted(e.target.checked);
                setOrders([]);
                setMeta({ total: 0, limit, offset: 0 });
                fetchOrders(0, limit);
              }}
              style={{ marginRight: 4 }}
            />
            Show deleted
          </label>
        </div>

      <div className="toolbar-break" />

      <div className="toolbar-actions">
        <button
          className="btn"
          onClick={() => {
            // clear current list before fetching new results
            setOrders([]);
            setMeta({ total: 0, limit, offset: 0 });
            fetchOrders(0, limit);
          }}
        >
          <FaSearch style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Search
        </button>

        {!IS_CLIENT && (
          <>
            <button
              className="btn primary"
              onClick={openLiveViewPopout}
              title="Open Live View dashboard in a new tab"
            >
              <FaExternalLinkAlt style={{ marginRight: 6, verticalAlign: '-2px' }} />
              Live View
            </button>
            <button
              className={`btn ${liveViewEnabled ? 'primary' : ''}`}
              onClick={() => setLiveViewEnabled(v => !v)}
              title={liveViewEnabled ? 'Hide docked Live View panel' : 'Show docked Live View panel'}
            >
              <FaEye style={{ marginRight: 6, verticalAlign: '-2px' }} />
              {liveViewEnabled ? 'Docked' : 'Dock'}
            </button>
          </>
        )}

        <button className="btn primary" onClick={onNew}><FaPlus /> New</button>

        <button
          className="btn"
          disabled={selectedOrderIds.length === 0}
          title={selectedOrderIds.length ? `Email ${selectedOrderIds.length} selected order(s)` : 'Select orders to email'}
          onClick={() => {
            if (!selectedOrderIds.length) return;
            setEmailOrderId(null); // multi-mode
            setEmailFields({
              to: '', cc: '', bcc: '',
              subject: `Orders #${selectedOrderIds.slice(0,10).join(', #')}${selectedOrderIds.length>10 ? '…' : ''}`,
              message: `Please find attached ${selectedOrderIds.length} order(s).`
            });
            setShowEmailModal(true);
          }}
        >
          <FaEnvelope style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Email Selected
        </button>

        {!IS_CLIENT && (
          <button
            className="btn"
            onClick={() => setCalcOpen(true)}
            title="Open Shipping Rate Calculator"
          >
            <FaCalculator style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Shipping Calculator
          </button>
        )}
        
        <button
          className="btn"
          onClick={() => {
            try {
              const params = new URLSearchParams({ sortField, sortOrder });
              if (q) params.set('q', q);
              if (fromDate) params.set('from', fromDate);
              if (toDate) params.set('to', toDate);
              if (shippedFromDate) params.set('shipped_from', shippedFromDate);
              if (shippedToDate) params.set('shipped_to', shippedToDate);
              if (dateField) params.set('datefield', String(dateField));
              if (!showDeleted) params.set('deleted', 0);
              if (!IS_CLIENT && clientFilter) params.set('clientname', clientFilter);
              if (creatorFilter) params.set('creator', creatorFilter);
              if (skuFilter) params.set('sku', skuFilter);
              if (IS_CLIENT && clientIdFilter) params.set('clientid', String(clientIdFilter));
              if (Array.isArray(statusIds) && statusIds.length > 0) {
                params.set('statusIds', statusIds.join(','));
                if (statusIds.length === 1) params.set('statusId', statusIds[0]);
              }
              if (billedFilter === 'unbilled') params.set('hasinvoice', 'unbilled');
              else if (billedFilter === 'billed') params.set('hasinvoice', 'billed');

              const url = `${API}/api/orders/export?${params.toString()}`;
              window.open(url, '_blank', 'noopener,noreferrer');
            } catch (e) {
              alert(e?.message || 'Failed to start CSV export');
            }
          }}
          title="Export current list to CSV"
        >
          <FaDownload style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Export CSV
        </button>

        <button
          className="btn"
          onClick={handleDownloadOrdersTemplate}
          title="Download Orders CSV Template"
        >
          <FaDownload style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Template
        </button>




        <button
          className="btn"
          onClick={() => orderImportInputRef.current?.click()}
          title="Import Orders from CSV"
          disabled={ordImportLoading || ordProgressOpen}
        >
          {(ordImportLoading || ordProgressOpen) && <span className="spinner" />}
          <FaUpload style={{ verticalAlign: '-2px', marginRight: 6 }} />
          {ordProgressOpen ? 'Working…' : 'Import'}
        </button>
        <input
          ref={orderImportInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleOrdersPreviewImport}
        />
        </div>
      </div>

      <div className="orders-list-tablecol">
        {loadingList ? (
          <div className="loading">Loading…</div>
        ) : (
          <>
            <table className="orders-table" ref={ordTableRef}>
              <colgroup>
                {ORDER_COLS_VISIBLE.map(k => (
                  <col key={k} style={{ width: ordColWidths[k] ? `${ordColWidths[k]}px` : undefined }} />
                ))}
              </colgroup>
              <thead>
                <tr ref={ordHeaderRowRef}>
                  {/* Selection master checkbox (hidden for client users) */}
                  {!IS_CLIENT && (
                    <th>
                      <input
                        type="checkbox"
                        checked={orders.length > 0 && selectedOrderIds.length === orders.length}
                        onChange={toggleSelectAll}
                      />
                      <span className="col-resizer" onMouseDown={(e)=>startOrdResize('select', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('select'); }} />
                    </th>
                  )}
                  <th>
                    Actions
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('actions', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('actions'); }} />
                  </th>
                  <th onClick={(e) => handleSort('id', e)} className={sortField === 'id' ? 'active-sort' : ''}>
                    OR # {sortField === 'id' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('id', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('id'); }} />
                  </th>
                  <th
                    onClick={(e) => handleSort('clientname', e)}
                    className={`col-client ${sortField === 'clientname' ? 'active-sort' : ''}`}
                    style={clientColStyle}
                  >
                    Client {sortField === 'clientname' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('clientname', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('clientname'); }} />
                  </th>
                  <th onClick={(e) => handleSort('statusid', e)} className={sortField === 'statusid' ? 'active-sort' : ''}>
                    Status {sortField === 'statusid' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('statusid', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('statusid'); }} />
                  </th>
                  <th onClick={(e) => handleSort('datecreated', e)} className={sortField === 'datecreated' ? 'active-sort' : ''}>
                    Created {sortField === 'datecreated' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('datecreated', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('datecreated'); }} />
                  </th>
                  <th onClick={(e) => handleSort('orderdate', e)} className={sortField === 'orderdate' ? 'active-sort' : ''}>
                    Order Date {sortField === 'orderdate' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('orderdate', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('orderdate'); }} />
                  </th>
                  <th onClick={(e) => handleSort('shippeddate', e)} className={sortField === 'shippeddate' ? 'active-sort' : ''}>
                    Shipped {sortField === 'shippeddate' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('shippeddate', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('shippeddate'); }} />
                  </th>
                  <th onClick={(e) => handleSort('createdby', e)} className={`col-creator ${sortField === 'createdby' ? 'active-sort' : ''}`}>
                    Creator {sortField === 'createdby' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('createdby', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('createdby'); }} />
                  </th>
                  {/* NEW: Assigned To column */}
                  <th>
                    Assigned To
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('assignedto', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('assignedto'); }} />
                  </th>
                  <th onClick={(e) => handleSort('warehouseid', e)} className={sortField === 'warehouseid' ? 'active-sort' : ''}>
                    Warehouse {sortField === 'warehouseid' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('warehouse', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('warehouse'); }} />
                  </th>
                  <th onClick={(e) => handleSort('po', e)} className={sortField === 'po' ? 'active-sort' : ''}>
                    PO # {sortField === 'po' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('po', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('po'); }} />
                  </th>
                  <th onClick={(e) => handleSort('bol', e)}>BOL…
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('bol', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('bol'); }} />
                  </th>
                  <th onClick={(e) => handleSort('trackingnumber', e)} className={sortField === 'trackingnumber' ? 'active-sort' : ''}>
                    Tracking # {sortField === 'trackingnumber' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('trackingnumber', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('trackingnumber'); }} />
                  </th>
                  <th data-colkey="notes">
                    Notes
                    <span className="col-resizer" onMouseDown={(e)=>startOrdResize('notes', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderCol('notes'); }} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.length ? (
                  orders.map((o) => {
                    const hasInventory = Number(o.inventoryCount ?? o.inventorycount ?? 0) > 0;
                    return (
                      <tr
                        key={o.id}
                        className={`${selectedOrderId === o.id ? 'selected active' : ''} ${o.type === 1 ? 'special-order' : ''}`}
                        style={{ cursor: o.deleted ? 'default' : 'pointer' }}
                        onClick={() => onRowClick(o)}
                        ref={(el) => {
                          if (el) orderRowRefs.current.set(o.id, el);
                          else orderRowRefs.current.delete(o.id);
                        }}
                      >
                      {/* Row selection checkbox (hidden for client users) */}
                      {!IS_CLIENT && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedOrderIds.includes(o.id)}
                            onClick={e => e.stopPropagation()}
                            onChange={e => toggleSelectOrder(o.id)}
                          />
                        </td>
                      )}
                      <td className="td-actions" style={{ whiteSpace: 'nowrap' }}>
                        <div className='actions'>
                          {!o.deleted ? (
                            <>
                              {(!IS_CLIENT || Number(o.locked) !== 1) && String(o.statusname || '').trim().toLowerCase() !== 'shipped' && (
                                <button
                                  className="mini-btn"
                                  title="Request Inventory for Order"
                                  tabIndex={-1}
                                  onClick={(e) => openPicker(e, o)}
                                >
                                  <FaBoxOpen size={16} />
                                </button>
                              )}

                              <button className="mini-btn" title="Edit" onClick={e => { e.stopPropagation(); onEdit(o); }}>
                                <FaEdit size={16} />
                              </button>

                              {!IS_CLIENT && Number(o.createdby_isclient || 0) === 1 && (
                                <button
                                  className="mini-btn lock"
                                  title={Number(o.locked) === 1 ? "Unlock Order" : "Lock Order"}
                                  aria-label={Number(o.locked) === 1 ? "Unlock Order" : "Lock Order"}
                                  disabled={false}
                                  onClick={e => {
                                    e.stopPropagation();
                                    lockOrder(o.id);
                                  }}
                                >
                                  {Number(o.locked) === 1 ? <FaLock size={16}/> : <FaLockOpen size={16}/>}
                                </button>
                              )}

                              <button
                                className="mini-btn"
                                title="Order Attachments"
                                tabIndex={-1}
                                onClick={(e) => onShowAttachments(o.id, e)}
                                style={{ color: o.attachments_count > 0 ? '#21bb50' : undefined }}
                              >
                                <FaPaperclip size={16} />
                              </button>

                              <button className="mini-btn" title="Email Order" tabIndex={-1}
                                onClick={e => {
                                e.stopPropagation();
                                setEmailOrderId(o.id);
                                setEmailFields({
                                  to: '', cc: '', bcc: '',
                                  subject: `Order #${o.id}`,
                                  message: `Please find attached Order #${o.id}.`,
                                });
                                setShowEmailModal(true);
                              }}>
                                <FaEnvelope size={16} />
                              </button>

                              <button className="mini-btn" title="Print Order" tabIndex={-1}
                                onClick={e => {
                                  e.stopPropagation();
                                  setPrintForOrderId(o.id);
                                  setPrintSortOpen(true);
                                }}>
                                <FaPrint size={16} />
                              </button>

                              <button className="mini-btn"
                                onClick={e => { e.stopPropagation(); window.open(`${API}/api/orders/${o.id}/packing-slip.pdf`, '_blank'); }}
                                title="Print Packing Slip" tabIndex={-1}>
                                <FaClipboardList size={16} />
                              </button>

                              {!IS_CLIENT && (
                                <button className="mini-btn"
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (!selectedOrderId) {
                                      setSelected(o);
                                      setSelectedOrderId(o.id);
                                    }
                                    handleGenerateLineItemBol();
                                  }}
                                  title="Generate Bill of Lading" tabIndex={-1}>
                                  <FaFileInvoice size={16} />
                                </button>
                              )}

                              {!IS_CLIENT && (
                                <button className="mini-btn" title="Assign Order" onClick={(e) => { e.stopPropagation(); assignOrder(o); }}>
                                  <FaUserPlus size={16} />
                                </button>
                              )}

                              {/* INTERNAL ONLY: Order History action */}
                              {!IS_CLIENT && (
                                <button className="mini-btn" title="Order History" tabIndex={-1}
                                  onClick={e => onShowOrderHistory(o.id, e)}>
                                  <FaHistory size={16} />
                                </button>
                              )}

                              {/* INTERNAL ONLY: Create/Open Invoice actions */}
                              {!IS_CLIENT && (
                                (!o.hasInvoice && !o.nonbillable) ? (
                                  <button
                                    className="mini-btn"
                                    title="Create Invoice from Order"
                                    tabIndex={-1}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openConfirm(`Create a new invoice from Order #${o.id}?`, () => createInvoiceForOrder(o.id));
                                    }}
                                    style={{ color: '#21bb50' }}
                                  >
                                    <FaDollarSign size={16} />
                                  </button>
                                ) : (
                                  <button
                                    className="mini-btn"
                                    title="Open invoice"
                                    tabIndex={-1}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (o.invoiceId) window.open(`${API}/api/invoices/${o.invoiceId}/pdf`, '_blank', 'noopener,noreferrer');
                                    }}
                                  >
                                    <FaEye size={16} />
                                  </button>
                                )
                              )}

                              {o.statusname === 'Created' && !hasInventory && (
                                // In the Actions column button, ensure explicit type to avoid any default form behavior
                                <button
                                  type="button"
                                  className="mini-btn danger"
                                  title="Delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openConfirm(`Delete order #${o.id}?`, () => deleteOrder(o.id));
                                  }}
                                >
                                  <FaTrash size={16} />
                                </button>
                              )}
                            </>
                          ) : (
                            <button className="mini-btn" title="Restore" style={{ color: 'green' }} onClick={e => { e.stopPropagation(); restoreOrder(o.id); }}>
                              <FaUndo size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                      <td>{o.id}</td>
                      <td className="col-client" style={clientColStyle}>{o.clientname || o.clientid}</td>
                      <td>{o.deleted ? <span style={{ color: 'red', fontWeight: 600 }}>Deleted</span> : (o.statusname || '')}</td>
                      <td>{o.datecreated ? new Date(o.datecreated).toLocaleDateString() : ''}</td>
                      <td>{o.orderdate ? new Date(o.orderdate).toLocaleDateString() : ''}</td>
                      <td>{o.shippeddate ? new Date(o.shippeddate).toLocaleDateString() : ''}</td>
                      <td className="col-creator">{o.userfullname}</td>
                      <td>{o.assignedto_name || o.assignedto || ''}</td>
                      <td>{o.warehousename || o.warehouseid}</td>
                      <td>{o.po}</td>
                      <td>{o.bol}</td>
                      <td>{o.trackingnumber}</td>
                      <td data-colkey="notes">{o.notes}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={ORDER_COLS_VISIBLE.length}>No orders</td></tr>
                )}
              </tbody>
            </table>

            <div className="pagination">
              <button className="btn" disabled={page <= 1} onClick={() => gotoPage(1)}>&laquo;</button>
              <button className="btn" disabled={page <= 1} onClick={() => gotoPage(page - 1)}>Prev</button>
              <span>Page {page} of {lastPage}</span>
              <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(page + 1)}>Next</button>
              <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(lastPage)}>&raquo;</button>
              <label style={{ marginLeft: 12, fontSize: 13 }}>
                Rows per page:&nbsp;
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={limitInput}
                  style={{ width: 60, fontSize: 13, padding: "2px 4px" }}
                  onKeyDown={(e) => {
                    setIsTypingLimit(true);
                    if (e.key === 'Enter') {
                      setIsTypingLimit(false);
                      handleLimitUpdate(e.currentTarget.value);
                    }
                  }}
                  onChange={e => {
                    setLimitInput(e.target.value);
                    if (!isTypingLimit) handleLimitUpdate(e.target.value);
                  }}
                  onBlur={e => {
                    setIsTypingLimit(false);
                    handleLimitUpdate(e.target.value);
                  }}
                />
              </label>
            </div>
          </>
        )}
      </div>
      {showEmailModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>
              {selectedOrderIds.length > 0 && !emailOrderId
                ? `Email ${selectedOrderIds.length} Order${selectedOrderIds.length===1?'':'s'}`
                : `Email Order #${emailOrderId}`}
            </h2>
            <form
              className="payment-form"
              autoComplete="on"
              onSubmit={(e) => { e.preventDefault(); if (!emailSending) sendEmailOrder(); }}
            >
              <div className="form-grid">
                <label>
                  <span>To</span>
                  <input
                    type="email"
                    placeholder="customer@example.com"
                    name="order_email_to"
                    autoComplete="email"
                    value={emailFields.to}
                    onChange={e => setEmailFields({ ...emailFields, to: e.target.value })}
                  />
                </label>
                <label>
                  <span>CC</span>
                  <input
                    type="text"
                    placeholder="optional"
                    name="order_email_cc"
                    autoComplete="email"
                    value={emailFields.cc}
                    onChange={e => setEmailFields({ ...emailFields, cc: e.target.value })}
                  />
                </label>
                <label>
                  <span>BCC</span>
                  <input
                    type="text"
                    placeholder="optional"
                    name="order_email_bcc"
                    autoComplete="email"
                    value={emailFields.bcc}
                    onChange={e => setEmailFields({ ...emailFields, bcc: e.target.value })}
                  />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                  <span>Subject</span>
                  <input
                    type="text"
                    placeholder={`Order #${emailOrderId}`}
                    value={emailFields.subject}
                    onChange={e => setEmailFields({ ...emailFields, subject: e.target.value })}
                  />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                  <span>Message</span>
                  <textarea
                    rows={6}
                    placeholder={`Please find attached Order #${emailOrderId}.`}
                    value={emailFields.message}
                    onChange={e => setEmailFields({ ...emailFields, message: e.target.value })}
                    style={{ background: 'var(--input-background)', color: 'var(--text-color)', border: '1px solid #444', borderRadius: 6, padding: '.5rem' }}
                  />
                </label>
              </div>
              {emailSingleOrderId && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong>Attachments</strong>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => setEmailAttachmentSelection(new Set((emailAttachments || []).map(a => a?.filename).filter(Boolean)))}
                        disabled={emailAttachmentsLoading || !emailAttachments.length}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => setEmailAttachmentSelection(new Set())}
                        disabled={emailAttachmentsLoading || !emailAttachments.length}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  {emailAttachmentsLoading ? (
                    <div style={{ fontSize: 13, opacity: 0.85 }}>Loading attachments…</div>
                  ) : emailAttachments.length ? (
                    <>
                      <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #444', borderRadius: 6 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ width: 50, textAlign: 'left', padding: '6px 8px' }}>Send</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Description</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Filename</th>
                              <th style={{ width: 180, textAlign: 'left', padding: '6px 8px' }}>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {emailAttachments.map((att, idx) => {
                              const fname = att?.filename || `file-${idx}`;
                              const checked = emailAttachmentSelection.has(fname);
                              const dt = att?.date ? new Date(att.date) : null;
                              const dateText = dt && !isNaN(dt.getTime()) ? dt.toLocaleString() : '';
                              return (
                                <tr key={`${fname}-${idx}`}>
                                  <td style={{ padding: '4px 8px' }}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => setEmailAttachmentSelection(prev => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(fname);
                                        else next.delete(fname);
                                        return next;
                                      })}
                                    />
                                  </td>
                                  <td style={{ padding: '4px 8px' }}>{att?.description || ''}</td>
                                  <td style={{ padding: '4px 8px' }}>
                                    {att.url ? (
                                      <a href={att.url} target="_blank" rel="noopener noreferrer">{fname}</a>
                                    ) : fname}
                                  </td>
                                  <td style={{ padding: '4px 8px' }}>{dateText}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
                        Order PDF is always attached. Select any additional files to include.
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 13, opacity: 0.85 }}>No attachments found for this order.</div>
                  )}
                </div>
              )}
              <div className="modal-actions">
                <button className="btn" onClick={closeEmailModal} disabled={emailSending}>
                  <FaTimes style={{ marginRight: 6 }} />
                  Cancel
                </button>
                <button className="btn primary" type="submit" disabled={emailSending}>
                  {emailSending ? 'Sending…' : (
                    <>
                      <FaPaperPlane style={{ marginRight: 6 }} />
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );

  const renderOrderItemsPanel = () => {
    if (!selectedOrderId) return null;
    const orderIsShipped = !!selected?.shippeddate;

    return (
      <div className="order-items-panel">
        <h3 style={{ marginBottom: 8, color: 'var(--primary-color,#ff6200)' }}>
          Order Line Items (Order #{selectedOrderId})
        </h3>

        {itemsLoading ? (
          <div className="loading">Loading items…</div>
        ) : (
          <div className="table-scroll-wrapper">
            <table className="orders-table" ref={ordItemsTableRef} style={{ background: 'transparent' }}>
              <colgroup>
                {ORDER_ITEM_COLS.map(k => (
                  <col key={k} style={{ width: ordItemsColWidths[k] ? `${ordItemsColWidths[k]}px` : undefined }} />
                ))}
              </colgroup>
              <thead>
                <tr ref={ordItemsHeaderRowRef}>
                  <th style={{ width: 28 }}>
                    {!IS_CLIENT && (
                      <input
                        type="checkbox"
                        checked={orderItems.length > 0 && selectedItemIds.size === orderItems.length}
                        onChange={e => toggleAllItems(e.target.checked)}
                        aria-label="Select all items"
                      />
                    )}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('select', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('select'); }} />
                  </th>
                  <th
                    onClick={(e)=>handleOiSort(ORDER_ITEM_ACTION_SORT_FIELD, e)}
                    className={oiSortField===ORDER_ITEM_ACTION_SORT_FIELD ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Action {renderOiSortIcon(ORDER_ITEM_ACTION_SORT_FIELD)}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('action', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('action'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('statusname', e)} className={oiSortField==='statusname' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Status {renderOiSortIcon('statusname')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('status', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('status'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('requestdate', e)} className={oiSortField==='requestdate' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Request Date {renderOiSortIcon('requestdate')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('requestdate', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('requestdate'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('receiptid', e)} className={oiSortField==='receiptid' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Receipt # {renderOiSortIcon('receiptid')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('receiptid', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('receiptid'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('palletnumber', e)} className={oiSortField==='palletnumber' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Pallet # {renderOiSortIcon('palletnumber')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('palletnumber', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('palletnumber'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('palletstorageunit', e)} className={oiSortField==='palletstorageunit' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Pallet Storage Unit {renderOiSortIcon('palletstorageunit')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('palletstorageunit', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('palletstorageunit'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('areaname', e)} className={oiSortField==='areaname' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Area {renderOiSortIcon('areaname')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('area', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('area'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('location', e)} className={oiSortField==='location' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Location {renderOiSortIcon('location')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('location', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('location'); }} />
                  </th>

                  {/* NEW: Image column (matches EOrderItems behavior) */}
                  <th>Image
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('image', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('image'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('sku', e)} className={oiSortField==='sku' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>SKU {renderOiSortIcon('sku')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('sku', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('sku'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('itemdescription', e)} className={oiSortField==='itemdescription' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Item Description {renderOiSortIcon('itemdescription')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('itemdescription', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('itemdescription'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('lotnum', e)} className={oiSortField==='lotnum' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>LOT {renderOiSortIcon('lotnum')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('lotnum', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('lotnum'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('partnumber', e)} className={oiSortField==='partnumber' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Part Number {renderOiSortIcon('partnumber')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('partnumber', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('partnumber'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('boxes_units', e)} className={oiSortField==='boxes_units' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Boxes/Units {renderOiSortIcon('boxes_units')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('boxes_units', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('boxes_units'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('single_pieces', e)} className={oiSortField==='single_pieces' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Single Pieces {renderOiSortIcon('single_pieces')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('single_pieces', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('single_pieces'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('totalpiecesrequested', e)} className={oiSortField==='totalpiecesrequested' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Total Pieces Requested {renderOiSortIcon('totalpiecesrequested')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('totalpiecesrequested', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('totalpiecesrequested'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('unitspicked', e)} className={oiSortField==='unitspicked' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Units Picked {renderOiSortIcon('unitspicked')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('unitspicked', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('unitspicked'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('piecespicked', e)} className={oiSortField==='piecespicked' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Pieces Picked {renderOiSortIcon('piecespicked')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('piecespicked', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('piecespicked'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('bol', e)} className={oiSortField==='bol' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>BOL {renderOiSortIcon('bol')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('bol', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('bol'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('trackingnumber', e)} className={oiSortField==='trackingnumber' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Tracking # {renderOiSortIcon('trackingnumber')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('trackingnumber', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('trackingnumber'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('shipdate', e)} className={oiSortField==='shipdate' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Shipped Date {renderOiSortIcon('shipdate')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('shipdate', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.prevent.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('shipdate'); }} />
                  </th>
                  <th onClick={(e)=>handleOiSort('shippingpalletnumber', e)} className={oiSortField==='shippingpalletnumber' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>Shipping Pallet # {renderOiSortIcon('shippingpalletnumber')}
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('shippingpallet', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.prevent.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('shippingpallet'); }} />
                  </th>
                  <th style={{ userSelect:'none' }}>Carrier
                    <span className="col-resizer" onMouseDown={(e)=>startOrdItemsResize('shippingpalletcarrier', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitOrderItemCol('shippingpalletcarrier'); }} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedOrderItems.length ? sortedOrderItems.map(item => {
                  const isPicked = (item.statusname || '').toLowerCase() === 'picked';

                  // Resolve a thumbnail source from multiple possible fields (url/base64/buffer)
                  const thumbSrc = resolveItemThumb(item, blobUrlBag);

                  return (
                    <tr key={item.id}>
                      <td>
                        {!IS_CLIENT && (
                          <input
                            type="checkbox"
                            checked={selectedItemIds.has(item.id)}
                            onClick={e => e.stopPropagation()}
                            onChange={() => toggleItemSelected(item.id)}
                            aria-label={`Select item ${item.id}`}
                          />
                        )}
                      </td>
                      {/* Action buttons: only while status is Requested or Picked and order not shipped */}
                      <td className="td-actions" style={{ whiteSpace: 'nowrap' }}>
                        <div className="actions">
                          {(() => {
                            if (orderIsShipped) return null;
                            const statusName = String(item.statusname || '').toLowerCase();
                            const isRequested = statusName === 'requested';
                            const isPicked = statusName === 'picked';
                            if (isRequested) {
                              return (
                                <>
                                  <button
                                    className="mini-btn"
                                    title="Pick item"
                                    onClick={e => { e.stopPropagation(); pickOrderItem(item, true); }}
                                    style={{ color: '#21bb50' }}
                                  >
                                    <FaHandPointer size={16} />
                                  </button>
                                  <button
                                    className="mini-btn danger"
                                    title="Delete item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openConfirm('Delete this item? Boxes and pieces will be returned to inventory.', () => deleteOrderItem(item));
                                    }}
                                  >
                                    <FaTrash size={16} />
                                  </button>
                                </>
                              );
                            }
                            if (isPicked) {
                              return (
                                <button
                                  className="mini-btn"
                                  title="Unpick item"
                                  onClick={e => { e.stopPropagation(); pickOrderItem(item, false); }}
                                  style={{ color: '#ffb400' }}
                                >
                                  <FaCheckCircle size={16} />
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </td>

                      <td>{item.statusname}</td>
                      <td>{item.requestdate ? new Date(item.requestdate).toLocaleString() : ''}</td>
                      <td>
                        {item.receiptid ? (
                          <a
                            href={`/receipts`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              // Set default search for Receipts list to this WR# before opening
                              try { localStorage.setItem('receiptsQ', `wr${item.receiptid}`); } catch {}
                              e.stopPropagation();
                            }}
                          >
                            WR{item.receiptid}
                          </a>
                        ) : ''}
                      </td>
                      <td>{pad4(item.palletnumber)}</td>
                      <td>{item.palletstorageunit}</td>
                      <td>{item.areaname}</td>
                      <td className={"nowrap"}>{item.location}</td>

                      {/* NEW: image cell with hover preview */}
                      <td>
                        {thumbSrc ? (
                          <img
                            src={thumbSrc}
                            alt={item.itemdescription || item.sku || 'thumbnail'}
                            className="order-item-thumb"
                            onMouseEnter={(e) => showPreview(item.itemimageurl || item.image1 || item.imageUrl, e)}
                            onMouseMove={movePreview}
                            onMouseLeave={hidePreview}
                          />
                        ) : (
                          <span style={{ opacity: 0.6 }}>—</span>
                        )}
                      </td>

                      <td>{item.sku}</td>
                      <td>{item.itemdescription}</td>
                      <td>{item.lotnum}</td>
                      <td>{item.partnumber}</td>
                      <td
                        className={(() => {
                          const remainingPieces = Number(item?.piecesonhand);
                          if (Number.isFinite(remainingPieces)) return remainingPieces <= 0 ? 'oi-all-inv' : '';
                          const remainingUnits = Number(item?.qtyunitsonhand);
                          if (Number.isFinite(remainingUnits)) return remainingUnits <= 0 ? 'oi-all-inv' : '';
                          return '';
                        })() || undefined}
                        title={(() => {
                          const remainingPieces = Number(item?.piecesonhand);
                          if (Number.isFinite(remainingPieces) && remainingPieces <= 0) return 'All inventory requested for this line item.';
                          const remainingUnits = Number(item?.qtyunitsonhand);
                          if (Number.isFinite(remainingUnits) && remainingUnits <= 0) return 'All inventory requested for this line item.';
                          return '';
                        })() || undefined}
                      >
                        {Number(item.qtyperunit ?? item.original_qtyperunit ?? 0) > 0
                          ? `${item.boxes_units} (${Number(item.qtyperunit ?? item.original_qtyperunit ?? 0).toLocaleString()} per unit)`
                          : item.boxes_units}
                      </td>
                      <td>{item.single_pieces}</td>
                      <td>{item.totalpiecesrequested}</td>
                      <td>{item.unitspicked}</td>
                      <td>{item.piecespicked}</td>
                      <td>{item.bol}</td>
                      <td>{item.trackingnumber || ''}</td>
                      <td>{item.shipdate ? new Date(item.shipdate).toLocaleDateString() : ''}</td>
                      <td>{item.shippingpalletnumber || '—'}</td>
                      <td>{item.shippingpalletcarriername || (item.shippingpalletcarrierid ? carrierLookup.get(String(item.shippingpalletcarrierid)) || `#${item.shippingpalletcarrierid}` : '—')}</td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={ORDER_ITEM_COLS.length}>No items</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>{sortedOrderItems.length ? `Totals:(${Number(sortedOrderItems.length).toLocaleString()} found)` : ''}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ fontWeight: 700 }}>{Number(orderItemsTotals.boxes_units || 0).toLocaleString()}</td>
                  <td style={{ fontWeight: 700 }}>{Number(orderItemsTotals.single_pieces || 0).toLocaleString()}</td>
                  <td style={{ fontWeight: 700 }}>{Number(orderItemsTotals.totalpiecesrequested || 0).toLocaleString()}</td>
                  <td style={{ fontWeight: 700 }}>{Number(orderItemsTotals.unitspicked || 0).toLocaleString()}</td>
                  <td style={{ fontWeight: 700 }}>{Number(orderItemsTotals.piecespicked || 0).toLocaleString()}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>

            {/* Hover Preview box */}
            {preview.visible && preview.src && (
              <div className="order-item-preview" ref={previewRef} style={{ left: preview.x, top: preview.y }}>
                <img src={preview.src} alt="" onLoad={onPreviewImgLoad} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };


  const renderOrderHistoryPanel = () => {
    if (!selectedOrderId || IS_CLIENT) return null; // hide from clients
    return (
      <div className="order-items-panel">
        <h3 style={{ marginTop: 0, marginRight: 0, marginLeft: 0, marginBottom: 8, color: 'var(--primary-color,#ff6200)' }}>
          Order History (Order #{selectedOrderId})
        </h3>
        {historyLoading ? (
          <div className="loading">Loading history…</div>
        ) : (
          <div className="table-scroll-wrapper">
            <table className="orders-table" style={{ width: '100%', background: 'transparent' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Log</th>
                </tr>
              </thead>
              <tbody>
                {history.length ? history.map(row => (
                  <tr key={row.id}>
                    <td>{row.date ? new Date(row.date).toLocaleString() : ''}</td>
                    <td>{row.username}</td>
                    <td style={{ whiteSpace: 'pre-line' }}>{row.log}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={3} style={{ color: '#aaa' }}>No history entries found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderOrderAttachmentsPanel = () => {
    if (!selectedOrderId) return null;
    const orderId = selectedOrderId;
    return (
      <div className="order-attachments-panel">
        <h3 style={{ marginTop: 0, marginRight: 0, marginLeft: 0, marginBottom: 8, color: 'var(--text-color,#ff6200)' }}>
          Order Attachments (Order #{orderId})
        </h3>

        <div className="attachments-toolbar">
          <div className={`dropzone ${isDragging ? 'dragging' : ''}`} onDragEnter={e => { e.preventDefault(); setIsDragging(true); }}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }} onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
            onDrop={e => { e.preventDefault(); setIsDragging(false); const files = Array.from(e.dataTransfer?.files || []); if (files.length) setPendingFiles(files); }}
            title="Drop files here to upload">
            Drop files here
          </div>
          <div className="attach-actions">
            <input
              type="text"
              className="attach-desc"
              placeholder="Description (optional)"
              value={attachDesc}
              onChange={e => setAttachDesc(e.target.value)}
            />
            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={e => {
                const files = e.target.files;
                if (files?.length) setPendingFiles(Array.from(files));
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
            <button className="btn" onClick={() => fileInputRef.current?.click()}>
              <FaUpload style={{ verticalAlign: '-2px' }} /> Browse…
            </button>
            <button className="btn primary" disabled={!pendingFiles.length} onClick={() => uploadFiles(pendingFiles)}
              title={pendingFiles.length ? `${pendingFiles.length} file(s) ready` : 'No files selected'}>
              <FaUpload style={{ verticalAlign: '-2px' }} /> Upload
            </button>
            <button className="btn" onClick={() => fetchOrderAttachments(orderId)} title="Refresh">
              <FaSync style={{ verticalAlign: '-2px' }} />
            </button>
          </div>
        </div>

        {!!pendingFiles.length && (
          <div className="pending-files">
            {pendingFiles.map((f, i) => (
              <span key={f.name + i} className="file-chip">
                {f.name}
                <button type="button" className="chip-x" title="Remove" aria-label={`Remove ${f.name}`} onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="table-scroll-wrapper">
          <table className="orders-table attachments-table" style={{ width: '100%', background: 'transparent' }}>
            <thead>
              <tr>
                <th>Actions</th>
                <th>Filename</th>
                <th>Date</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {attachmentsLoading ? (
                <tr><td colSpan={4}>Loading…</td></tr>
              ) : attachments.length ? (
                attachments.map(a => {
                  const name = (a.filename || '').split('/').pop();
                  return (
                    <tr key={a.id}>
                      <td className="actions">
                        <button className="mini-btn danger" title="Delete attachment" onClick={() => deleteOrderAttachment(a.id)}>
                          <FaTrash size={16} />
                        </button>
                      </td>
                      <td>
                        <a href={`${API}/api/orders/${orderId}/attachments/${encodeURIComponent(a.filename)}`} target="_blank" rel="noopener noreferrer">
                          {(a.filename || '').split('/').pop()}
                        </a>
                      </td>
                      <td>{a.date ? new Date(a.date).toLocaleString() : ''}</td>
                      <td>{a.description || ''}</td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={3} style={{ color: '#aaa' }}>No attachments</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderLiveViewPanel = () => {
    const assignedSummary = (Array.isArray(liveViewAssignedFilters) && liveViewAssignedFilters.length > 0)
      ? (() => {
        const labels = liveViewAssignedFilters
          .map((v) => liveViewAssignedOptions.find(opt => opt.value === v)?.label)
          .filter(Boolean);
        if (labels.length === 1) return labels[0];
        if (labels.length === 2) return `${labels[0]}, ${labels[1]}`;
        if (labels.length >= 3) return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
        return `${liveViewAssignedFilters.length} assignees`;
      })()
      : 'All assignees';
    const statusSummary = liveViewStatusScope === 'requested'
      ? 'Requested only'
      : liveViewStatusScope === 'picked'
        ? 'Picked only'
      : liveViewStatusScope === 'pre'
        ? 'Requested → Ready to Ship'
        : 'All statuses';
    const formatDate = (order) => {
      const raw = order?.orderdate || order?.datecreated;
      if (!raw) return '';
      const dt = new Date(raw);
      return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString();
    };
    const detailHeader = liveViewSelectedOrder
      ? `Order #${liveViewSelectedOrder.id} · ${liveViewSelectedOrder.clientname || 'Unknown client'}`
      : 'Select an order to view its line items';
    const renderDetailTable = () => {
      if (!liveViewSelectedOrderId) {
        return <div className="live-view-detail-empty">Select an order from the list above.</div>;
      }
      if (liveViewItemsLoading) {
        return <div className="live-view-detail-empty loading">Loading items…</div>;
      }
      if (!liveViewDetailRows.length) {
        return <div className="live-view-detail-empty">No line items found for this order.</div>;
      }
      return (
        <div className="live-view-detail-table-wrapper">
          <table className="live-view-detail-table">
            <thead>
              <tr>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleLiveViewDetailSort('status')}
                  className={liveViewDetailSortField === 'status' ? 'active-sort' : ''}
                >
                  Status {renderLiveViewSortIndicator('status', liveViewDetailSortField, liveViewDetailSortDir)}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleLiveViewDetailSort('receipt')}
                  className={liveViewDetailSortField === 'receipt' ? 'active-sort' : ''}
                >
                  WR # {renderLiveViewSortIndicator('receipt', liveViewDetailSortField, liveViewDetailSortDir)}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleLiveViewDetailSort('pallet')}
                  className={liveViewDetailSortField === 'pallet' ? 'active-sort' : ''}
                >
                  Pallet # {renderLiveViewSortIndicator('pallet', liveViewDetailSortField, liveViewDetailSortDir)}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleLiveViewDetailSort('area')}
                  className={liveViewDetailSortField === 'area' ? 'active-sort' : ''}
                >
                  Area {renderLiveViewSortIndicator('area', liveViewDetailSortField, liveViewDetailSortDir)}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleLiveViewDetailSort('location')}
                  className={liveViewDetailSortField === 'location' ? 'active-sort' : ''}
                >
                  Location {renderLiveViewSortIndicator('location', liveViewDetailSortField, liveViewDetailSortDir)}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleLiveViewDetailSort('qty')}
                  className={liveViewDetailSortField === 'qty' ? 'active-sort' : ''}
                >
                  Qty {renderLiveViewSortIndicator('qty', liveViewDetailSortField, liveViewDetailSortDir)}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleLiveViewDetailSort('picked')}
                  className={liveViewDetailSortField === 'picked' ? 'active-sort' : ''}
                >
                  Picked {renderLiveViewSortIndicator('picked', liveViewDetailSortField, liveViewDetailSortDir)}
                </th>
              </tr>
            </thead>
            <tbody>
              {liveViewDetailRows.map((item) => {
                const normalizedStatus = normalizeStatusName(item?.statusname);
                const statusClass = `live-status ${getLiveStatusClass(normalizedStatus)}`;
                const qty = Number(item?.boxes_units ?? item?.totalpiecesrequested ?? 0);
                const picked = Number(item?.unitspicked ?? 0);
                return (
                  <tr key={item.id}>
                    <td><span className={statusClass}>{item?.statusname || '—'}</span></td>
                    <td>
                      {item?.receiptid ? (
                        <a
                          href="/receipts"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            try { localStorage.setItem('receiptsQ', `wr${item.receiptid}`); } catch {}
                            e.stopPropagation();
                          }}
                        >
                          WR{item.receiptid}
                        </a>
                      ) : '—'}
                    </td>
                    <td>{pad4(item?.palletnumber) || '—'}</td>
                    <td>{item?.areaname || '—'}</td>
                    <td>{item?.location || '—'}</td>
                    <td>{Number.isNaN(qty) || qty === 0 ? '—' : qty}</td>
                    <td>{Number.isNaN(picked) || picked === 0 ? '—' : picked}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    };
    return (
      <div className="live-view-panel">
        <div className="live-view-header">
          <div>
            <h3>Live View Dashboard</h3>
            <p>Monitor in-flight orders and assignments</p>
          </div>
          <div className="live-view-header-actions">
            <button
              type="button"
              className="btn"
              onClick={() => {
                setLiveViewAssignedFilters([]);
                setLiveViewAssigneeOpen(false);
                setLiveViewAssigneeQuery('');
                setLiveViewStatusScope('pre');
                setLiveViewFromDate('');
                setLiveViewToDate('');
                setLiveViewDatePreset('');
                try { localStorage.setItem('ordersLiveViewDatePreset', ''); } catch {}
              }}
              title="Reset Live View filters"
            >
              <FaUndo style={{ marginRight: 4 }} />
              Reset
            </button>
            <button
              type="button"
              className="btn"
              onClick={fetchLiveViewOrders}
              disabled={liveViewLoading}
              title="Refresh Live View only"
            >
              <FaSync style={{ marginRight: 4 }} />
              {liveViewLoading ? 'Refreshing…' : 'Refresh'}
            </button>
            {!liveViewOnly && (
              <button
                type="button"
                className="btn"
                onClick={openLiveViewPopout}
                title="Open Live View in a new tab"
              >
                <FaExternalLinkAlt style={{ marginRight: 4 }} />
                Pop out
              </button>
            )}
            {liveViewOnly ? (
              <>
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => navigate('/orders')}
                  title="Back to Orders"
                >
                  <FaListUl style={{ marginRight: 4 }} />
                  Orders
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    try { window.close(); } catch {}
                    navigate('/orders');
                  }}
                  aria-label="Close Live View tab"
                  title="Close this tab"
                >
                  <FaTimes style={{ marginRight: 4 }} />
                  Close tab
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn"
                onClick={() => setLiveViewEnabled(false)}
                aria-label="Close Live View"
                title="Close Live View"
              >
                <FaTimes style={{ marginRight: 4 }} />
                Close
              </button>
            )}
          </div>
	        </div>
	        <div className="live-view-toolbar">
	          <div className="live-view-toolbar-row">
	            <label>
	              <span>Assigned To</span>
                <div className="live-assignee-picker" ref={liveViewAssigneeRef}>
                  <button
                    type="button"
                    className="live-assignee-trigger"
                    onClick={() => setLiveViewAssigneeOpen((v) => !v)}
                    disabled={liveViewUsersLoading && (!liveViewUserOptions || liveViewUserOptions.length === 0)}
                    title="Select one or more assignees"
                    aria-haspopup="dialog"
                    aria-expanded={liveViewAssigneeOpen ? 'true' : 'false'}
                  >
                    {assignedSummary}
                  </button>
                  {liveViewAssigneeOpen ? (
                    <div className="live-assignee-popover" role="dialog" aria-label="Assignee filter">
                      <div className="live-assignee-popover-header">
                        <input
                          type="text"
                          value={liveViewAssigneeQuery}
                          onChange={(e) => setLiveViewAssigneeQuery(e.target.value)}
                          placeholder="Search users…"
                        />
                        <div className="live-assignee-popover-actions">
                          <button
                            type="button"
                            className="btn secondary"
                            onClick={() => setLiveViewAssignedFilters([])}
                            disabled={!liveViewAssignedFilters.length}
                            title="Clear assignee filter"
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setLiveViewAssigneeOpen(false)}
                            title="Close"
                          >
                            Done
                          </button>
                        </div>
                      </div>
                      <div className="live-assignee-list">
                        {(() => {
                          const q = String(liveViewAssigneeQuery || '').trim().toLowerCase();
                          const opts = q
                            ? liveViewAssignedOptions.filter((opt) => String(opt.label || '').toLowerCase().includes(q))
                            : liveViewAssignedOptions;
                          if (!opts.length) {
                            return <div className="live-assignee-empty">No matching users.</div>;
                          }
                          const allValues = liveViewAssignedOptions.map((o) => o.value);
                          const selected = Array.isArray(liveViewAssignedFilters) ? liveViewAssignedFilters : [];
                          const toggle = (value) => {
                            const next = selected.includes(value)
                              ? selected.filter((v) => v !== value)
                              : [...selected, value];
                            // If "everything" is selected, treat it as "All assignees" (no filter).
                            const normalized = next.length === allValues.length ? [] : next;
                            setLiveViewAssignedFilters(normalized);
                          };
                          return opts.map((opt) => (
                            <label key={opt.value} className="live-assignee-option">
                              <input
                                type="checkbox"
                                checked={selected.includes(opt.value)}
                                onChange={() => toggle(opt.value)}
                              />
                              <span>{opt.label}</span>
                            </label>
                          ));
                        })()}
                      </div>
                    </div>
                  ) : null}
                </div>
	            </label>
	            <label>
	              <span>Status Scope</span>
	              <select
	                value={liveViewStatusScope}
	                onChange={(e) => setLiveViewStatusScope(e.target.value)}
	              >
	                <option value="pre">Requested → Ready to Ship</option>
	                <option value="picked">Picked only</option>
	                <option value="requested">Requested only</option>
	                <option value="all">All statuses</option>
	              </select>
	            </label>
	            <label className="live-refresh-input">
	              <span>Auto Refresh</span>
	              <div className="live-refresh-field">
	                <FaClock />
	                <input
	                  type="number"
	                  min={0}
	                  max={LIVE_REFRESH_MAX_MINUTES}
	                  value={liveViewRefreshMinutes}
	                  onChange={(e) => {
	                    const raw = Number(e.target.value);
	                    const next = Number.isFinite(raw) ? Math.max(0, Math.min(LIVE_REFRESH_MAX_MINUTES, raw)) : 0;
	                    setLiveViewRefreshMinutes(next);
	                  }}
	                />
	                <span>min</span>
	              </div>
	            </label>
	          </div>
	          <div className="live-view-toolbar-row live-view-toolbar-row--dates">
	            <label>
	              <span>Date Preset</span>
	              <select
	                value={liveViewDatePreset}
	                onChange={(e) => applyLiveViewDatePreset(e.target.value)}
	              >
	                <option value="">Custom</option>
	                <option value="today">Today</option>
	                <option value="yesterday">Yesterday</option>
	                <option value="this_week">This week</option>
	                <option value="last_week">Last week</option>
	                <option value="this_month">This month</option>
	                <option value="last_month">Last month</option>
	                <option value="this_year">This year</option>
	                <option value="last_year">Last year</option>
	                <option value="all">All</option>
	              </select>
	            </label>
	            <label>
	              <span>Date From</span>
	              <input
	                type="date"
	                value={liveViewFromDate}
	                onChange={(e) => {
	                  setLiveViewFromDate(e.target.value);
	                  setLiveViewDatePreset('');
	                  try { localStorage.setItem('ordersLiveViewDatePreset', ''); } catch {}
	                }}
	              />
	            </label>
	            <label>
	              <span>Date To</span>
	              <input
	                type="date"
	                value={liveViewToDate}
	                onChange={(e) => {
	                  setLiveViewToDate(e.target.value);
	                  setLiveViewDatePreset('');
	                  try { localStorage.setItem('ordersLiveViewDatePreset', ''); } catch {}
	                }}
	              />
	            </label>
	          </div>
	        </div>
	        <div className="live-view-summary">
	          <span>
	            Showing <strong>{liveViewOrders.length}</strong> order{liveViewOrders.length === 1 ? '' : 's'} · {statusSummary} · {assignedSummary}
	          </span>
          {(liveViewPrioritySaving || liveViewPriorityEditSaving) && (
            <span className="live-priority-saving">Saving priority…</span>
          )}
          {liveViewCanDrag ? (
            <span className="live-priority-hint">Drag the handle (or click the priority) to reprioritize orders.</span>
          ) : (
            liveViewSortField && (
              <span className="live-priority-hint">Click the priority to edit. Clear custom sorting to enable drag-and-drop prioritization.</span>
            )
          )}
        </div>
        <div className="live-view-split-wrapper">
          <Split
            direction="vertical"
            className="live-view-split"
            sizes={liveViewSplitSizes}
            minSize={[140, 120]}
            gutterSize={8}
            onDragEnd={(sizes) => setLiveViewSplitSizes(sizes)}
          >
            <div className="live-view-table-pane">
              <div className="live-view-table-wrapper">
                {liveViewOrders.length ? (
                  <table className="live-view-table">
                    <thead>
                      <tr>
                        <th
                          style={{ width: '12%', cursor: 'pointer' }}
                          onClick={() => handleLiveViewSort('priority')}
                          className={liveViewSortField === 'priority' ? 'active-sort' : ''}
                        >
                          Priority {renderLiveViewSortIndicator('priority', liveViewSortField, liveViewSortDir)}
                        </th>
                        <th
                          style={{ width: '15%', cursor: 'pointer' }}
                          onClick={() => handleLiveViewSort('id')}
                          className={liveViewSortField === 'id' ? 'active-sort' : ''}
                        >
                          Order # {renderLiveViewSortIndicator('id', liveViewSortField, liveViewSortDir)}
                        </th>
                        <th
                          style={{ width: '25%', cursor: 'pointer' }}
                          onClick={() => handleLiveViewSort('assigned')}
                          className={liveViewSortField === 'assigned' ? 'active-sort' : ''}
                        >
                          Assigned To {renderLiveViewSortIndicator('assigned', liveViewSortField, liveViewSortDir)}
                        </th>
                        <th
                          style={{ width: '20%', cursor: 'pointer' }}
                          onClick={() => handleLiveViewSort('status')}
                          className={liveViewSortField === 'status' ? 'active-sort' : ''}
                        >
                          Status {renderLiveViewSortIndicator('status', liveViewSortField, liveViewSortDir)}
                        </th>
                        <th
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleLiveViewSort('client')}
                          className={liveViewSortField === 'client' ? 'active-sort' : ''}
                        >
                          Client {renderLiveViewSortIndicator('client', liveViewSortField, liveViewSortDir)}
                        </th>
                        <th
                          style={{ width: '18%', cursor: 'pointer' }}
                          onClick={() => handleLiveViewSort('orderdate')}
                          className={liveViewSortField === 'orderdate' ? 'active-sort' : ''}
                        >
                          Order Date {renderLiveViewSortIndicator('orderdate', liveViewSortField, liveViewSortDir)}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {liveViewOrders.map((order) => {
                        const statusName = order?.statusname || '—';
                        const normalizedStatus = normalizeStatusName(statusName);
                        const statusClass = `live-status ${getLiveStatusClass(normalizedStatus)}`;
                        const assignedMeta = getLiveAssignedMeta(order);
                        const formattedDate = formatDate(order);
                        const isActive = Number(liveViewSelectedOrderId) === Number(order?.id);
                        const rowClassNames = [
                          isActive ? 'active' : '',
                          liveViewDraggingId === order.id ? 'dragging' : '',
                          liveViewDragOverId === order.id ? 'drag-over' : '',
                        ].filter(Boolean).join(' ') || undefined;
                        return (
                          <tr
                            key={order.id}
                            className={rowClassNames}
                            onClick={() => handleLiveViewRowClick(order)}
                            onDragOver={liveViewCanDrag ? (e) => handleLiveViewDragOver(order, e) : undefined}
                            onDrop={liveViewCanDrag ? (e) => handleLiveViewDrop(order, e) : undefined}
                          >
                            <td className="live-priority-cell">
                              {Number(liveViewPriorityEditOrderId) === Number(order.id) ? (
                                <input
                                  ref={liveViewPriorityInputRef}
                                  type="number"
                                  min="1"
                                  step="1"
                                  className="live-priority-input"
                                  value={liveViewPriorityDraft}
                                  onChange={(e) => setLiveViewPriorityDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      liveViewPriorityIgnoreBlurRef.current = true;
                                      cancelLiveViewPriorityEdit();
                                      return;
                                    }
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      saveLiveViewPriorityEdit(order.id);
                                    }
                                  }}
                                  onBlur={() => {
                                    if (liveViewPriorityIgnoreBlurRef.current) {
                                      liveViewPriorityIgnoreBlurRef.current = false;
                                      cancelLiveViewPriorityEdit();
                                      return;
                                    }
                                    saveLiveViewPriorityEdit(order.id);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  aria-label={`Priority for order ${order.id}`}
                                  disabled={liveViewPrioritySaving || liveViewPriorityEditSaving}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="live-priority-badge live-priority-badge-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startLiveViewPriorityEdit(order);
                                  }}
                                  title="Click to edit priority"
                                  disabled={liveViewPrioritySaving || liveViewPriorityEditSaving}
                                >
                                  {order.assignedpriority != null ? order.assignedpriority : '—'}
                                </button>
                              )}
                              {liveViewCanDrag && order.assignedto ? (
                                <button
                                  type="button"
                                  className="live-drag-handle"
                                  draggable
                                  onDragStart={(e) => handleLiveViewDragStart(order, e)}
                                  onDragEnd={handleLiveViewDragEnd}
                                  onClick={(e) => e.stopPropagation()}
                                  title="Drag to reprioritize"
                                >
                                  <FaGripVertical size={14} />
                                </button>
                              ) : null}
                            </td>
                            <td className="live-col-order">#{order.id}</td>
                            <td>{assignedMeta.label}</td>
                            <td><span className={statusClass}>{statusName}</span></td>
                            <td>{order.clientname || '—'}</td>
                            <td>{formattedDate || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="live-view-empty">
                    {liveViewLoading || !liveViewHasFetched
                      ? 'Loading Live View orders…'
                      : 'No orders match the Live View filters.'}
                  </div>
                )}
              </div>
            </div>
            <div className="live-view-detail-pane">
              <div className="live-view-detail-header">
                <span>{detailHeader}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {liveViewItemsLoading && <span className="live-view-detail-hint">Loading…</span>}
                  {liveViewSelectedOrderId && (
                    <button
                      type="button"
                      className="btn"
                      onClick={handleCloseLiveViewDetails}
                      title="Close order detail panel"
                    >
                      <FaTimes style={{ marginRight: 4 }} />
                      Close
                    </button>
                  )}
                </div>
              </div>
              {renderDetailTable()}
            </div>
          </Split>
        </div>
      </div>
    );
  };

  const renderOrderEditor = (force = false) => {
  if (!force && !editing) return null;
       {/* 👉 Add this block here */}
    const isNew = !selected?.id || creating;
    const requireClientSelection = isNew && !IS_CLIENT && !(IS_CLIENT && singleClientOption);
    const requireWarehouseSelection = isNew;
    const isFtzOutbound = Number(selected?.is_ftz_outbound || 0) === 1;
    const isInbondRemoval = isFtzOutbound && String(selected?.ftz_removal_type || '').trim().toLowerCase() === 'inbond';
    const hasAllocatedOrderItems = (orderItems?.length || 0) > 0;
    const hasAllocatedFtzOrderItems = Array.isArray(orderItems) && orderItems.some((row) => (
      Number(row?.ftz_admission_line_id || 0) > 0 ||
      String(row?.zone_lot_no || '').trim() !== '' ||
      String(row?.ftz_status || '').trim() !== '' ||
      String(row?.country_of_origin || '').trim() !== '' ||
      String(row?.htsus_at_allocation || '').trim() !== '' ||
      row?.customs_value_per_unit != null
    ));
    const ftzOutboundDesignationLocked = isFtzOutbound && (orderItems?.length || 0) > 0;
    const currentFtzStatus = String(selected?.ftz_removal_status || 'draft').trim().toLowerCase() || 'draft';
    const canMarkValidated = isFtzOutbound && !isNew && currentFtzStatus === 'draft';
    const canMarkFiled = isFtzOutbound && !isNew && currentFtzStatus === 'validated';
    const canMarkAuthorized = isFtzOutbound && !isNew && currentFtzStatus === 'filed';
    const canMarkRemoved = isFtzOutbound && !isNew && currentFtzStatus === 'authorized';
    const saveDisabled = Boolean(
      saving || (isNew && (!selected?.warehouseid || !selected?.clientid))
    );
    const saveTitle = (isNew && (!selected?.warehouseid || !selected?.clientid))
      ? 'Select a warehouse and client to save this order'
      : '';
    return (
      <div className="order-editor">
        <h2>{selected?.id ? `Edit Order #${selected.id}` : 'New Order'}</h2>
        <div className="order-form-sections">
          <section className="order-form-section">
            <div className="order-section-header">
              <div className="order-section-kicker">Order Setup</div>
              <div className="order-section-title">Core Details</div>
            </div>
            <div className="form-grid order-section-grid">
              {isNew && (
                <div className="order-editor-required-note">
                  Fields outlined in orange are required.
                </div>
              )}
              {!(IS_CLIENT && singleClientOption) && (
                <label className={requireClientSelection ? 'required-field' : ''}>
                  <span className={requireClientSelection ? 'required-label' : undefined}>
                    Client
                    {requireClientSelection && <span aria-hidden="true">*</span>}
                  </span>
                  {IS_CLIENT ? (
                    <select
                      value={selected?.clientid || ''}
                      onChange={e => {
                        setSelected(s => ({ ...s, clientid: Number(e.target.value || 0) }));
                        setFieldErrors(p => ({ ...p, clientid: false }));
                        setFormErrors(p => ({ ...p, clientid: undefined }));
                      }}
                      disabled={!!selected?.id}
                      className={[
                        requireClientSelection ? 'required-outline' : '',
                        requireClientSelection && !selected?.clientid ? 'required-input' : '',
                        showFieldError('clientid') ? 'error' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <option value="">— Select —</option>
                      {clientOptions.map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={selected?.clientid || ''}
                      onChange={e => {
                        setSelected(s => ({ ...s, clientid: Number(e.target.value || 0) }));
                        setFieldErrors(p => ({ ...p, clientid: false }));
                        setFormErrors(p => ({ ...p, clientid: undefined }));
                      }}
                      disabled={!!selected?.id}
                      className={[
                        requireClientSelection ? 'required-outline' : '',
                        requireClientSelection && !selected?.clientid ? 'required-input' : '',
                        showFieldError('clientid') ? 'error' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <option value="">— Select —</option>
                      {clientOptions.map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                      ))}
                    </select>
                  )}
                  {formErrors.clientid && <span className="error-text">{formErrors.clientid}</span>}
                </label>
              )}
              <label>
                Created Date
                <input
                  type="date"
                  value={(selected.datecreated || todayStr()).slice(0, 10)}
                  onChange={e => setSelected(s => ({ ...s, datecreated: e.target.value }))}
                  disabled={true}
                />
              </label>
              {!isNew && (
                <label>
                  Status
                  <select
                    value={selected.statusid ?? createdStatusId}
                    onChange={e => setSelected(s => ({ ...s, statusid: Number(e.target.value) }))}
                    disabled={IS_CLIENT || !selected.id}
                    className={formErrors.statusid ? 'error' : ''}
                  >
                    <option value="">— Select —</option>
                    {statusOptions.map(opt => (
                      <option key={opt.id || opt.statusid} value={opt.id || opt.statusid}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.statusid && <span className="error-text">{formErrors.statusid}</span>}
                </label>
              )}
              <label>
                Order Date
                <input
                  type="date"
                  value={(selected.orderdate || todayStr()).slice(0, 10)}
                  onChange={e => setSelected(s => ({ ...s, orderdate: e.target.value }))}
                />
              </label>
              {!isNew && (
                <label>
                  Shipped Date
                  <input
                    type="date"
                    value={selected?.shippeddate ? String(selected.shippeddate).slice(0, 10) : ''}
                    onChange={e => setSelected(s => ({ ...s, shippeddate: e.target.value }))}
                  />
                </label>
              )}
              <label className={requireWarehouseSelection ? 'required-field' : ''}>
                <span className={requireWarehouseSelection ? 'required-label' : undefined}>
                  Warehouse
                  {requireWarehouseSelection && <span aria-hidden="true">*</span>}
                </span>
                <select
                  value={selected?.warehouseid || ''}
                  onChange={e => setSelected(s => ({ ...s, warehouseid: Number(e.target.value || 0) }))}
                  disabled={!canChangeWarehouse}
                  className={[
                    requireWarehouseSelection ? 'required-outline' : '',
                    requireWarehouseSelection && !selected?.warehouseid ? 'required-input' : '',
                    showFieldError('warehouseid') ? 'error' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <option value="">— Select —</option>
                  {warehouseOptions.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
                {formErrors.warehouseid && <span className="error-text">{formErrors.warehouseid}</span>}
                {selected?.id && !canChangeWarehouse && (
                  <div style={{ fontSize: 12, opacity: .8, marginTop: 4 }}>
                    Warehouse can be changed only when status is <b>Created</b> and no items have been added.
                  </div>
                )}
              </label>
            </div>
          </section>

          <section className="order-form-section">
            <div className="order-section-header">
              <div className="order-section-kicker">Order Setup</div>
              <div className="order-section-title">Shipping & Billing</div>
            </div>
            <div className="form-grid order-section-grid">
              <label>
                PO Number
                <input
                  value={selected?.po || ''}
                  onChange={e => setSelected(s => ({ ...s, po: e.target.value }))}
                />
              </label>
              {!isNew && (
                <label>
                  Tracking Number
                  <input
                    value={selected?.trackingnumber || ''}
                    onChange={e => setSelected(s => ({ ...s, trackingnumber: e.target.value }))}
                  />
                </label>
              )}
              <label>
                Carrier
                <select
                  value={selected?.carrierid || ''}
                  onChange={e => setSelected(s => ({ ...s, carrierid: e.target.value }))}
                  className={formErrors.carrierid ? 'error' : ''}
                  disabled={selectedOrderIsShipped}
                >
                  <option value="">— Select —</option>
                  {carrierOptions.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
                {formErrors.carrierid && <span className="error-text">{formErrors.carrierid}</span>}
                {selectedOrderIsShipped && (
                  <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                    Carrier cannot be changed once an order is marked Shipped.
                  </div>
                )}
              </label>
              <label>
                Destination
                <select
                  value={selected?.destinationid || ''}
                  onChange={e => setSelected(s => ({ ...s, destinationid: e.target.value }))}
                  disabled={IS_CLIENT || selectedOrderIsShipped}
                >
                  <option value="">— Select —</option>
                  {destinationOptions.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
                {selectedOrderIsShipped && (
                  <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                    Destination cannot be changed once an order is marked Shipped.
                  </div>
                )}
              </label>
              <label>
                Non-billable
                <select
                  value={selected?.nonbillable || 0}
                  onChange={e => setSelected(s => ({ ...s, nonbillable: Number(e.target.value) }))}
                >
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label>
                Locked
                <select
                  value={selected?.locked || 0}
                  onChange={e => setSelected(s => ({ ...s, locked: Number(e.target.value) }))}
                >
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              {!isNew && (
                <label>
                  Assigned To
                  <input
                    value={
                      (selected?.assignedto_name && selected.assignedto_name.trim() !== '')
                        ? selected.assignedto_name
                        : (selected?.assignedto ? String(selected.assignedto) : '')
                    }
                    onChange={e => {
                      const val = e.target.value;
                      setSelected(s => ({
                        ...s,
                        assignedto_name: val,
                        assignedto: /^\d+$/.test(val.trim())
                          ? Number(val.trim())
                          : (s?.assignedto ?? null),
                      }));
                    }}
                    readOnly={IS_CLIENT}
                  />
                </label>
              )}
              <label className="span-2">
                Notes
                <textarea
                  value={selected?.notes || ''}
                  onChange={e => setSelected(s => ({ ...s, notes: e.target.value }))}
                />
              </label>
            </div>
          </section>

          <section className="order-form-section">
            <div className="order-section-header">
              <div className="order-section-kicker">Delivery</div>
              <div className="order-section-title">Ship To</div>
            </div>
            <div className="form-grid order-section-grid">
              <label>
                Name
                <input
                  value={selected?.shiptoname || ''}
                  onChange={e => setSelected(s => ({ ...s, shiptoname: e.target.value }))}
                />
              </label>
              <label>
                Address
                <input
                  value={selected?.shiptoaddress || ''}
                  onChange={e => setSelected(s => ({ ...s, shiptoaddress: e.target.value }))}
                />
              </label>
              <label>
                City
                <input
                  value={selected?.shiptocity || ''}
                  onChange={e => setSelected(s => ({ ...s, shiptocity: e.target.value }))}
                />
              </label>
              <label>
                State
                <select
                  value={selected?.shiptostate || ''}
                  onChange={e => setSelected(s => ({ ...s, shiptostate: e.target.value }))}
                >
                  <option value="">— Select —</option>
                  {US_STATES.map(st => (
                    <option key={st.code} value={st.code}>
                      {st.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                ZIP
                <input
                  value={selected?.shiptozip || ''}
                  onChange={e => setSelected(s => ({ ...s, shiptozip: e.target.value }))}
                  placeholder="e.g. 90210"
                />
              </label>
              <label>
                Phone
                <input
                  type="text"
                  value={selected?.shiptophone || ''}
                  onChange={e => {
                    const formatted = formatPhone(e.target.value);
                    setSelected(s => ({ ...s, shiptophone: formatted }));
                  }}
                  placeholder="(555) 123-4567"
                />
              </label>
            </div>
          </section>

          <section className={`order-form-section ${isFtzOutbound ? 'order-form-section--ftz' : ''}`}>
            <div className="order-section-header">
              <div className="order-section-kicker">{isInbondRemoval ? 'FTZ In-Bond / 7512' : 'FTZ Outbound'}</div>
              <div className="order-section-title">{isInbondRemoval ? 'In-Bond Removal Tracking' : 'Removal Tracking'}</div>
            </div>
            <div className="form-grid order-section-grid">
              <label>
                FTZ Outbound Order
                <select
                  value={selected?.is_ftz_outbound || 0}
                  disabled={ftzOutboundDesignationLocked}
                  onChange={e => {
                    const enabled = Number(e.target.value || 0);
                    setSelected(s => ({
                      ...s,
                      is_ftz_outbound: enabled,
                      ftz_removal_type: enabled ? (s?.ftz_removal_type || '') : '',
                      ftz_removal_status: enabled ? (s?.ftz_removal_status || 'draft') : '',
                      ftz_broker_reference: enabled ? (s?.ftz_broker_reference || '') : '',
                      ftz_ace_submission_id: enabled ? (s?.ftz_ace_submission_id || '') : '',
                      ftz_ace_reference_no: enabled ? (s?.ftz_ace_reference_no || '') : '',
                      ftz_export_reference: enabled ? (s?.ftz_export_reference || '') : '',
                      ftz_inbond_number: enabled ? (s?.ftz_inbond_number || '') : '',
                      ftz_inbond_type: enabled ? (s?.ftz_inbond_type || '') : '',
                      ftz_inbond_origin_port: enabled ? (s?.ftz_inbond_origin_port || '') : '',
                      ftz_inbond_destination_port: enabled ? (s?.ftz_inbond_destination_port || '') : '',
                      ftz_inbond_carrier: enabled ? (s?.ftz_inbond_carrier || '') : '',
                      ftz_inbond_conveyance: enabled ? (s?.ftz_inbond_conveyance || '') : '',
                      ftz_status_message: enabled ? (s?.ftz_status_message || '') : '',
                      ftz_filed_at: enabled ? (s?.ftz_filed_at || '') : '',
                      ftz_authorized_at: enabled ? (s?.ftz_authorized_at || '') : '',
                      ftz_removed_at: enabled ? (s?.ftz_removed_at || '') : '',
                    }));
                  }}
                >
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
                {ftzOutboundDesignationLocked && (
                  <div className="order-ftz-lock-note">
                    FTZ outbound designation is locked because inventory has already been allocated to this order.
                  </div>
                )}
              </label>
              <label>
                Removal Type
                <select
                  value={selected?.ftz_removal_type || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_removal_type: e.target.value }))}
                  disabled={!isFtzOutbound}
                >
                  <option value="">— Select —</option>
                  <option value="domestic">Domestic</option>
                  <option value="export">Export</option>
                  <option value="inbond">In-Bond</option>
                </select>
              </label>
              <label>
                {isInbondRemoval ? '7512 / In-Bond Status' : 'FTZ Status'}
                <input
                  value={selected?.ftz_removal_status || ''}
                  readOnly
                  disabled={!isFtzOutbound}
                  placeholder="draft / validated / removed"
                />
              </label>
              <label>
                Broker Reference
                <input
                  value={selected?.ftz_broker_reference || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_broker_reference: e.target.value }))}
                  disabled={!isFtzOutbound}
                />
              </label>
              <label>
                ACE Submission ID
                <input
                  value={selected?.ftz_ace_submission_id || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_ace_submission_id: e.target.value }))}
                  disabled={!isFtzOutbound}
                />
              </label>
              <label>
                ACE Reference No
                <input
                  value={selected?.ftz_ace_reference_no || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_ace_reference_no: e.target.value }))}
                  disabled={!isFtzOutbound}
                />
              </label>
              <label>
                Export Reference
                <input
                  value={selected?.ftz_export_reference || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_export_reference: e.target.value }))}
                  disabled={!isFtzOutbound}
                />
              </label>
              <label>
                {isInbondRemoval ? '7512 / In-Bond Number' : 'In-Bond Number'}
                <input
                  value={selected?.ftz_inbond_number || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_inbond_number: e.target.value }))}
                  disabled={!isFtzOutbound}
                />
              </label>
              <label>
                In-Bond Type
                <select
                  value={selected?.ftz_inbond_type || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_inbond_type: e.target.value }))}
                  disabled={!isFtzOutbound || String(selected?.ftz_removal_type || '') !== 'inbond'}
                >
                  <option value="">— Select —</option>
                  <option value="it">Immediate Transportation</option>
                  <option value="te">Transportation and Exportation</option>
                  <option value="iew">Immediate Exportation from Warehouse</option>
                </select>
              </label>
              <label>
                In-Bond Origin Port
                <input
                  value={selected?.ftz_inbond_origin_port || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_inbond_origin_port: e.target.value }))}
                  disabled={!isFtzOutbound || String(selected?.ftz_removal_type || '') !== 'inbond'}
                />
              </label>
              <label>
                In-Bond Destination Port
                <input
                  value={selected?.ftz_inbond_destination_port || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_inbond_destination_port: e.target.value }))}
                  disabled={!isFtzOutbound || String(selected?.ftz_removal_type || '') !== 'inbond'}
                />
              </label>
              <label>
                In-Bond Carrier
                <input
                  value={selected?.ftz_inbond_carrier || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_inbond_carrier: e.target.value }))}
                  disabled={!isFtzOutbound || String(selected?.ftz_removal_type || '') !== 'inbond'}
                />
              </label>
              <label>
                In-Bond Conveyance
                <input
                  value={selected?.ftz_inbond_conveyance || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_inbond_conveyance: e.target.value }))}
                  disabled={!isFtzOutbound || String(selected?.ftz_removal_type || '') !== 'inbond'}
                />
              </label>
              <label>
                Filed At
                <input
                  type="datetime-local"
                  value={toDateTimeLocalInput(selected?.ftz_filed_at)}
                  readOnly
                  disabled={!isFtzOutbound}
                />
              </label>
              <label>
                Authorized At
                <input
                  type="datetime-local"
                  value={toDateTimeLocalInput(selected?.ftz_authorized_at)}
                  readOnly
                  disabled={!isFtzOutbound}
                />
              </label>
              <label>
                Removed At
                <input
                  type="datetime-local"
                  value={toDateTimeLocalInput(selected?.ftz_removed_at)}
                  readOnly
                  disabled={!isFtzOutbound}
                />
              </label>
              <label className="span-2">
                FTZ Status Note
                <textarea
                  value={selected?.ftz_status_message || ''}
                  onChange={e => setSelected(s => ({ ...s, ftz_status_message: e.target.value }))}
                  disabled={!isFtzOutbound}
                />
              </label>
            </div>
            {isFtzOutbound && !isNew && (
              <div className="order-ftz-actions">
                {isInbondRemoval && (
                  <div className="order-ftz-inline-note">
                    In-Bond orders use the 7512 packet naming and require the in-bond number, type, origin port, and destination port before workflow progression.
                  </div>
                )}
                <button
                  type="button"
                  className="btn"
                  disabled={!hasAllocatedFtzOrderItems}
                  title={hasAllocatedFtzOrderItems ? '' : 'Allocate FTZ inventory before exporting an outbound FTZ packet'}
                  onClick={() => exportOrderFtzPacket(selected?.id, 'pdf')}
                >
                  {isInbondRemoval ? 'Export 7512 PDF Packet' : 'Export PDF Packet'}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!hasAllocatedFtzOrderItems}
                  title={hasAllocatedFtzOrderItems ? '' : 'Allocate FTZ inventory before exporting an outbound FTZ packet'}
                  onClick={() => exportOrderFtzPacket(selected?.id, 'json')}
                >
                  {isInbondRemoval ? 'Export 7512 JSON Packet' : 'Export JSON Packet'}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!hasAllocatedFtzOrderItems}
                  title={hasAllocatedFtzOrderItems ? '' : 'Allocate FTZ inventory before exporting an outbound FTZ packet'}
                  onClick={() => exportOrderFtzPacket(selected?.id, 'csv')}
                >
                  {isInbondRemoval ? 'Export 7512 CSV Packet' : 'Export CSV Packet'}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!canMarkValidated || !!ftzActionRunning}
                  onClick={() => runOrderFtzAction('validate')}
                >
                  {ftzActionRunning === 'validate' ? 'Validating…' : 'Mark Validated'}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!canMarkFiled || !!ftzActionRunning}
                  onClick={() => runOrderFtzAction('filed')}
                >
                  {ftzActionRunning === 'filed' ? 'Filing…' : 'Mark Filed'}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!canMarkAuthorized || !!ftzActionRunning}
                  onClick={() => runOrderFtzAction('authorized')}
                >
                  {ftzActionRunning === 'authorized' ? 'Authorizing…' : 'Mark Authorized'}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!canMarkRemoved || !!ftzActionRunning}
                  onClick={() => runOrderFtzAction('removed')}
                >
                  {ftzActionRunning === 'removed' ? 'Removing…' : 'Mark Removed'}
                </button>
              </div>
            )}
            {isFtzOutbound && !isNew && !hasAllocatedFtzOrderItems && (
              <div className="order-ftz-lock-note">
                Allocate FTZ inventory to the order before exporting an outbound FTZ packet.
              </div>
            )}
            {isFtzOutbound && !isNew && (
              <div className="order-ftz-drilldown">
                <div className="order-ftz-drilldown-head">
                  <div>
                    <strong>FTZ Removal Detail</strong>
                    <span>Review linked admission lines, remaining FTZ balance, and the outbound audit trail.</span>
                  </div>
                </div>

                {ftzDetailLoading ? (
                  <div className="order-ftz-drilldown-empty">Loading FTZ removal detail…</div>
                ) : (
                  <>
                    <div className="order-ftz-summary-grid">
                      <div className="order-ftz-summary-tile">
                        <span>FTZ Lines</span>
                        <strong>{Number(ftzDetail?.totals?.ftz_line_count || 0)}</strong>
                      </div>
                      <div className="order-ftz-summary-tile">
                        <span>Removed Lines</span>
                        <strong>{Number(ftzDetail?.totals?.removed_line_count || 0)}</strong>
                      </div>
                      <div className="order-ftz-summary-tile">
                        <span>Admitted Qty</span>
                        <strong>{Number(ftzDetail?.totals?.admitted_qty || 0).toLocaleString()}</strong>
                      </div>
                      <div className="order-ftz-summary-tile">
                        <span>Removed Qty</span>
                        <strong>{Number(ftzDetail?.totals?.removed_qty || 0).toLocaleString()}</strong>
                      </div>
                      <div className="order-ftz-summary-tile">
                        <span>Balance Qty</span>
                        <strong>{Number(ftzDetail?.totals?.balance_qty || 0).toLocaleString()}</strong>
                      </div>
                      <div className="order-ftz-summary-tile">
                        <span>On Hand Qty</span>
                        <strong>{Number(ftzDetail?.totals?.on_hand_qty || 0).toLocaleString()}</strong>
                      </div>
                    </div>

                    <div className="order-ftz-detail-block">
                      <div className="order-ftz-detail-title">Linked FTZ Removal Lines</div>
                      <div className="order-ftz-table-wrap">
                        <table className="order-ftz-table">
                          <thead>
                            <tr>
                              <th>Order Inv</th>
                              <th>Receipt</th>
                              <th>Admission</th>
                              <th>SKU</th>
                              <th>Zone Lot</th>
                              <th>Status</th>
                              <th>Qty</th>
                              <th>Admitted</th>
                              <th>Removed</th>
                              <th>Balance</th>
                              <th>On Hand</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(ftzDetail?.lines || []).length ? (
                              (ftzDetail.lines || []).map((line) => (
                                <tr key={`ftz-detail-line-${line.orderinventoryid || line.inventoryid || line.ftz_admission_line_id}`}>
                                  <td>{line.orderinventoryid ? `OI${line.orderinventoryid}` : '—'}</td>
                                  <td>
                                    {line.receiptid ? (
                                      <a href={receiptEditorHref(line.receiptid)} target="_blank" rel="noreferrer">
                                        WR{line.receiptid}
                                      </a>
                                    ) : '—'}
                                  </td>
                                  <td>{line.admission_number ? `${line.admission_number}${line.admission_line_no ? ` / L${line.admission_line_no}` : ''}` : (line.ftz_admission_line_id ? `L${line.ftz_admission_line_id}` : '—')}</td>
                                  <td>{line.sku || '—'}</td>
                                  <td>{line.zone_lot_no || '—'}</td>
                                  <td>{line.line_status || '—'}</td>
                                  <td>{Number(line.qty_units || 0).toLocaleString()} {line.quantity_uom || ''}</td>
                                  <td>{Number(line.admitted_qty || 0).toLocaleString()}</td>
                                  <td>{Number(line.removed_qty_total || 0).toLocaleString()}</td>
                                  <td>{Number(line.balance_qty || 0).toLocaleString()}</td>
                                  <td>{Number(line.on_hand_qty || 0).toLocaleString()}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={11}>No FTZ inventory has been allocated to this outbound order yet.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="order-ftz-detail-block">
                      <div className="order-ftz-detail-title">FTZ Audit Trail</div>
                      <div className="order-ftz-table-wrap">
                        <table className="order-ftz-table">
                          <thead>
                            <tr>
                              <th>When</th>
                              <th>Action</th>
                              <th>User</th>
                              <th>Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(ftzDetail?.audit_log || []).length ? (
                              (ftzDetail.audit_log || []).map((row) => (
                                <tr key={`ftz-audit-${row.id}`}>
                                  <td>{fmtFtzDateTime(row.created_at)}</td>
                                  <td>{row.action || '—'}</td>
                                  <td>{row.username || row.userid || 'system'}</td>
                                  <td className="order-ftz-detail-pre">{formatFtzAuditDetails(row.details)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={4}>No FTZ audit history has been recorded for this order yet.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </section>
        </div>

    

        <div className="actions">
          <button
            className="btn"
            type="button"
            onClick={() => {
              setSelected(null);
              setEditing(false);
              setBottomPaneMode(null);
              setFormErrors({});
            }}
            style={{ marginRight: 10 }}
          >
            Close
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => saveOrder(selected)}
            disabled={saveDisabled}
            title={saveTitle}
          >
            
            <FaRegSave size={16} /> {saving ? 'Saving…' : 'Save Order'}
          </button>
        </div>
      </div>
    );
  };
  const bottomPaneActive = !!bottomPaneMode && !editing;
  const renderLeftPane = () => (
    <div className="orders-left-pane">
      {bottomPaneActive ? (
        <Split
          className="orders-vertical-split"
          direction="vertical"
          sizes={splitHeights}
          minSize={[220, 180]}
          gutterSize={6}
          style={{ height: '100%', width: '100%' }}
          onDragEnd={(sizes) => {
            setSplitHeights(sizes);
            localStorage.setItem('ordersItemsSplitHeights', JSON.stringify(sizes));
            localStorage.setItem('ordersItemsSplitPct', String(sizes[1]));
          }}
        >
          <div className="pane orders-pane-top">
            {OrdersTopList}
          </div>
          <div className="pane orders-pane-bottom">
            <div className="bottom-pane-toolbar">
              <button
                className={`btn toggle ${bottomPaneMode === 'items' ? 'active' : ''}`}
                onClick={() => setBottomPaneMode('items')}
                disabled={!selectedOrderId}
                aria-pressed={bottomPaneMode === 'items'}
                title="Items"
              >
                <FaListUl style={{ verticalAlign: '-2px' }} />
                <span style={{ marginLeft: 6 }}>Items</span>
              </button>
              {!IS_CLIENT && (
                <button
                  className={`btn toggle ${bottomPaneMode === 'history' ? 'active' : ''}`}
                  onClick={() => setBottomPaneMode('history')}
                  disabled={!selectedOrderId}
                  aria-pressed={bottomPaneMode === 'history'}
                  title="History"
                >
                  <FaHistory style={{ verticalAlign: '-2px' }} />
                  History
                </button>
              )}
              <button
                className={`btn toggle ${bottomPaneMode === 'attachments' ? 'active' : ''}`}
                onClick={() => setBottomPaneMode('attachments')}
                disabled={!selectedOrderId}
                aria-pressed={bottomPaneMode === 'attachments'}
                title="Attachments"
              >
                <FaPaperclip style={{ verticalAlign: '-2px' }} />
                Attachments
              </button>
              <div style={{ flex: 1 }} />
              {!IS_CLIENT && (
                <button
                  className="btn"
                  onClick={openChangeStatus}
                  disabled={!selectedOrderId || (selectedItemIds.size === 0) || selectionHasShipped}
                  title={selectionHasShipped ? 'Cannot change status for items already Shipped' : 'Change status for selected line items'}
                >
                  <FaEdit style={{ verticalAlign: '-2px' }} />
                  Change Item Status
                </button>
              )}
              {!IS_CLIENT && (
                <button
                  className="btn"
                  onClick={openLineItemBolModal}
                  disabled={!selectedOrderId || selectedItemIds.size === 0}
                  title="Set or clear the BOL for selected line items"
                >
                  <FaFileSignature style={{ verticalAlign: '-2px' }} />
                  Set Item BOL
                </button>
              )}
              {!IS_CLIENT && (
                <button
                  className="btn"
                  onClick={openLineItemTrackingModal}
                  disabled={!selectedOrderId || selectedItemIds.size === 0}
                  title="Set or clear the Tracking # for selected line items"
                >
                  <FaHashtag style={{ verticalAlign: '-2px' }} />
                  Set Tracking #
                </button>
              )}
              {!IS_CLIENT && (
                <button
                  className="btn"
                  onClick={openShippingPalletModal}
                  disabled={!selectedOrderId || selectedItemIds.size === 0}
                  title="Assign a Shipping Pallet # to the selected line items"
                >
                  <FaBoxOpen style={{ verticalAlign: '-2px' }} />
                  Assign Pallet #
                </button>
              )}
              {!IS_CLIENT && (
                <button
                  className="btn"
                  onClick={handleGenerateLineItemBol}
                  disabled={!selectedOrderId || selectedItemIds.size === 0}
                  title="Generate a Bill of Lading for the selected line items"
                >
                  <FaFileInvoice style={{ verticalAlign: '-2px' }} />
                  Generate BOL
                </button>
              )}
              {!IS_CLIENT && (
                <button
                  className="btn"
                  onClick={openPalletLabels}
                  disabled={!selectedOrderId}
                  title="Print 4×6 Pallet Labels"
                >
                  <FaPrint style={{ verticalAlign: '-2px' }} />
                  Pallet Labels
                </button>
              )}
              <button className="btn" onClick={() => setBottomPaneMode(null)} title="Close">
                <FaTimes style={{ verticalAlign: '-2px' }} />
                Close
              </button>
            </div>
            {bottomPaneMode === 'history' && !IS_CLIENT
              ? renderOrderHistoryPanel()
              : bottomPaneMode === 'attachments'
                ? renderOrderAttachmentsPanel()
                : renderOrderItemsPanel()}
          </div>
        </Split>
      ) : (
        <div className="pane orders-pane-top">{OrdersTopList}</div>
      )}
    </div>
  );
  const showLiveView = !IS_CLIENT && (liveViewOnly || (liveViewEnabled && !editing && !creating));
  const leftPane = liveViewOnly ? null : renderLeftPane();
  const wrapperClass = `orders-wrapper-col${(selected && editing) ? ' with-editor' : ' full-list'}${showLiveView ? ' has-live' : ''}${liveViewOnly ? ' liveview-only' : ''}`;
  useEffect(() => {
    if (!showLiveView) return;
    // Call the latest callback directly so changes (like Live View date range) refresh immediately.
    fetchLiveViewOrders();
  }, [showLiveView, liveViewFilterSignature, fetchLiveViewOrders]);
  useEffect(() => {
    if (!showLiveView) return;
    if (liveViewUsersInitRef.current) return;
    liveViewUsersInitRef.current = true;
    fetchLiveViewUsers();
  }, [showLiveView, fetchLiveViewUsers]);
  useEffect(() => {
    if (!showLiveView || liveViewRefreshMinutes <= 0) return;
    const intervalMs = Math.max(LIVE_REFRESH_MIN_MS, liveViewRefreshMinutes * 60 * 1000);
    const id = setInterval(() => {
      fetchLiveViewOrdersRef.current();
    }, intervalMs);
    return () => clearInterval(id);
  }, [showLiveView, liveViewRefreshMinutes]);
  useEffect(() => {
    if (showLiveView) return;
    if (liveViewFetchAbort.current) {
      try { liveViewFetchAbort.current.abort(); } catch {}
      liveViewFetchAbort.current = null;
    }
    setLiveViewLoading(false);
    setLiveViewSelectedOrderId(null);
    setLiveViewItems([]);
    setLiveViewAssigneeOpen(false);
    setLiveViewAssigneeQuery('');
    cancelLiveViewPriorityEdit();
  }, [showLiveView, cancelLiveViewPriorityEdit]);
  const clampedLiveWidth = clampLiveWidthPct(liveViewWidthPct);
  const clampedLeftWidth = 100 - clampedLiveWidth;
  const liveLayout = (!liveViewOnly && showLiveView) ? (
    <div className="orders-live-row" ref={liveLayoutRef}>
      <div className="orders-live-left" style={{ flexBasis: `${clampedLeftWidth}%` }}>
        {leftPane}
      </div>
      <div
        className="orders-live-gutter"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Live View"
        onMouseDown={handleLiveResizeStart}
      />
      <div className="orders-live-pane" style={{ flexBasis: `${clampedLiveWidth}%` }}>
        {renderLiveViewPanel()}
      </div>
    </div>
  ) : null;
  const liveOnlyLayout = (liveViewOnly && showLiveView)
    ? (
      <div className="orders-live-pane orders-live-pane--solo">
        {renderLiveViewPanel()}
      </div>
    )
    : null;
  return (
    <div className={wrapperClass}>
      {showLiveView ? (liveViewOnly ? liveOnlyLayout : liveLayout) : leftPane}

      {(editing || creating) && selected && (
        <>
          {selected?.id ? (
            <div className="orders-right-pane">
              <div className="order-editor-scroll">
                <OrderEditWorkspace
                  order={selected}
                  editor={renderOrderEditor()}
                />
              </div>
            </div>
          ) : (
            <div className="neo-overlay">
              <div className="neo-modal" onClick={(e) => e.stopPropagation()}>
                <div className="neo-titlebar">
                  <h3 className="neo-title">New Order</h3>
                  <button
                    className="icon-btn"
                    onClick={() => { setSelected(null); setEditing(false); setCreating(false); }}
                    aria-label="Close"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="neo-body">
                  {renderOrderEditor(true)} {/* pass flag if you want modal mode */}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {assignOpen && (
        <div className="modal-overlay" onClick={closeAssign}>
          <div className="modal large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 style={{ margin: 0 }}>
                {assignForOrder ? `Assign Order #${assignForOrder.id}` : 'Assign Order'}
              </h3>
              <button className="mini-btn" onClick={closeAssign} aria-label="Close">×</button>
            </div>
            <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {assignLoading ? (
                <div className="loading">Loading users…</div>
              ) : assignUsers.length ? (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span>User</span>
                  <select
                    className="neo-input"
                    value={assignSelected || ''}
                    onChange={(e) => {
                      setAssignSelected(e.target.value);
                      setAssignPriority('');
                      setAssignPriorityDirty(false);
                    }}
                  >
                    <option value="">Select a user…</option>
                    {assignUsers.map((u) => (
                      <option key={u.userid} value={String(u.userid)}>
                        {u.fullname || u.login || u.email || u.userid}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <div style={{ opacity: 0.75 }}>No users available.</div>
              )}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span>Priority (optional)</span>
                <input
                  className="neo-input"
                  type="number"
                  min={0}
                  step={1}
                  value={assignPriority}
                  onChange={(e) => {
                    setAssignPriority(e.target.value.replace(/[^0-9-]/g, ''));
                    setAssignPriorityDirty(true);
                  }}
                  placeholder="e.g. 1, 2, 3"
                />
                <small style={{ opacity: 0.7 }}>Lower numbers can signal higher priority in Live View.</small>
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={closeAssign}>Cancel</button>
              <button
                className="btn primary"
                disabled={!assignSelected || assignLoading}
                onClick={confirmAssign}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
      {showNewCarrier && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>New Carrier</h3>
            <div><label>Name<input value={newCarrierName} onChange={e => setNewCarrierName(e.target.value)} autoFocus /></label></div>
            <div className="actions" style={{ marginTop: 16 }}>
              <button className="btn" onClick={() => setShowNewCarrier(false)} style={{ marginRight: 8 }}>Cancel</button>
              <button className="btn primary" onClick={() => {/* implement quickAddCarrier if needed */ }}>
                <FaRegSave style={{ verticalAlign: '-2px' }} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
      {showNewDestination && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>New Destination</h3>
            <div><label>Name<input value={newDestinationName} onChange={e => setNewDestinationName(e.target.value)} autoFocus /></label></div>
            <div className="actions" style={{ marginTop: 16 }}>
              <button className="btn" onClick={() => setShowNewDestination(false)} style={{ marginRight: 8 }}>Cancel</button>
              <button className="btn primary" onClick={() => {/* implement quickAddDestination if needed */ }}>
                <FaRegSave style={{ verticalAlign: '-2px' }} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Removed duplicate email modal overlay (caused white band + blocked dismiss) */}
      {!IS_CLIENT && changeStatusOpen && (
        <div className="modal-overlay" onClick={closeChangeStatus}>
          <div
            className="modal"
            onClick={(e)=>e.stopPropagation()}
            style={{ width: '50vw', maxWidth: '50vw' }}
          >
            <h3>Change Item Status</h3>
            <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
              <label>
                <span style={{ display:'block', fontSize:12, opacity:.8 }}>New Status</span>
                <select value={changeStatusId} onChange={e => setChangeStatusId(e.target.value)}>
                  <option value="">— Select —</option>
                  {itemStatuses.map(st => (
                    <option key={st.id || st.statusid} value={st.id || st.statusid}>{st.name}</option>
                  ))}
                </select>
              </label>
              {(() => {
                const sid = Number(changeStatusId || 0);
                const st = itemStatuses.find(s => Number(s.id || s.statusid) === sid);
                const isShipped = !!(st && String(st.name || '').trim().toLowerCase() === 'shipped');
                if (!isShipped) return null;
                return (
                  <label>
                    <span style={{ display:'block', fontSize:12, opacity:.8 }}>Shipped Date</span>
                    <input type="date" value={changeShipDate || ''} onChange={e => setChangeShipDate(e.target.value)} />
                  </label>
                );
              })()}
            </div>
            <div className="actions" style={{ marginTop: 12 }}>
              <button className="btn" onClick={closeChangeStatus}>Cancel</button>
              <button className="btn primary" disabled={!changeStatusId || selectedItemIds.size === 0 || selectionHasShipped} onClick={confirmChangeStatus} title={selectionHasShipped ? 'Selection includes items already Shipped' : undefined}>Apply</button>
            </div>
          </div>
        </div>
      )}
      {!IS_CLIENT && lineBolOpen && (
        <div className="modal-overlay" onClick={closeLineItemBolModal}>
          <div
            className="modal"
            onClick={(e)=>e.stopPropagation()}
            style={{ width: 'min(620px, 95vw)' }}
          >
            <h3>Set Line Item BOL</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ width: '100%', display: 'block' }}>
                <span style={{ display: 'block', fontSize: 12, opacity: 0.8, marginBottom: 4 }}>BOL</span>
                <input
                  type="text"
                  value={lineBolValue}
                  onChange={(e) => setLineBolValue(e.target.value)}
                  placeholder="Enter BOL # (leave blank to clear)"
                  autoFocus
                />
              </label>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
                Applying to {selectedItemIds.size} selected line item{selectedItemIds.size === 1 ? '' : 's'}.
              </div>
            </div>
            <div className="actions">
              <button className="btn" onClick={closeLineItemBolModal} disabled={lineBolSaving}>Cancel</button>
              <button
                className="btn primary"
                onClick={confirmApplyLineItemBol}
                disabled={lineBolSaving || selectedItemIds.size === 0}
              >
                {lineBolSaving ? 'Saving…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
      {!IS_CLIENT && lineTrackingOpen && (
        <div className="modal-overlay" onClick={closeLineItemTrackingModal}>
          <div
            className="modal"
            onClick={(e)=>e.stopPropagation()}
            style={{ width: 'min(620px, 95vw)' }}
          >
            <h3>Set Line Item Tracking #</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ width: '100%', display: 'block' }}>
                <span style={{ display: 'block', fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Tracking #</span>
                <input
                  type="text"
                  value={lineTrackingValue}
                  onChange={(e) => setLineTrackingValue(e.target.value)}
                  placeholder="Enter Tracking # (leave blank to clear)"
                  autoFocus
                />
              </label>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
                Applying to {selectedItemIds.size} selected line item{selectedItemIds.size === 1 ? '' : 's'}.
              </div>
            </div>
            <div className="actions">
              <button className="btn" onClick={closeLineItemTrackingModal} disabled={lineTrackingSaving}>Cancel</button>
              <button
                className="btn primary"
                onClick={confirmApplyLineItemTracking}
                disabled={lineTrackingSaving || selectedItemIds.size === 0}
              >
                {lineTrackingSaving ? 'Saving…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
      {!IS_CLIENT && shippingPalletOpen && (
        <div className="modal-overlay" onClick={closeShippingPalletModal}>
          <div
            className="modal shipping-pallet-modal"
            onClick={(e)=>e.stopPropagation()}
          >
            <h3>Assign Shipping Pallet</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, marginBottom:8 }}>
                <input
                  type="checkbox"
                  checked={shippingPalletSequential}
                  onChange={(e) => setShippingPalletSequential(e.target.checked)}
                />
                Assign sequential pallet numbers
              </label>
              {!shippingPalletSequential ? (
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Shipping Pallet #</span>
                  <input
                    type="number"
                    min="1"
                    value={shippingPalletValue}
                    onChange={(e) => setShippingPalletValue(e.target.value)}
                    placeholder="Enter pallet number"
                    autoFocus
                  />
                </label>
              ) : (
                <div style={{ display:'flex', gap:12, flexWrap:'wrap', justifyContent:'flex-start' }}>
                  <label style={{ flex:'0 1 260px', minWidth:220 }}>
                    <span style={{ display: 'block', fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Starting Pallet #</span>
                    <input
                      type="number"
                      min="1"
                      value={shippingPalletStartValue}
                      onChange={(e) => setShippingPalletStartValue(e.target.value)}
                      placeholder="e.g. 10"
                      autoFocus
                    />
                  </label>
                  <label style={{ flex:'0 0 140px' }}>
                    <span style={{ display: 'block', fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Increment</span>
                    <input
                      type="number"
                      min="1"
                      value={shippingPalletStepValue}
                      onChange={(e) => setShippingPalletStepValue(e.target.value)}
                      placeholder="1"
                    />
                  </label>
                </div>
              )}
              {shippingPalletSequential && (
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
                  Pallet numbers will increment per selected line item using the order they appear in the list.
                </div>
              )}
              <label style={{ display: 'block', marginTop: 12 }}>
                <span style={{ display: 'block', fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Carrier</span>
                <select
                  value={shippingPalletCarrierId}
                  onChange={(e) => setShippingPalletCarrierId(e.target.value)}
                >
                  <option value="">-- None --</option>
                  {carrierOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </label>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
                Applying to {selectedItemIds.size} selected line item{selectedItemIds.size === 1 ? '' : 's'}.
              </div>
            </div>
            <div className="actions">
              <button className="btn" onClick={closeShippingPalletModal} disabled={shippingPalletSaving}>Cancel</button>
              <button
                className="btn primary"
                onClick={confirmApplyShippingPallet}
                disabled={shippingPalletSaving || selectedItemIds.size === 0}
              >
                {shippingPalletSaving ? 'Saving…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
      {calcOpen && <ShippingCalculator onClose={() => setCalcOpen(false)} />}
      {pickerOpen && selected?.id && (
        <OrderItemsPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onConfirm={async (_rows, result) => {
            try {
              if (!selected?.id) return setPickerOpen(false);
              if (result?.success) {
                await fetchOrderItems(selected.id);
                await fetchOrders(meta.offset, limit);
                openNotify('success', 'Add Success', `Added ${result.added} line(s) to Order #${selected.id}.` );
              } else {
                if (result?.error) openNotify('error', 'Error', result.error);
                else openNotify('success', 'Not added', `Nothing to add.` );
              }
            } catch (err) {
              openNotify('error', 'Error', `Failed to add items: ${err.message}` );
            } finally {
              setPickerOpen(false);
            }
          }}
          order={{
            orderid: selected?.id ?? null,
            ordernum: selected?.id ?? null,
            clientname: selected?.clientname ?? '',
            created: selected?.datecreated ?? '',
            carrier: selected?.carrier ?? '',
          }}
          clientid={selected?.clientid ?? ''}
          warehouseid={selected?.warehouseid ?? ''}
          fetchAvailable={async (filters) => {
            if (!selected?.id) return { data: [], meta: { limit: 0, offset: 0 } };
            const params = new URLSearchParams();
            ['q', 'receiptid', 'sku', 'lot', 'pallet', 'area', 'location', 'limit', 'offset'].forEach(k => {
              const v = filters?.[k];
              if (v !== undefined && v !== null && String(v) !== '') params.set(k, v);
            });
            const url = `${API}/api/orders/${selected.id}/available${params.toString() ? `?${params.toString()}` : ''}`;
            return fetchJSON(url);
          }}
          persistPicks={async (rows2) => {
            if (!selected?.id || !Array.isArray(rows2) || !rows2.length) return { success: false, added: 0 };
            const items = rows2.map(r => ({
              inventoryid: Number(r.inventoryid),
              boxes: Math.max(0, Number(r.boxes || 0)),
              pieces: Math.max(0, Number(r.pieces || 0)),
            })).filter(x => x.inventoryid && (x.boxes > 0 || x.pieces > 0));
            if (!items.length) return { success: false, added: 0 };
            return fetchJSON(`${API}/api/orders/${selected.id}/items/bulk`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: getUserFullName(), items }),
            });
          }}
        />
      )}
      {ordPreviewOpen && (
        <div className="modal-backdrop" onClick={() => setOrdPreviewOpen(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ordersImportPreviewTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="ordersImportPreviewTitle">Orders Import Preview</h3>
              <button
                className="icon-btn"
                onClick={() => {
                  cancelOrdersImport();
                  setOrdPreviewOpen(false);
                }}
                aria-label="Close preview"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Required columns: <b>Client ID</b>, <b>PO Number</b>, <b>SKU</b>, <b>Qty</b>. Orders are keyed by
                PO Number; each row adds that SKU/Qty to its PO for the specified client. <b>Any missing SKU/client or insufficient inventory will block import.</b>
              </p>
              {/* Counts */}
              {ordPreviewData.length > 0 && (
                <div className="preview-summary">
                  <strong>{ordValidCount}</strong> valid, <strong>{ordInvalidCount}</strong> invalid
                  {ordInvalidCount > 0 && (
                    <div className="preview-warning">
                      Fix all errors in your CSV and re-upload before importing.
                    </div>
                  )}
                </div>
              )}
              {/* Global errors */}
              {ordPreviewErrors.length > 0 && (
                <ul style={{ marginTop: 6 }}>
                  {ordPreviewErrors.map((e, i) => (
                    <li key={i} style={{ color: '#d64545' }}>{e}</li>
                  ))}
                </ul>
              )}
              {/* Table */}
              <div className="preview-table-wrapper" style={{ overflow: 'auto' }}>
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Client ID</th>
                      <th>PO Number</th>
                      <th>SKU</th>
                      <th>Qty</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordPreviewData.map((row, idx) => (
                      <tr key={idx} className={row.valid ? 'valid-row' : 'error-row'}>
                        <td>{row.valid ? <span className="preview-valid">✓</span> : <span className="preview-error">!</span>}</td>
                        <td>{row.clientid ?? ''}</td>
                        <td>{row.po}</td>
                        <td>{row.sku}</td>
                        <td>{row.qty}</td>
                        <td style={{ color: '#d64545' }}>
                          {Array.isArray(row.errors) && row.errors.length ? row.errors.join('; ') : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn secondary"
                onClick={() => {
                  cancelOrdersImport();
                  setOrdPreviewOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                className={`btn primary ${ordValidCount === 0 || ordInvalidCount > 0 || ordImportLoading || ordProgressOpen ? 'disabled-grey' : ''}`}
                onClick={handleOrdersConfirmImport}
                disabled={ordValidCount === 0 || ordInvalidCount > 0 || ordImportLoading || ordProgressOpen}
                title={ordInvalidCount > 0 ? 'Fix all errors first' : ''}
              >
                {(ordImportLoading || ordProgressOpen) && <span className="spinner" />}
                {ordImportLoading || ordProgressOpen
                  ? 'Importing…'
                  : `Import ${ordValidCount} Valid Row${ordValidCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {!IS_CLIENT && palletLabelsOpen && (
        <div className="modal-overlay" onClick={() => setPalletLabelsOpen(false)}>
          <div
            className="modal"
            onClick={e => e.stopPropagation()}
            style={{ width: '50vw', maxWidth: '50vw' }}
          >
            <h3>Print Pallet Labels</h3>
            <div className="payment-form">
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <label>
                  <span>Start Pallet #</span>
                  <input
                    type="number"
                    min={1}
                    value={palletStart}
                    onChange={e => setPalletStart(e.target.value)}
                    autoFocus
                  />
                </label>
                <label>
                  <span>End Pallet #</span>
                  <input
                    type="number"
                    min={1}
                    value={palletEnd}
                    onChange={e => setPalletEnd(e.target.value)}
                  />
                </label>
              </div>
              <div className="modal-actions">
                <button className="btn" onClick={() => setPalletLabelsOpen(false)}>Cancel</button>
                <button className="btn primary" onClick={confirmPalletLabels}>
                  <FaPrint style={{ verticalAlign: '-2px' }} />
                  <span style={{ marginLeft: 6 }}>Print</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Order: choose sort modal */}
      {printSortOpen && (
        <div className="modal-overlay" onClick={() => setPrintSortOpen(false)}>
          <div
            className="modal"
            onClick={e => e.stopPropagation()}
            style={{ width: '32vw', maxWidth: '420px', minWidth: '320px', background: '#fff', color: '#000' }}
          >
            <h3>Print Order</h3>
            <div style={{ marginTop: 6, marginBottom: 10 }}>
              How would you like items sorted?
            </div>
            <div style={{ display:'flex', gap: 8, justifyContent:'flex-end' }}>
              <button className="btn" onClick={() => setPrintSortOpen(false)}>Cancel</button>
              <button className="btn" onClick={() => launchOrderPdf('location')}>By Location</button>
              <button className="btn primary" onClick={() => launchOrderPdf('sku')}>By SKU</button>
            </div>
          </div>
        </div>
      )}

      {confirmState.open && (
        <div className="assign-modal-overlay" onClick={closeConfirm}>
          <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assign-head">
              <h3>Confirm</h3>
              <button className="mini-btn" onClick={closeConfirm} aria-label="Close">×</button>
            </div>
            <div className="assign-search">
              <div style={{ color: 'var(--text-color,#eaeef5)' }}>{confirmState.message}</div>
            </div>
            <div className="assign-actions">
              <button className="btn" onClick={closeConfirm}>Cancel</button>
              <button
                className="btn primary"
                onClick={() => {
                  const fn = confirmState.onConfirm;
                  closeConfirm();
                  if (typeof fn === 'function') fn();
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {ordProgressOpen && (
        <div className="modal-backdrop" onClick={() => { /* block backdrop close while active */ }}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ordersProgressTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="ordersProgressTitle">{ordJobId ? 'Importing Orders' : 'Processing Orders Preview'}</h3>
              <button className="icon-btn" onClick={ordJobId ? cancelOrdersImport : cancelOrdersPreviewUpload} aria-label="Cancel">×</button>
            </div>
            <div className="modal-body">
              <div className="progress-wrap">
                <div className="progress-label">{ordProgressMsg || 'Working…'}</div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.max(1, Math.min(ordProgressPct, 100))}%` }} />
                </div>
                <div className="progress-percent">{Math.max(1, Math.min(ordProgressPct, 100))}%</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn secondary" onClick={ordJobId ? cancelOrdersImport : cancelOrdersPreviewUpload}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <NoticeModal
        open={notify.open}
        type={notify.type}
        title={notify.title}
        message={notify.message}
        onClose={closeNotify}
      />

    </div>
  );
}
